import React from 'react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ReferenceLine, ResponsiveContainer, Tooltip,
  XAxis, YAxis,
} from 'recharts';
import {
  Activity, Apple, Atom, AudioWaveform, Baby, Bone, Brain, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
  ChevronsUpDown, ClipboardList, Dna, Droplet, ExternalLink, Eye, FileText, FlaskConical, Flame, Hand, HeartPulse, Hexagon,
  Microscope, PersonStanding, Ruler, ScanLine, Stethoscope, Waypoints,
} from 'lucide-react';

import { tx } from './i18n';
import {
  SearchField, Segmented, WorkspaceTabs, XomicsSelect,
} from './parts';
import {
  CHART, ChartTip, NUMBER, counted, percent, stackRows,
} from './MobileParts';

// Every result a patient has, drawn with the Mobile app section's parts: flat
// panels with hairlines, navy for data and colour only where it means something.
// The data contracts are strict: a renderer reads exactly the tables its data type
// returns and refuses an ambiguous one rather than guessing, and the boundary at
// the bottom shows that refusal instead of an inferred value.

const SERIES = ['#203a85', '#7185bd', '#0a1e57', '#a9b5d9', '#4a64ad', '#c3cbe6', '#2d4a9a', '#8d9dcb'];
const MAX_TABLE_ROWS = 80;

const DATA_TYPE_CODES = Object.freeze([
  'medical.patient_history',
  'medical.anthropometrics',
  'medical.tonometry',
  'medical.vital_signs',
  'medical.electrocardiogram',
  'medical.ankle_brachial_index',
  'medical.resting_metabolic_rate',
  'medical.bone_health_imaging',
  'medical.diagnostic_ultrasound',
  'medical.gp_examination',
  'medical.cognitive_assessment',
  'medical.total_body_skin_mapping',
  'medical.body_composition_3d',
  'medical.laboratory_panel',
  'medical.nutrition_assessment',
  'omics.genome',
  'omics.carrier_screening',
  'omics.newborn_screening',
  'omics.gpgs',
  'omics.gut_microbiome',
  'omics.oral_microbiome',
  'omics.vaginal_microbiome',
  'omics.transcriptome',
  'omics.plasma_lipidomics',
  'omics.plasma_metabolomics',
  'omics.urine_metabolomics',
  'omics.olink_proteomics',
]);

// These are exact target-table names, including explicit aliases observed in
// successive contracts. Matching is equality-only: a similarly named output
// can never be selected by accident.
const TABLE_ALIASES = Object.freeze({
  genomeInheritance: ['genomics_inheritance'],
  genomePharmaco: ['genomics_pharmaco'],
  genomeTraits: ['genomics_traits'],
  carrierScreening: [
    'familyrisk_carrier_carrier_screening',
    'familyrisk_carrier_screening',
    'carrier_screening',
  ],
  carrierVariants: ['familyrisk_carrier_carrier_variants'],
  newbornScreening: [
    'familyrisk_newborn_newborn_monogenic',
    'familyrisk_newborn_monogenic',
    'newborn_monogenic',
  ],
  newbornCarrier: ['familyrisk_newborn_newborn_carrier_status'],
  newbornSummary: ['familyrisk_newborn_newborn_summary'],
  newbornVariants: ['familyrisk_newborn_newborn_variants'],
  gpgsDomains: ['gpgs_clinical_domains'],
  gpgsDomainPgs: ['gpgs_clinical_domain_pgs'],
  gpgsFindings: ['gpgs_pgs_findings'],
  gpgsOrganSystems: ['gpgs_pgs_organ_systems'],
  gpgsCategories: ['gpgs_pgs_categories'],
  gpgsLandscapes: ['gpgs_phenotype_landscapes'],
  gpgsSubsections: ['gpgs_phenotype_subsections'],
  gpgsSubsectionPgs: ['gpgs_phenotype_subsection_pgs'],
  gpgsDiseaseRankings: ['gpgs_disease_rankings'],
  gpgsDrivingModules: ['gpgs_disease_driving_modules'],
  gpgsDrivingModulePgs: ['gpgs_disease_driving_module_pgs'],
  gpgsModuleProfiles: ['gpgs_module_profiles'],
  gpgsModuleDiseases: ['gpgs_module_diseases'],
  gpgsElevatedDomains: ['gpgs_elevated_domains'],
  gpgsMonitoring: ['gpgs_monitoring_recommendations'],
  gpgsPatient: ['gpgs_patient_summary'],
  gutDiversity: ['metagenomics_gut_diversity'],
  gutProfiled: ['metagenomics_gut_profiled'],
  gutTopPhylum: ['metagenomics_gut_top5phylum'],
  gutDiseaseRows: ['metagenomics_gut_report'],
  gutDiseaseSummary: ['metagenomics_gut_report_summary'],
  gutDetails: ['metagenomics_gut_gm_details'],
  oralDiversity: ['metagenomics_oral_diversity'],
  oralProfiled: ['metagenomics_oral_profiled'],
  oralTopPhylum: ['metagenomics_oral_top5phylum'],
  oralDiseaseRows: ['metagenomics_oral_report'],
  oralDiseaseSummary: ['metagenomics_oral_report_summary'],
  vaginalSummary: ['vaginal_health_summary'],
  vaginalSections: ['vaginal_health_sections'],
  vaginalFlags: ['vaginal_health_flags'],
  vaginalOrganisms: ['vaginal_health_organisms'],
  vaginalSpecies: ['vaginal_health_observed_species'],
  transcriptCells: ['transcriptome_cell_proportions'],
  transcriptDiseases: ['transcriptome_disease_risks'],
  transcriptReport: ['transcriptome_reports'],
  plasmaLipidFeatures: ['plasma_lipidomics_features'],
  plasmaMetaboliteFeatures: ['plasma_metabolomics_features'],
  urineMetaboliteFeatures: ['urine_metabolomics_features'],
  olinkProteinFeatures: ['olink_proteomics_features'],
});

// The diversity indices' reference ranges, order and decimals from the gut
// and oral reports (metagenomics summary.js INDEX_RULES). F/B ratio is stored
// multiplied by 100 and shown divided by it, as there.
const MICROBIOME_RANGES = Object.freeze([
  { field: 'richness', labelKey: 'ui.patient.richness', low: 115, high: 300, divisor: 1, digits: 1 },
  { field: 'shannon_diversity', labelKey: 'ui.patient.shannon_diversity', low: 4.25, high: 7, divisor: 1, digits: 1 },
  { field: 'pielou_evenness', labelKey: 'ui.patient.pielou_evenness', low: 0.65, high: 1, divisor: 1, digits: 1 },
  { field: 'fb_ratio', labelKey: 'ui.patient.fb_ratio', low: 0.2, high: 1.7, divisor: 100, digits: 2 },
]);

// A mark for each kind of data, by the catalogue's category, and for omics by what
// was measured. Only a mark: the catalogue decides what the data types are.
const CATEGORY_ICONS = Object.freeze({
  history: ClipboardList,
  vital_signs: HeartPulse,
  cardiovascular: Activity,
  eye_health: Eye,
  metabolism: Flame,
  laboratory: FlaskConical,
  clinical_examination: Stethoscope,
  cognitive_health: Brain,
  body_measurements: Ruler,
  body_composition: PersonStanding,
  radiology: ScanLine,
  dermatology: Hand,
  nutrition: Apple,
  genomics: Dna,
  microbiome: Microscope,
  transcriptomics: AudioWaveform,
  metabolomics: Atom,
  proteomics: Hexagon,
});
const SUBCATEGORY_ICONS = Object.freeze({
  bone_health: Bone,
  carrier_screening: Baby,
  newborn_screening: Baby,
  polygenic_scores: Waypoints,
  urine_metabolomics: Droplet,
});

export function dataTypeIcon(dataType) {
  return SUBCATEGORY_ICONS[dataType?.subcategory] || CATEGORY_ICONS[dataType?.category] || FileText;
}

// ---------------------------------------------------------------- values

function hasValue(value) {
  return value !== null && value !== undefined && value !== '';
}

// A stored code as words: pipelines and forms write codes, not sentences.
// SINUS_RHYTHM and sinusRhythm both read "Sinus rhythm"; an acronym or a code
// with digits (HbA1c, CST, EUR) keeps its letters.
function human(value) {
  if (!hasValue(value)) return tx('ui.patient.not_recorded');
  const text = String(value).replace(/([a-z])([A-Z][a-z])/g, '$1 $2').replace(/_/g, ' ').trim();
  const shouting = text.length > 3 && /[A-Z]/.test(text) && text === text.toUpperCase() && !/\d/.test(text);
  const words = shouting ? text.toLowerCase() : text;
  return words.replace(/^\p{Ll}/u, (letter) => letter.toUpperCase())
    .replace(/(^|\s)(\p{Lu})(\p{Ll}+)/gu, (match, space, first, rest, offset) => (offset === 0 ? match : `${space}${first.toLowerCase()}${rest}`));
}

export function codeWords(value) {
  return hasValue(value) ? String(value).replace(/_/g, ' ') : '';
}

function numeric(value) {
  if (!hasValue(value) || typeof value === 'boolean') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatNumber(value, digits = 1) {
  const parsed = numeric(value);
  if (parsed === null) return '—';
  return parsed.toFixed(digits).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
}

function signed(value, digits = 2) {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  if (value === 0) return '0';
  return `${value > 0 ? '+' : '−'}${formatNumber(Math.abs(value), digits)}`;
}

// A stored value as text. A list reads as a list and a small object as its
// fields in words ("Protein negative · Glucose negative"), never as JSON.
function formatValue(value) {
  if (!hasValue(value)) return '—';
  if (typeof value === 'boolean') return tx(value ? 'ui.patient.yes' : 'ui.patient.no');
  if (Array.isArray(value)) return value.map(formatValue).join(', ');
  if (typeof value === 'object') {
    return Object.entries(value).filter(([, item]) => hasValue(item)).map(([key, item]) => `${human(key)} ${formatValue(item)}`).join(' · ');
  }
  return String(value);
}

function asList(value) {
  return Array.isArray(value) ? value : [];
}

function normalQuery(query) {
  return String(query || '').trim().toLocaleLowerCase();
}

function searchable(value) {
  if (!hasValue(value)) return '';
  if (typeof value === 'object') return JSON.stringify(value).toLocaleLowerCase();
  return String(value).toLocaleLowerCase();
}

function matchesQuery(value, query) {
  const wanted = normalQuery(query);
  return wanted === '' || searchable(value).includes(wanted);
}

function filterRows(rows, query) {
  return normalQuery(query) === '' ? rows : rows.filter((row) => matchesQuery(row, query));
}

function toneForStatus(value) {
  const code = String(value || '').toLowerCase();
  if (/normal|healthy|favourable|favorable|pass|clear|protective|typical|available|complete|routine/.test(code)) return 'good';
  if (/critical|pathogenic|very_important|elevated|high_risk|diagnostic|action_required/.test(code)) return 'bad';
  if (/important|moderate|borderline|monitor|reduced|abnormal|limited/.test(code)) return 'warn';
  return 'info';
}

function dateText(timestamp) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(timestamp));
}

function entryTimestamp(entry) {
  return Number(entry?.selection?.observedAt || entry?.selection?.recordedAt
    || entry?.selection?.uploadedAt || 0);
}

function entryVisitLabel(entry, index = 0) {
  if (Number.isSafeInteger(Number(entry?.selection?.visitNumber))) {
    return tx('ui.patient.visit_n', { n: Number(entry.selection.visitNumber) });
  }
  const timestamp = entryTimestamp(entry);
  if (timestamp > 0) return dateText(timestamp);
  return tx('ui.patient.record_n', { n: index + 1 });
}

function sortedEntries(entries) {
  return asList(entries).filter(Boolean).sort((left, right) => {
    const leftVisit = numeric(left?.selection?.visitNumber);
    const rightVisit = numeric(right?.selection?.visitNumber);
    if (leftVisit !== null && rightVisit !== null && leftVisit !== rightVisit) return leftVisit - rightVisit;
    return entryTimestamp(left) - entryTimestamp(right);
  });
}

// Hiding one row of a result, such as a variant, from reports, the app and the
// digital twin. The patient record provides it to someone who may hide rows; a
// table offers it once it opts in (OmicsTable's `hideable`).
export const ResultRowHiding = React.createContext(null);
// The output each row was read from, so a row can be hidden by its output's id.
const ROW_OUTPUT = new WeakMap();

// A row hidden from reports, the app and the digital twin is left out of every
// view, as everywhere else; a view that lets someone show it again asks for it.
function readRows(entry, aliasKey, requiredColumns = [], { withHidden = false } = {}) {
  if (!Array.isArray(entry?.data)) {
    throw new Error(`${entry?.dataType?.code || 'Patient result'} does not contain an output list`);
  }
  const aliases = TABLE_ALIASES[aliasKey];
  if (!aliases) throw new Error(`Unknown output contract alias ${aliasKey}`);
  const matches = entry.data.filter((output) => aliases.includes(String(output?.targetTable || '')));
  if (matches.length !== 1) {
    const received = entry.data.map((output) => output?.targetTable).filter(Boolean).join(', ') || 'none';
    throw new Error(`${entry.dataType.code} expected exactly one of [${aliases.join(', ')}]; found ${matches.length}. Returned tables: ${received}`);
  }
  if (!Array.isArray(matches[0].rows)) {
    throw new Error(`${matches[0].targetTable} did not return a rows array`);
  }
  if (matches[0].rows.length > 0) {
    const missing = requiredColumns.filter((column) => !Object.prototype.hasOwnProperty.call(matches[0].rows[0], column));
    if (missing.length > 0) {
      throw new Error(`${matches[0].targetTable} is missing required column(s): ${missing.join(', ')}`);
    }
  }
  matches[0].rows.forEach((row) => ROW_OUTPUT.set(row, matches[0].jobOutputId));
  return withHidden ? matches[0].rows : matches[0].rows.filter((row) => row.hidden !== true);
}

function oneRow(entry, aliasKey, requiredColumns = []) {
  const result = readRows(entry, aliasKey, requiredColumns);
  if (result.length !== 1) {
    throw new Error(`${entry.dataType.code}.${aliasKey} expected exactly one row; found ${result.length}`);
  }
  return result[0];
}

function resolvedTaxon(entry, id) {
  const reference = entry?.references?.bacteria?.[String(id)];
  if (!reference?.taxonName) throw new Error(`Taxon ${id} has no resolved bacteria reference`);
  return String(reference.taxonName).replace(/_/g, ' ').replace(/^[a-z] /, '');
}

function resolvedDisease(entry, id) {
  const reference = entry?.references?.diseases?.[String(id)];
  if (!reference?.name) throw new Error(`Disease ${id} has no resolved disease reference`);
  return reference;
}

// ---------------------------------------------------------------- parts

function Status({ value, tone }) {
  return <span className={`xo-pv-status is-${tone || toneForStatus(value)}`}>{human(value)}</span>;
}

function EmptyState({ children }) {
  return <div className="xo-pv-empty">{children || tx('ui.patient.no_match_in_record')}</div>;
}

function Section({ eyebrow, title, detail, aside = null, children, className = '' }) {
  return (
    <section className={`xo-pv-panel ${className}`.trim()}>
      {(title || eyebrow) && (
        <header className="xo-pv-panel-head">
          <div>{eyebrow && <span>{eyebrow}</span>}{title && <h4>{title}</h4>}{detail && <p>{detail}</p>}</div>
          {aside}
        </header>
      )}
      {children}
    </section>
  );
}

function Prose({ children }) {
  return <p className="xo-pv-prose">{formatValue(children)}</p>;
}

function Metric({ label, value, unit, tone = 'info' }) {
  // Words sit smaller than numbers, so "Non-smoker" does not break like a figure.
  const words = typeof value === 'string' && /[A-Za-z]{3,}/.test(value);
  return (
    <div className={`xo-pv-metric is-${tone}${words ? ' is-text' : ''}`}>
      <span>{label}</span>
      <strong>{formatValue(value)}{unit && hasValue(value) && <small>{unit}</small>}</strong>
    </div>
  );
}

function MetricGrid({ children }) {
  return <div className="xo-pv-metrics">{children}</div>;
}

function FactGrid({ items }) {
  const present = items.filter((item) => hasValue(item.value));
  if (present.length === 0) return <EmptyState />;
  return (
    <dl className="xo-pv-facts">
      {present.map((item) => <div key={item.label}><dt>{item.label}</dt><dd>{formatValue(item.value)}{item.unit || ''}</dd></div>)}
    </dl>
  );
}

function ChipList({ items, empty }) {
  const present = items.filter((item) => hasValue(item));
  if (present.length === 0) return <EmptyState>{empty || tx('ui.patient.none_recorded')}</EmptyState>;
  return <div className="xo-pv-chips">{present.map((item, index) => <span key={`${item}-${index}`}>{formatValue(item)}</span>)}</div>;
}

function RecordList({ rows, query }) {
  const filtered = filterRows(rows, query);
  if (filtered.length === 0) return <EmptyState />;
  return (
    <div className="xo-pv-records">
      {filtered.slice(0, MAX_TABLE_ROWS).map((row, index) => (
        <dl key={index}>
          {Object.entries(row || {}).filter(([, value]) => hasValue(value)).map(([key, value]) => (
            <div key={key}><dt>{human(key)}</dt><dd>{formatValue(value)}</dd></div>
          ))}
        </dl>
      ))}
    </div>
  );
}

function DataTable({ rows, columns, query, empty, limit = MAX_TABLE_ROWS }) {
  const filtered = filterRows(rows, query);
  if (filtered.length === 0) return <EmptyState>{empty || tx('ui.patient.no_rows')}</EmptyState>;
  const shown = filtered.slice(0, limit);
  return (
    <div className="xo-pv-table-wrap">
      <table className="xo-pv-table">
        <thead><tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr></thead>
        <tbody>
          {shown.map((row, index) => (
            // Rows are a fixed read of one record, never reordered in place.
            // eslint-disable-next-line react/no-array-index-key
            <tr key={index}>
              {columns.map((column) => (
                <td key={column.key}>{column.render ? column.render(row[column.key], row) : formatValue(row[column.key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {filtered.length > shown.length && (
        <p className="xo-pv-table-note">{tx('ui.patient.table_limited', { shown: NUMBER.format(shown.length), total: NUMBER.format(filtered.length) })}</p>
      )}
    </div>
  );
}

const statusColumn = (key, label) => ({ key, label, render: (value) => (hasValue(value) ? <Status value={value} /> : '—') });
const wordsColumn = (key, label) => ({ key, label, render: (value) => (hasValue(value) ? human(value) : '—') });

// A value against its reference interval: the interval shaded, the value a dot,
// and the dot coral when it falls outside.
function ReferenceRange({ label, value, low, high, unit = '' }) {
  const actual = numeric(value);
  const minimum = numeric(low);
  const maximum = numeric(high);
  if (minimum === null || maximum === null || maximum <= minimum) {
    throw new Error(`${label} has no valid reference interval`);
  }
  const width = maximum - minimum;
  const axisLow = Math.min(minimum - (width * 0.6), actual === null ? minimum : actual);
  const axisHigh = Math.max(maximum + (width * 0.6), actual === null ? maximum : actual);
  const position = (at) => ((at - axisLow) / (axisHigh - axisLow)) * 100;
  const where = actual === null ? 'none' : actual < minimum ? 'low' : actual > maximum ? 'high' : 'in';
  return (
    <div className={`xo-pv-range is-${where}`}>
      <span className="xo-pv-range-label" title={label}>{label}</span>
      <span className="xo-pv-range-track" aria-hidden="true">
        <i style={{ left: `${position(minimum)}%`, width: `${position(maximum) - position(minimum)}%` }} />
        {actual !== null && <b style={{ left: `${position(actual)}%` }} />}
      </span>
      <span className="xo-pv-range-value">
        <strong>{formatNumber(actual, 2)}{actual !== null ? unit : ''}</strong>
        <small>{tx('ui.patient.range_span', { low: formatNumber(minimum, 2), high: formatNumber(maximum, 2) })}</small>
      </span>
    </div>
  );
}

function configuredRange(entry, field) {
  const range = entry?.dataType?.configuration?.visualization?.referenceRanges?.[field];
  if (!range) throw new Error(`Reference range is not configured for ${entry.dataType.code}.${field}`);
  return { label: range.label || human(field), low: range.low, high: range.high, unit: range.unit ? ` ${range.unit}` : '' };
}

function ConfiguredRange({ entry, field }) {
  return <ReferenceRange {...configuredRange(entry, field)} value={entry.data?.[field]} />;
}

function RangeList({ children }) {
  return <div className="xo-pv-ranges">{children}</div>;
}


// ---------------------------------------------------------------- charts

function pointTitle(_label, point) {
  return { main: point.label, sub: point.at > 0 ? dateText(point.at) : null };
}

// One measurement over visits, small: its latest value and change, and on hover
// each visit with its change since the first and the previous one.
function MiniTrend({ label, points, unit = '' }) {
  const valid = points.filter((item) => numeric(item.value) !== null);
  if (valid.length === 0) return null;
  const data = valid.map((item, index) => ({
    ...item,
    value: Number(item.value),
    first: Number(valid[0].value),
    previous: index > 0 ? Number(valid[index - 1].value) : null,
    index,
  }));
  const last = data[data.length - 1];
  const change = data.length > 1 ? last.value - data[0].value : null;
  return (
    <div className="xo-pv-mini">
      <header>
        <span title={label}>{label}</span>
        <strong>{formatNumber(last.value, 2)}{unit}</strong>
        {change !== null && <small className={change === 0 ? '' : change > 0 ? 'is-up' : 'is-down'}>{signed(change)}</small>}
      </header>
      <div className="xo-pv-mini-chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 6, right: 6, bottom: 2, left: 6 }}>
            <XAxis dataKey="label" hide />
            <YAxis hide domain={['auto', 'auto']} />
            <Tooltip cursor={CHART.lineCursor} animationDuration={120} content={(
              <ChartTip
                title={pointTitle}
                rows={(item) => [{ name: label, color: CHART.ink, mark: 'line', text: `${formatNumber(item.value, 2)}${unit}` }]}
                facts={(item) => [
                  item.index > 0 ? { label: tx('ui.patient.since_first'), value: signed(item.value - item.first) } : null,
                  item.previous !== null && item.index > 1 ? { label: tx('ui.patient.since_previous'), value: signed(item.value - item.previous) } : null,
                  item.version ? { label: tx('ui.patient.analysis_version'), value: item.version } : null,
                ]}
              />
            )} />
            <Area type="monotone" dataKey="value" stroke={CHART.ink} strokeWidth={1.8} fill="rgba(32, 58, 133, .07)"
              dot={{ r: 2.5, fill: CHART.ink, strokeWidth: 0 }} activeDot={{ r: 4 }} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <small className="xo-pv-mini-span">{tx('ui.patient.span_from_to', { from: data[0].label, to: last.label })}</small>
    </div>
  );
}

function SmallMultiples({ title, detail, series }) {
  const drawn = series.filter((item) => item.points.some((point) => numeric(point.value) !== null));
  if (drawn.length === 0) return <Section title={title}><EmptyState /></Section>;
  return (
    <Section title={title} detail={detail}>
      <div className="xo-pv-minis">{drawn.map((item) => <MiniTrend key={item.label} label={item.label} points={item.points} unit={item.unit} />)}</div>
    </Section>
  );
}

// Several measurements over visits on one axis, with a dashed rule where the
// analysis version changed, since values either side of it may not compare.
function TrendLines({ title, series, unit = '' }) {
  const usable = series.map((item) => ({ ...item, points: item.points.filter((point) => numeric(point.value) !== null) }))
    .filter((item) => item.points.length > 0);
  if (usable.length === 0) return <Section title={title}><EmptyState /></Section>;
  const longest = usable.reduce((best, item) => (item.points.length > best.points.length ? item : best));
  const data = longest.points.map((point, index) => {
    const row = { label: point.label, at: point.at, version: point.version };
    usable.forEach((item, seriesIndex) => {
      const match = item.points.find((candidate) => candidate.label === point.label) || item.points[index];
      row[`s${seriesIndex}`] = match ? Number(match.value) : null;
    });
    return row;
  });
  const breaks = data.filter((row, index) => index > 0 && row.version !== data[index - 1].version).map((row) => row.label);
  return (
    <Section title={title}>
      <div className="xo-pv-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={CHART.grid} />
            <XAxis dataKey="label" tick={CHART.tick} tickLine={false} axisLine={false} />
            <YAxis tick={CHART.tick} tickLine={false} axisLine={false} width={44} domain={['auto', 'auto']} />
            {breaks.map((label) => (
              <ReferenceLine key={label} x={label} stroke={CHART.soft} strokeDasharray="3 3"
                label={{ value: tx('ui.patient.new_version'), position: 'insideTopLeft', fill: '#8b93ad', fontSize: 10 }} />
            ))}
            <Tooltip cursor={CHART.lineCursor} animationDuration={120} content={(
              <ChartTip
                title={pointTitle}
                rows={(row) => usable.map((item, seriesIndex) => ({
                  name: item.label,
                  color: SERIES[seriesIndex % SERIES.length],
                  mark: 'line',
                  text: row[`s${seriesIndex}`] === null || row[`s${seriesIndex}`] === undefined ? null : `${formatNumber(row[`s${seriesIndex}`], 2)}${unit}`,
                }))}
                facts={(row) => [row.version ? { label: tx('ui.patient.analysis_version'), value: row.version } : null]}
              />
            )} />
            {usable.map((item, seriesIndex) => (
              <Line key={item.label} type="monotone" dataKey={`s${seriesIndex}`} name={item.label} stroke={SERIES[seriesIndex % SERIES.length]}
                strokeWidth={2} dot={{ r: 3, strokeWidth: 0, fill: SERIES[seriesIndex % SERIES.length] }} activeDot={{ r: 5 }} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="xo-mob-key">
        {usable.map((item, seriesIndex) => <span key={item.label}><i className="is-line" style={{ background: SERIES[seriesIndex % SERIES.length] }} />{item.label}</span>)}
      </div>
    </Section>
  );
}

// Parts of a whole as a ring, with each part's value and share beside it. The
// centre names the largest part, or the total when the parts add up to a
// measured whole (fat and lean mass), since shares of a partial list do not.
function Composition({ title, detail, items, unit = '', showTotal = false }) {
  const usable = items.filter((item) => numeric(item.value) !== null && Number(item.value) >= 0);
  const total = usable.reduce((sum, item) => sum + Number(item.value), 0);
  if (usable.length < 2 || total <= 0) return <Section title={title}><EmptyState>{tx('ui.patient.composition_needs_two')}</EmptyState></Section>;
  const data = usable.map((item, index) => ({ name: item.label, value: Number(item.value), color: SERIES[index % SERIES.length] }));
  const largest = data.reduce((best, part) => (part.value > best.value ? part : best));
  const centre = showTotal
    ? { value: `${formatNumber(total, 1)}${unit}`, label: tx('ui.patient.total_measured') }
    : { value: `${formatNumber(largest.value, 2)}${unit}`, label: largest.name };
  return (
    <Section title={title} detail={detail}>
      <div className="xo-pv-composition">
        <div className="xo-pv-donut">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius="64%" outerRadius="94%" paddingAngle={1.2} stroke="none" isAnimationActive={false}>
                {data.map((part) => <Cell key={part.name} fill={part.color} />)}
              </Pie>
              <Tooltip animationDuration={120} content={(
                <ChartTip
                  title={(_label, part) => ({ main: part.name })}
                  rows={(part) => [{ name: tx('ui.patient.measured'), color: part.color, text: `${formatNumber(part.value, 2)}${unit}`, share: percent(part.value, total) }]}
                />
              )} />
            </PieChart>
          </ResponsiveContainer>
          <div className="xo-pv-donut-centre"><strong>{centre.value}</strong><span title={centre.label}>{centre.label}</span></div>
        </div>
        <ol className="xo-pv-legend">
          {data.map((part) => (
            <li key={part.name}>
              <i style={{ background: part.color }} />
              <span title={part.name}>{part.name}</span>
              <em>{formatNumber(part.value, 2)}{unit}</em>
              <small>{percent(part.value, total)}</small>
            </li>
          ))}
        </ol>
      </div>
    </Section>
  );
}

function StackedBars({ title, groups, segments }) {
  if (groups.length === 0) return <Section title={title}><EmptyState /></Section>;
  const parts = segments.map((segment, index) => ({ key: segment.key, name: segment.label, color: SERIES[index % SERIES.length] }));
  const data = groups.map((group) => ({
    label: group.label,
    ...Object.fromEntries(segments.map((segment) => [segment.key, Math.max(0, numeric(group.values[segment.key]) || 0)])),
  }));
  return (
    <Section title={title}>
      <div className="xo-pv-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={CHART.grid} />
            <XAxis dataKey="label" tick={CHART.tick} tickLine={false} axisLine={false} />
            <YAxis tick={CHART.tick} tickLine={false} axisLine={false} width={44} />
            <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
              <ChartTip title={(_label, row) => ({ main: row.label })}
                rows={(row) => stackRows(row, parts, { sorted: true, format: (value) => formatNumber(value, 3) })} />
            )} />
            {parts.map((part) => <Bar key={part.key} dataKey={part.key} name={part.name} stackId="parts" fill={part.color} maxBarSize={56} isAnimationActive={false} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="xo-mob-key">{parts.map((part) => <span key={part.key}><i style={{ background: part.color }} />{part.name}</span>)}</div>
    </Section>
  );
}


function Waveform({ samples, label }) {
  const values = asList(samples).map(numeric).filter((value) => value !== null);
  if (values.length < 2) return <EmptyState>{tx('ui.patient.no_waveform')}</EmptyState>;
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const range = Math.max(0.000001, maximum - minimum);
  const points = values.map((value, index) => `${(index / (values.length - 1)) * 100},${42 - (((value - minimum) / range) * 34)}`);
  return (
    <Section title={label} className="xo-pv-wave">
      <svg viewBox="0 0 100 48" preserveAspectRatio="none" role="img" aria-label={label}><polyline points={points.join(' ')} /></svg>
    </Section>
  );
}

function waveformSamples(data) {
  for (const artifact of asList(data?.artifacts)) {
    if (Array.isArray(artifact?.samples)) return artifact.samples;
    if (Array.isArray(artifact?.waveformSamples)) return artifact.waveformSamples;
  }
  return [];
}

// ---------------------------------------------------------------- medical records

function HistoryLedger({ items, query, empty }) {
  const filtered = filterRows(items, query);
  if (filtered.length === 0) return <EmptyState>{normalQuery(query) ? tx('ui.patient.no_history_match') : empty}</EmptyState>;
  return (
    <ol className="xo-pv-ledger">
      {filtered.map((item, index) => (
        <li key={`${item.title}-${item.meta || ''}-${index}`}>
          <i className={`is-${item.tone || 'info'}`} aria-hidden="true" />
          <div><strong>{item.title}</strong>{item.detail && <p>{item.detail}</p>}</div>
          {item.meta && <span>{item.meta}</span>}
        </li>
      ))}
    </ol>
  );
}

function LifestyleCard({ title, status, facts, note }) {
  const present = facts.filter((item) => hasValue(item.value));
  return (
    <article className="xo-pv-lifestyle">
      <header><strong>{title}</strong>{hasValue(status) && <Status value={status} />}</header>
      {present.length > 0 && <dl>{present.map((item) => <div key={item.label}><dt>{item.label}</dt><dd>{item.format ? item.format(item.value) : formatValue(item.value)}</dd></div>)}</dl>}
      {hasValue(note) && <p>{note}</p>}
      {present.length === 0 && !hasValue(note) && !hasValue(status) && <small>{tx('ui.patient.not_recorded')}</small>}
    </article>
  );
}

const per = (key) => (value) => tx(key, { value: formatValue(value) });

function renderPatientHistory(entry, query) {
  const data = entry.data || {};
  const conditions = asList(data.chronicConditions);
  const medications = asList(data.medications);
  const supplements = asList(data.supplements);
  const allergies = asList(data.allergies);
  const surgeries = asList(data.surgeries);
  const familyHistory = asList(data.familyHistory);
  const concerns = asList(data.chiefConcerns).filter((item) => matchesQuery(item, query));
  const treatmentRows = medications.map((item) => ({
    title: item.name,
    detail: [item.dose, item.frequency, item.reason].filter(hasValue).join(' · '),
    meta: tx('ui.patient.medication'),
  })).concat(supplements.map((item) => ({
    title: item.name,
    detail: [item.dose, item.frequency].filter(hasValue).join(' · '),
    meta: tx('ui.patient.supplement'),
  })));
  const historyRows = allergies.map((item) => ({
    title: item.substance,
    detail: item.reaction,
    meta: item.severity ? tx('ui.patient.allergy_severity', { severity: codeWords(item.severity).toLowerCase() }) : tx('ui.patient.allergy'),
    tone: toneForStatus(item.severity),
  })).concat(surgeries.map((item) => ({
    title: item.procedure,
    detail: item.notes,
    meta: item.year ? tx('ui.patient.procedure_year', { year: item.year }) : tx('ui.patient.procedure'),
  }))).concat(familyHistory.map((item) => ({
    title: item.condition,
    detail: item.relation,
    meta: item.ageAtDiagnosis ? tx('ui.patient.family_age', { age: item.ageAtDiagnosis }) : tx('ui.patient.family_history'),
  })));
  const lifestyle = [
    {
      title: tx('ui.patient.tobacco'), status: data.tobaccoUse?.status,
      facts: [
        { label: tx('ui.patient.products'), value: data.tobaccoUse?.products },
        { label: tx('ui.patient.use'), value: data.tobaccoUse?.daysPerWeek, format: per('ui.patient.days_a_week') },
        { label: tx('ui.patient.cigarettes'), value: data.tobaccoUse?.cigarettesPerDay, format: per('ui.patient.a_day') },
        { label: tx('ui.patient.regular_use'), value: data.tobaccoUse?.yearsOfRegularUse, format: per('ui.patient.years_value') },
        { label: tx('ui.patient.stopped'), value: data.tobaccoUse?.quitYear },
      ],
      note: data.tobaccoUse?.notes,
    },
    {
      title: tx('ui.patient.alcohol'), status: data.alcoholUse?.status,
      facts: [
        { label: tx('ui.patient.drinking_days'), value: data.alcoholUse?.drinkingDaysPerWeek, format: per('ui.patient.a_week') },
        { label: tx('ui.patient.standard_drinks'), value: data.alcoholUse?.standardDrinksPerWeek, format: per('ui.patient.a_week') },
        { label: tx('ui.patient.typical_occasion'), value: data.alcoholUse?.typicalDrinksPerOccasion },
        { label: tx('ui.patient.largest_occasion'), value: data.alcoholUse?.largestDrinksOnOneOccasion },
        { label: tx('ui.patient.stopped'), value: data.alcoholUse?.quitYear },
      ],
      note: data.alcoholUse?.notes,
    },
    {
      title: tx('ui.patient.physical_activity'), status: null,
      facts: [
        { label: tx('ui.patient.active_days'), value: data.physicalActivity?.activityDaysPerWeek, format: per('ui.patient.a_week') },
        { label: tx('ui.patient.aerobic_activity'), value: data.physicalActivity?.aerobicMinutesPerWeek, format: per('ui.patient.minutes_a_week') },
        { label: tx('ui.patient.strength'), value: data.physicalActivity?.strengthSessionsPerWeek, format: per('ui.patient.a_week') },
        { label: tx('ui.patient.mobility'), value: data.physicalActivity?.mobilitySessionsPerWeek, format: per('ui.patient.a_week') },
        { label: tx('ui.patient.sedentary_time'), value: data.physicalActivity?.sedentaryHoursPerDay, format: per('ui.patient.hours_a_day') },
      ],
      note: [data.physicalActivity?.usualActivities, data.physicalActivity?.limitations].filter(hasValue).join(' · '),
    },
    {
      title: tx('ui.patient.sleep'), status: data.sleep?.quality,
      facts: [{ label: tx('ui.patient.average_sleep'), value: data.sleep?.hoursPerNight, format: per('ui.patient.hours_a_night') }],
      note: data.sleep?.concerns,
    },
  ].filter((item) => matchesQuery(item, query));

  return (
    <div className="xo-pv-history">
      <div className="xo-pv-history-top">
        <Section eyebrow={tx('ui.patient.recorded_baseline')} title={tx('ui.patient.person')}>
          <dl className="xo-pv-facts is-three">
            <div><dt>{tx('ui.patient.sex')}</dt><dd>{human(data.sex)}</dd></div>
            <div><dt>{tx('ui.patient.date_of_birth')}</dt><dd>{formatValue(data.dateOfBirth)}</dd></div>
            <div><dt>{tx('ui.patient.place_of_birth')}</dt><dd>{formatValue(data.placeOfBirth)}</dd></div>
          </dl>
        </Section>
        <Section eyebrow={tx('ui.patient.why_they_came')} title={tx('ui.patient.chief_concerns')}>
          <ChipList items={concerns} empty={normalQuery(query) ? tx('ui.patient.no_concern_match') : tx('ui.patient.no_chief_concerns')} />
        </Section>
      </div>

      <MetricGrid>
        <Metric label={tx('ui.patient.conditions')} value={conditions.length} tone={conditions.length ? 'warn' : 'good'} />
        <Metric label={tx('ui.patient.medications_supplements')} value={medications.length + supplements.length} />
        <Metric label={tx('ui.patient.allergies')} value={allergies.length} />
        <Metric label={tx('ui.patient.family_history_entries')} value={familyHistory.length} />
      </MetricGrid>

      <div className="xo-pv-split">
        <div className="xo-pv-column">
          <Section eyebrow={tx('ui.patient.clinical')} title={tx('ui.patient.conditions')}>
            <HistoryLedger query={query} empty={tx('ui.patient.no_conditions')} items={conditions.map((item) => ({
              title: item.name,
              detail: item.notes,
              meta: [item.status && human(item.status), item.diagnosedYear && tx('ui.patient.since_year', { year: item.diagnosedYear })].filter(hasValue).join(' · '),
              tone: toneForStatus(item.status),
            }))} />
          </Section>
          <Section title={tx('ui.patient.medications_supplements')}>
            <HistoryLedger items={treatmentRows} query={query} empty={tx('ui.patient.no_treatment')} />
          </Section>
          <Section title={tx('ui.patient.allergies_procedures_family')}>
            <HistoryLedger items={historyRows} query={query} empty={tx('ui.patient.no_allergies_procedures_family')} />
          </Section>
        </div>
        <div className="xo-pv-column">
          <Section eyebrow={tx('ui.patient.lifestyle')} title={tx('ui.patient.daily_life')}>
            <div className="xo-pv-lifestyles">
              {lifestyle.map((item) => <LifestyleCard key={item.title} {...item} />)}
              {lifestyle.length === 0 && <EmptyState>{tx('ui.patient.no_lifestyle_match')}</EmptyState>}
            </div>
          </Section>
          <Section title={tx('ui.patient.nutrition_notes')}>
            <dl className="xo-pv-notes">
              <div><dt>{tx('ui.patient.dietary_pattern')}</dt><dd>{formatValue(data.diet?.pattern)}</dd></div>
              <div><dt>{tx('ui.patient.restrictions')}</dt><dd>{formatValue(data.diet?.restrictions)}</dd></div>
              <div><dt>{tx('ui.patient.nutrition_concerns')}</dt><dd>{formatValue(data.diet?.concerns)}</dd></div>
              <div><dt>{tx('ui.patient.additional_notes')}</dt><dd>{formatValue(data.notes)}</dd></div>
            </dl>
          </Section>
        </div>
      </div>
    </div>
  );
}

function renderAnthropometrics(entry) {
  const data = entry.data || {};
  return (
    <>
      <MetricGrid>
        <Metric label={tx('ui.patient.height')} value={data.heightCm} unit="cm" />
        <Metric label={tx('ui.patient.weight')} value={data.weightKg} unit="kg" />
        <Metric label={tx('ui.patient.bmi')} value={data.bmiKgM2} unit="kg/m²" />
        <Metric label={tx('ui.patient.waist')} value={data.waistCircumferenceCm} unit="cm" />
        <Metric label={tx('ui.patient.hip')} value={data.hipCircumferenceCm} unit="cm" />
      </MetricGrid>
      <Section title={tx('ui.patient.against_reference')}><RangeList><ConfiguredRange entry={entry} field="bmiKgM2" /></RangeList></Section>
    </>
  );
}

function renderTonometry(entry) {
  const data = entry.data || {};
  return (
    <>
      <MetricGrid>
        <Metric label={tx('ui.patient.left_eye')} value={data.leftEyePressureMmhg} unit="mmHg" />
        <Metric label={tx('ui.patient.right_eye')} value={data.rightEyePressureMmhg} unit="mmHg" />
        <Metric label={tx('ui.patient.method')} value={data.method} />
      </MetricGrid>
      <Section title={tx('ui.patient.intraocular_pressure')}>
        <RangeList>
          <ConfiguredRange entry={entry} field="leftEyePressureMmhg" />
          <ConfiguredRange entry={entry} field="rightEyePressureMmhg" />
        </RangeList>
      </Section>
    </>
  );
}

function renderVitalSigns(entry) {
  const data = entry.data || {};
  return (
    <>
      <MetricGrid>
        <Metric label={tx('ui.patient.blood_pressure')} value={`${formatNumber(data.systolicBloodPressureMmhg, 0)}/${formatNumber(data.diastolicBloodPressureMmhg, 0)}`} unit="mmHg" />
        <Metric label={tx('ui.patient.oxygen_saturation')} value={data.spo2Percent} unit="%" />
        <Metric label={tx('ui.patient.pulse')} value={data.pulseBpm} unit="bpm" />
        <Metric label={tx('ui.patient.respiration')} value={data.respiratoryRatePerMin} unit="/min" />
        <Metric label={tx('ui.patient.temperature')} value={data.temperatureC} unit="°C" />
        <Metric label={tx('ui.patient.position')} value={hasValue(data.position) ? human(data.position) : null} />
      </MetricGrid>
      <Section title={tx('ui.patient.against_reference')}>
        <RangeList>
          <ConfiguredRange entry={entry} field="systolicBloodPressureMmhg" />
          <ConfiguredRange entry={entry} field="diastolicBloodPressureMmhg" />
          <ConfiguredRange entry={entry} field="spo2Percent" />
        </RangeList>
      </Section>
    </>
  );
}

function renderElectrocardiogram(entry) {
  const data = entry.data || {};
  return (
    <>
      <MetricGrid>
        <Metric label={tx('ui.patient.rhythm')} value={hasValue(data.rhythm) ? human(data.rhythm) : null} tone={toneForStatus(data.interpretation)} />
        <Metric label={tx('ui.patient.heart_rate')} value={data.heartRateBpm} unit="bpm" />
        <Metric label="PR" value={data.prIntervalMs} unit="ms" />
        <Metric label="QRS" value={data.qrsDurationMs} unit="ms" />
        <Metric label="QT" value={data.qtIntervalMs} unit="ms" />
        <Metric label="QTc" value={data.qtcIntervalMs} unit="ms" />
      </MetricGrid>
      <Waveform samples={waveformSamples(data)} label={hasValue(data.rhythm) ? human(data.rhythm) : tx('ui.patient.recorded_rhythm')} />
      <Section title={tx('ui.patient.interpretation')}><Prose>{data.interpretation}</Prose></Section>
    </>
  );
}

function renderAbi(entry) {
  const data = entry.data || {};
  return (
    <>
      <MetricGrid>
        <Metric label={tx('ui.patient.left_abi')} value={data.leftAbi} />
        <Metric label={tx('ui.patient.right_abi')} value={data.rightAbi} />
        <Metric label={tx('ui.patient.left_ankle')} value={data.leftAnklePressureMmhg} unit="mmHg" />
        <Metric label={tx('ui.patient.right_ankle')} value={data.rightAnklePressureMmhg} unit="mmHg" />
        <Metric label={tx('ui.patient.brachial')} value={data.brachialPressureMmhg} unit="mmHg" />
      </MetricGrid>
      <Section title={tx('ui.patient.against_reference')}>
        <RangeList><ConfiguredRange entry={entry} field="leftAbi" /><ConfiguredRange entry={entry} field="rightAbi" /></RangeList>
      </Section>
      <Section title={tx('ui.patient.interpretation')}><Prose>{data.interpretation}</Prose></Section>
    </>
  );
}

function renderMetabolicRate(entry) {
  const data = entry.data || {};
  return (
    <>
      <MetricGrid>
        <Metric label={tx('ui.patient.resting_energy')} value={data.restingEnergyExpenditureKcalDay} unit="kcal" />
        <Metric label="VO₂" value={data.vo2MlMin} unit="mL/min" />
        <Metric label="VCO₂" value={data.vco2MlMin} unit="mL/min" />
        <Metric label={tx('ui.patient.respiratory_quotient')} value={data.respiratoryQuotient} />
      </MetricGrid>
      <Section title={tx('ui.patient.interpretation')}><Prose>{data.interpretation}</Prose></Section>
    </>
  );
}

function renderBone(entry, query) {
  const data = entry.data || {};
  return (
    <>
      <MetricGrid>
        <Metric label={tx('ui.patient.modality')} value={data.modality} />
        <Metric label={tx('ui.patient.device')} value={data.deviceModel} />
        <Metric label={tx('ui.patient.fragility_score')} value={data.fragilityScore} />
        <Metric label={tx('ui.patient.fracture_risk_5y')} value={data.fractureRisk5yPercent} unit="%" tone={numeric(data.fractureRisk5yPercent) > 0 ? 'warn' : 'info'} />
      </MetricGrid>
      <Section title={tx('ui.patient.bone_density_by_site')}>
        <DataTable rows={asList(data.sites)} query={query} columns={[
          wordsColumn('anatomicalSite', tx('ui.patient.site')),
          { key: 'hipSide', label: tx('ui.patient.side') },
          { key: 'boneMineralDensityGcm2', label: tx('ui.patient.bmd') },
          { key: 'tScore', label: tx('ui.patient.t_score') },
          { key: 'zScore', label: tx('ui.patient.z_score') },
        ]} />
      </Section>
      <Section title={tx('ui.patient.findings')}><Prose>{data.findings}</Prose></Section>
    </>
  );
}

function renderUltrasound(entry, query) {
  const data = entry.data || {};
  return (
    <>
      <Section title={tx('ui.patient.studies')}><RecordList rows={asList(data.studies)} query={query} /></Section>
      <div className="xo-pv-split">
        <Section title={tx('ui.patient.findings')}><Prose>{data.findings}</Prose></Section>
        <Section title={tx('ui.patient.impression')}><Prose>{data.impression}</Prose></Section>
      </div>
    </>
  );
}

// The GP's written report in its sections: a title line ("GENERAL
// APPEARANCE AND SKIN:") with its sentences or bullets under it, sections apart
// by a blank line. A report written another way is one section without a title.
function gpReportSections(report) {
  if (typeof report !== 'string') return [];
  return report.split(/\n\s*\n/).map((block) => block.trim()).filter(Boolean).map((block) => {
    const [first, ...lines] = block.split('\n');
    if (!lines.length || !/:\s*$/.test(first)) return { area: '', finding: block.replace(/\s*\n\s*/g, ' ') };
    return { area: first.replace(/:\s*$/, '').trim(), finding: lines.map((line) => line.trim().replace(/^•\s*/, '')).join(' ') };
  });
}

function renderGp(entry, query) {
  const data = entry.data || {};
  // A record with the written report shows it, a row for each section.
  const report = gpReportSections(data.report);
  const findings = Object.entries(data.generalPhysicalFindings || {})
    .map(([area, finding]) => ({ area, finding, examination: tx('ui.patient.general_physical') }))
    .concat(Object.entries(data.neurologicalFindings || {})
      .map(([area, finding]) => ({ area, finding, examination: tx('ui.patient.neurological') })));
  return (
    <>
      {(hasValue(data.clinicalImpression) || !report.length) && (
        <Section title={tx('ui.patient.clinical_impression')}><Prose>{data.clinicalImpression}</Prose></Section>
      )}
      <Section title={tx('ui.patient.examination_findings')}>
        {report.length ? (
          <DataTable rows={report} query={query} columns={[
            { key: 'area', label: tx('ui.patient.area') },
            { key: 'finding', label: tx('ui.patient.finding') },
          ]} />
        ) : (
          <DataTable rows={findings} query={query} columns={[
            { key: 'examination', label: tx('ui.patient.examination') },
            wordsColumn('area', tx('ui.patient.area')),
            { key: 'finding', label: tx('ui.patient.finding') },
          ]} />
        )}
      </Section>
      {(asList(data.recommendations).length > 0 || !report.length) && (
        <Section title={tx('ui.patient.recommendations')}><ChipList items={asList(data.recommendations).filter((item) => matchesQuery(item, query))} /></Section>
      )}
    </>
  );
}

function renderCognition(entry, query) {
  const data = entry.data || {};
  const domains = Object.entries(data.domains || {}).map(([domain, score]) => ({ domain, score }));
  return (
    <>
      <MetricGrid>
        <Metric label={tx('ui.patient.instrument')} value={data.instrument} />
        <Metric label={tx('ui.patient.score')} value={hasValue(data.score) ? `${formatValue(data.score)} / ${formatValue(data.maximumScore)}` : null} />
        <Metric label={tx('ui.patient.interpretation')} value={data.interpretation} tone={toneForStatus(data.interpretation)} />
      </MetricGrid>
      <Section title={tx('ui.patient.cognitive_domains')}>
        <DataTable rows={domains} query={query} columns={[wordsColumn('domain', tx('ui.patient.domain')), { key: 'score', label: tx('ui.patient.recorded_score') }]} />
      </Section>
    </>
  );
}

function renderSkin(entry, query) {
  const data = entry.data || {};
  return (
    <>
      <MetricGrid>
        <Metric label={tx('ui.patient.mapped_lesions')} value={asList(data.lesions).length} />
        <Metric label={tx('ui.patient.retained_images')} value={asList(data.artifacts).length} />
      </MetricGrid>
      <Section title={tx('ui.patient.summary')}><Prose>{data.summary}</Prose></Section>
      <Section title={tx('ui.patient.lesion_map')}><RecordList rows={asList(data.lesions)} query={query} /></Section>
      <Section title={tx('ui.patient.recommendations')}><ChipList items={asList(data.recommendations).filter((item) => matchesQuery(item, query))} /></Section>
    </>
  );
}

function renderBodyComposition(entry, query) {
  const data = entry.data || {};
  const mass = [{ label: tx('ui.patient.fat_mass'), value: data.fatMassKg }, { label: tx('ui.patient.lean_mass'), value: data.leanMassKg }];
  return (
    <>
      <MetricGrid>
        <Metric label={tx('ui.patient.body_fat')} value={data.bodyFatPercent} unit="%" />
        <Metric label={tx('ui.patient.fat_mass')} value={data.fatMassKg} unit="kg" />
        <Metric label={tx('ui.patient.lean_mass')} value={data.leanMassKg} unit="kg" />
        <Metric label={tx('ui.patient.skeletal_muscle')} value={data.skeletalMuscleMassKg} unit="kg" />
        <Metric label={tx('ui.patient.visceral_fat')} value={data.visceralFatRating} />
      </MetricGrid>
      {mass.every((item) => numeric(item.value) !== null) && (
        <Composition title={tx('ui.patient.mass_composition')} detail={tx('ui.patient.mass_composition_detail')} items={mass} unit=" kg" showTotal />
      )}
      <div className="xo-pv-split">
        <Section title={tx('ui.patient.circumferences')}><RecordList rows={Object.entries(data.circumferences || {}).map(([measurement, value]) => ({ measurement: human(measurement), value }))} query={query} /></Section>
        <Section title={tx('ui.patient.posture')}><RecordList rows={Object.entries(data.posture || {}).map(([measurement, value]) => ({ measurement: human(measurement), value }))} query={query} /></Section>
      </div>
    </>
  );
}

// An analyte's name as the catalogue configures it for its panel ("MCV",
// "White blood cells"), or its code in words when the catalogue has none.
function analyteName(entry, code) {
  for (const panel of asList(entry?.dataType?.configuration?.panels)) {
    const match = asList(panel?.analytes).find((analyte) => analyte?.analyteCode === code);
    if (match?.displayName) return match.displayName;
  }
  return human(code);
}

function renderLaboratory(entry, query) {
  const data = entry.data || {};
  const analytes = filterRows(asList(data.analytes), query);
  const flagged = analytes.filter((row) => hasValue(row.flag) && String(row.flag).toLowerCase() !== 'normal');
  return (
    <>
      <MetricGrid>
        <Metric label={tx('ui.patient.panel')} value={data.panelName || data.panelCode} />
        <Metric label={tx('ui.patient.scope')} value={hasValue(data.coreVsExtended) ? human(data.coreVsExtended) : null} />
        <Metric label={tx('ui.patient.specimen')} value={hasValue(data.specimenType) ? human(data.specimenType) : null} />
        <Metric label={tx('ui.patient.analytes')} value={analytes.length} />
        <Metric label={tx('ui.patient.flagged')} value={flagged.length} tone={flagged.length ? 'warn' : 'good'} />
      </MetricGrid>
      <Section title={tx('ui.patient.analytes')} detail={tx('ui.patient.analytes_detail')}>
        {analytes.length === 0 ? <EmptyState /> : (
          <RangeList>
            {analytes.map((analyte) => {
              const low = numeric(analyte.refLow);
              const high = numeric(analyte.refHigh);
              if (numeric(analyte.value) !== null && low !== null && high !== null && high > low) {
                return <ReferenceRange key={analyte.analyteCode} label={analyteName(entry, analyte.analyteCode)} value={analyte.value} low={low} high={high} unit={analyte.unit ? ` ${analyte.unit}` : ''} />;
              }
              return (
                <div className="xo-pv-range is-one-sided" key={analyte.analyteCode}>
                  <span className="xo-pv-range-label">{analyteName(entry, analyte.analyteCode)}</span>
                  <span className="xo-pv-range-flag">{hasValue(analyte.flag) && <Status value={analyte.flag} />}</span>
                  <span className="xo-pv-range-value"><strong>{formatValue(analyte.value)} {analyte.unit || ''}</strong></span>
                </div>
              );
            })}
          </RangeList>
        )}
      </Section>
    </>
  );
}

function renderNutrition(entry, query) {
  const data = entry.data || {};
  return (
    <>
      <MetricGrid>
        <Metric label={tx('ui.patient.dietary_pattern')} value={data.dietaryPattern} />
        <Metric label={tx('ui.patient.restrictions')} value={asList(data.restrictions).length} />
        <Metric label={tx('ui.patient.goals')} value={asList(data.goals).length} />
        <Metric label={tx('ui.patient.recommendations')} value={asList(data.recommendations).length} />
      </MetricGrid>
      <div className="xo-pv-split">
        <Section title={tx('ui.patient.restrictions')}><ChipList items={asList(data.restrictions).filter((item) => matchesQuery(item, query))} /></Section>
        <Section title={tx('ui.patient.goals')}><ChipList items={asList(data.goals).filter((item) => matchesQuery(item, query))} /></Section>
      </div>
      <Section title={tx('ui.patient.recommendations')}><ChipList items={asList(data.recommendations).filter((item) => matchesQuery(item, query))} /></Section>
      <Section title={tx('ui.patient.dietitian_notes')}><Prose>{data.notes}</Prose></Section>
    </>
  );
}

// ---------------------------------------------------------------- omics results

function screeningRows(entry, mode) {
  return readRows(entry, mode === 'carrier' ? 'carrierScreening' : 'newbornScreening', ['disease', 'genes', 'hgvsc']);
}

function transcriptCellItems(cells) {
  return Object.entries(cells).filter(([key, value]) => key !== 'sourceRow' && numeric(value) !== null)
    .map(([key, value]) => ({ label: human(key), value: Number(value) * 100 }));
}

// A microbiome condition score's band in the gut_health and oral_health
// reports' words and colour. A LOW score is the disease-associated end, so
// the low band is the one flagged; high is the reports' "very good state".
const BAND_WORDS = Object.freeze({
  high: ['ui.patient.omx_mb_band_high', 'good'],
  normalhigh: ['ui.patient.omx_mb_band_normalhigh', 'info'],
  normal: ['ui.patient.omx_mb_band_normal', 'info'],
  normallow: ['ui.patient.omx_mb_band_normallow', 'info'],
  low: ['ui.patient.omx_mb_band_low', 'warn'],
});

function BandStatus({ band }) {
  const [labelKey, tone] = BAND_WORDS[band] || [null, 'info'];
  return <span className={`xo-pv-status is-${tone}`}>{labelKey ? tx(labelKey) : human(band)}</span>;
}

function microbiomeBand(mode, score) {
  const value = numeric(score);
  if (value === null || value < 0 || value > 1) throw new Error(`${mode} disease score ${formatValue(score)} is outside the report contract range 0..1`);
  if (mode === 'oral') return value >= 0.8 ? 'high' : value >= 0.4 ? 'normal' : 'low';
  return value > 0.8 ? 'high' : value > 0.55 ? 'normalhigh' : value > 0.3 ? 'normallow' : 'low';
}

// ---------------------------------------------------------------- omics sections

// Every omics section opens with what the test is and the few results that
// matter, and keeps its detail behind tabs, so no section is one long scroll.
// A finding says what it is in words: the gene and variant, the condition, the
// drug, the organism. Big tables search, filter, sort and page, and open on the
// rows that need a look.

const OMICS_PAGE = 25;

// A check for typical, a warning triangle, a danger mark; a plain dot is a
// neutral bullet, not a status.
function OmicsMark({ tone = 'info', title }) {
  const label = title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true };
  if (tone === 'good') {
    return (
      <svg className="xo-omx-mark is-good" viewBox="0 0 20 20" {...label}>
        <circle cx="10" cy="10" r="10" />
        <path d="M5.7 10.3 8.7 13.3 14.4 7" />
      </svg>
    );
  }
  if (tone === 'warn') {
    return (
      <svg className="xo-omx-mark is-warn" viewBox="0 0 20 20" {...label}>
        <path className="is-shape" d="M10 2.2 18.4 16.9H1.6z" />
        <path d="M10 7.4v4.6" />
        <circle cx="10" cy="14.6" r="1.2" />
      </svg>
    );
  }
  if (tone === 'bad') {
    return (
      <svg className="xo-omx-mark is-bad" viewBox="0 0 20 20" {...label}>
        <circle className="is-shape" cx="10" cy="10" r="10" />
        <path d="M10 5.2v6" />
        <circle cx="10" cy="14.6" r="1.3" />
      </svg>
    );
  }
  return <svg className="xo-omx-mark is-dot" viewBox="0 0 20 20" {...label}><circle cx="10" cy="10" r="4" /></svg>;
}

function Chip({ tone = 'muted', children }) {
  return <span className={`xo-omx-chip is-${tone}`}>{children}</span>;
}

// Key facts across the top of a section: a label, a value in words or
// numbers, and colour only when the value needs a look.
function OmicsFacts({ items }) {
  const present = items.filter((item) => hasValue(item.value));
  return (
    <dl className="xo-omx-facts">
      {present.map((item) => (
        <div key={item.label} className={`is-${item.tone || 'info'}`}>
          <dt>{item.label}</dt>
          <dd>{typeof item.value === 'number' ? NUMBER.format(item.value) : item.value}{item.unit && <small>{item.unit}</small>}</dd>
          {item.detail && <p>{item.detail}</p>}
        </div>
      ))}
    </dl>
  );
}

// A run summary's "Sample ID" row is the name the lab wrote inside its file,
// which can name a different sample than the one delivered for this patient.
// The profile never shows it: the record already identifies the patient.
function isSampleIdRow(row) {
  return /^sample[\s_-]*id$/i.test(String(row?.content || '').trim());
}

// What the test is, its key facts, then tabs. A tab's render gets the tab
// setter so a summary can send the reader to the full list.
// Which result a view shows. The chosen tab stays when the clinician moves to
// another result date, but the tab's content (search, filters, sort, opened
// rows) starts fresh for each tab and each result.
function recordIdentity(entry) {
  const selection = entry?.selection || {};
  return [entry?.dataType?.code, selection.jobId ?? '', ...asList(entry?.data).map((output) => output?.jobOutputId ?? '')].join(':');
}

function OmicsView({ record, about, facts, tabs }) {
  const [active, setActive] = React.useState(tabs[0].value);
  const current = tabs.find((tab) => tab.value === active) || tabs[0];
  return (
    <div className="xo-omx">
      <p className="xo-omx-about">{about}</p>
      {facts && <OmicsFacts items={facts} />}
      <WorkspaceTabs className="xo-omx-tabs is-compact" value={current.value} onChange={setActive} scrollable
        label={tx('ui.patient.omx_sections')} tabs={tabs.map(({ value, label, count }) => ({ value, label, count }))} />
      <div className="xo-omx-pane" role="tabpanel" aria-label={current.label} key={`${current.value}:${recordIdentity(record)}`}>
        {current.render(setActive)}
      </div>
    </div>
  );
}

function OmicsBlock({ title, detail, action, children, className = '' }) {
  return (
    <section className={`xo-omx-block ${className}`.trim()}>
      <header>
        <div><h4>{title}</h4>{detail && <p>{detail}</p>}</div>
        {action}
      </header>
      {children}
    </section>
  );
}

function SeeAll({ label, onClick }) {
  return <button type="button" className="xo-omx-link" onClick={onClick}>{label}<ChevronRight aria-hidden="true" /></button>;
}

function compareValues(left, right) {
  const leftEmpty = left === null || left === undefined || left === '';
  const rightEmpty = right === null || right === undefined || right === '';
  if (leftEmpty || rightEmpty) return leftEmpty === rightEmpty ? 0 : leftEmpty ? 1 : -1;
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
}

// Whether one row is shown, as a switch beside each row's chevron, for someone
// the patient record lets hide it: on is shown everywhere, off is hidden.
function RowVisibility({ row }) {
  const hiding = React.useContext(ResultRowHiding);
  const jobOutputId = ROW_OUTPUT.get(row);
  if (!hiding || jobOutputId === undefined || !Number.isSafeInteger(Number(row.sourceRow))) return null;
  const label = tx('ui.patient.result_row_switch');
  return (
    <label className="xo-omx-switch" title={label}>
      <input type="checkbox" role="switch" aria-label={label} checked={row.hidden !== true}
        disabled={hiding.busy.has(`${jobOutputId}:${row.sourceRow}`)} onChange={() => hiding.toggle(jobOutputId, row)} />
      <i aria-hidden="true" />
    </label>
  );
}

// A big table: search, filters, sortable columns and pages of 25. Filters open
// on the flagged rows when the section gives them an initial value; the
// record's own search box narrows every table too. A row can open to show its
// evidence.
function OmicsTable({ rows: givenRows, columns, query, filters = [], initialSort = null, rowKey, detail, empty, label, searchLabel, hideable = false }) {
  const hiding = React.useContext(ResultRowHiding);
  const withVisibility = hideable && hiding !== null;
  // The last cell holds the chevron that opens a row's details and, for someone
  // who may hide rows, the row's switch.
  const expandable = Boolean(detail) || withVisibility;
  // A hidden row is listed only where it can be shown again.
  const rows = React.useMemo(
    () => (withVisibility ? givenRows : givenRows.filter((row) => row.hidden !== true)),
    [givenRows, withVisibility],
  );
  const [search, setSearch] = React.useState('');
  // A filter opens on its flagged rows only when there are some; otherwise on all rows.
  const [choice, setChoice] = React.useState(() => Object.fromEntries(filters.map((filter) => [
    filter.key, filter.initial && filter.initial !== 'all' && rows.some((row) => filter.test(row, filter.initial)) ? filter.initial : 'all',
  ])));
  const [sort, setSort] = React.useState(initialSort);
  const [page, setPage] = React.useState(0);
  const [open, setOpen] = React.useState(() => new Set());
  // Codes are searched as words: "signal present" finds SIGNAL_PRESENT.
  const texts = React.useMemo(() => rows.map((row) => columns.map((column) => {
    const value = column.text ? column.text(row) : row[column.key];
    return hasValue(value) ? String(value).replace(/_/g, ' ') : '';
  }).join(' \u0001 ').toLocaleLowerCase()), [rows, columns]);
  const visible = React.useMemo(() => {
    const wanted = [normalQuery(query), normalQuery(search)].filter(Boolean);
    let list = rows.map((row, index) => ({ row, index }));
    filters.forEach((filter) => {
      const value = choice[filter.key];
      if (value !== 'all') list = list.filter(({ row }) => filter.test(row, value));
    });
    if (wanted.length) list = list.filter(({ index }) => wanted.every((needle) => texts[index].includes(needle)));
    if (sort) {
      const column = columns.find((candidate) => candidate.key === sort.key);
      const valueOf = column?.sort || ((row) => row[sort.key]);
      const empty = (value) => value === null || value === undefined || value === '' || Number.isNaN(value);
      // Rows without a value stay at the end in both directions.
      list = [...list].sort((left, right) => {
        const a = valueOf(left.row);
        const b = valueOf(right.row);
        if (empty(a) || empty(b)) return empty(a) === empty(b) ? left.index - right.index : empty(a) ? 1 : -1;
        return compareValues(a, b) * (sort.direction === 'desc' ? -1 : 1) || left.index - right.index;
      });
    }
    return list;
  }, [rows, filters, choice, query, search, sort, columns, texts]);
  React.useEffect(() => { setPage(0); }, [search, choice, sort, query]);
  const pages = Math.max(1, Math.ceil(visible.length / OMICS_PAGE));
  const at = Math.min(page, pages - 1);
  const shown = visible.slice(at * OMICS_PAGE, (at + 1) * OMICS_PAGE);
  const toggleSort = (column) => setSort((current) => {
    if (!current || current.key !== column.key) return { key: column.key, direction: column.firstDirection || 'asc' };
    return { key: column.key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  });
  const toggleRow = (key) => setOpen((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  return (
    <div className="xo-omx-table">
      <div className="xo-omx-toolbar">
        <SearchField className="xo-omx-search" value={search} onChange={setSearch} placeholder={searchLabel || tx('ui.patient.omx_search_table')} />
        {filters.map((filter) => (
          <label key={filter.key} className="xo-omx-filter">
            <span>{filter.label}</span>
            <XomicsSelect value={choice[filter.key]} aria-label={filter.label}
              onChange={(event) => setChoice((current) => ({ ...current, [filter.key]: event.target.value }))}>
              {filter.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </XomicsSelect>
          </label>
        ))}
        <span className="xo-omx-count" aria-live="polite">{tx('ui.patient.omx_rows_of', { shown: NUMBER.format(visible.length), total: NUMBER.format(rows.length) })}</span>
      </div>
      {visible.length === 0 ? <EmptyState>{rows.length === 0 ? empty : tx('ui.patient.omx_no_rows_match')}</EmptyState> : (
        <div className="xo-pv-table-wrap">
          <table className="xo-pv-table xo-omx-grid" aria-label={label}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className={column.align === 'end' ? 'is-end' : ''}
                    aria-sort={sort?.key === column.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}>
                    {column.sortable === false ? column.label : (
                      <button type="button" onClick={() => toggleSort(column)}>
                        {column.label}
                        {sort?.key === column.key ? (sort.direction === 'asc' ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />) : <ChevronsUpDown aria-hidden="true" />}
                      </button>
                    )}
                  </th>
                ))}
                {expandable && <th className="is-toggle"><span className="xo-visually-hidden">{tx('ui.patient.omx_details')}</span></th>}
              </tr>
            </thead>
            <tbody>
              {shown.map(({ row, index }) => {
                const key = rowKey ? rowKey(row, index) : index;
                const expanded = open.has(key);
                return (
                  <React.Fragment key={key}>
                    <tr className={[expanded ? 'is-open' : '', withVisibility && row.hidden === true ? 'is-hidden' : ''].filter(Boolean).join(' ')}>
                      {columns.map((column) => (
                        <td key={column.key} className={column.align === 'end' ? 'is-end' : ''}>
                          {column.render ? column.render(row) : (hasValue(row[column.key]) ? formatValue(row[column.key]) : <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span>)}
                        </td>
                      ))}
                      {expandable && (
                        <td className="is-toggle">
                          <span className="xo-omx-row-controls">
                            {withVisibility && <RowVisibility row={row} />}
                            <button type="button" aria-expanded={expanded} aria-label={tx('ui.patient.omx_details')} onClick={() => toggleRow(key)}>
                              <ChevronRight aria-hidden="true" />
                            </button>
                          </span>
                        </td>
                      )}
                    </tr>
                    {expanded && detail && (
                      <tr className="xo-omx-detail-row"><td colSpan={columns.length + 1}>{detail(row)}</td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && (
        <div className="xo-omx-pager">
          <span>{tx('ui.patient.omx_page_range', {
            from: NUMBER.format(at * OMICS_PAGE + 1), to: NUMBER.format(at * OMICS_PAGE + shown.length), total: NUMBER.format(visible.length),
          })}</span>
          <button type="button" disabled={at === 0} onClick={() => setPage(at - 1)} aria-label={tx('ui.patient.previous')}><ChevronLeft aria-hidden="true" /></button>
          <span className="xo-omx-page">{tx('ui.patient.omx_page_of', { page: NUMBER.format(at + 1), pages: NUMBER.format(pages) })}</span>
          <button type="button" disabled={at >= pages - 1} onClick={() => setPage(at + 1)} aria-label={tx('ui.patient.next')}><ChevronRight aria-hidden="true" /></button>
        </div>
      )}
    </div>
  );
}

// Evidence under an opened row or a finding: label and value pairs, empty ones left out.
function Evidence({ items }) {
  const present = items.filter((item) => item && hasValue(item.value));
  if (present.length === 0) return <p className="xo-omx-none">{tx('ui.patient.omx_no_more_detail')}</p>;
  return (
    <dl className="xo-omx-evidence">
      {present.map((item) => <div key={item.label}><dt>{item.label}</dt><dd>{item.value}</dd></div>)}
    </dl>
  );
}

// A result a clinician may raise with the patient: what it is first, then the
// facts that say how sure and how serious, and the evidence behind a toggle.
function Finding({ tone, title, chips, lines, facts, evidence, note }) {
  const [open, setOpen] = React.useState(false);
  return (
    <article className={`xo-omx-finding is-${tone || 'info'}`}>
      <OmicsMark tone={tone} />
      <div className="xo-omx-finding-main">
        <header><strong>{title}</strong>{chips}</header>
        {lines.filter(Boolean).length > 0 && <p className="xo-omx-finding-line">{lines.filter(Boolean).join(' · ')}</p>}
        {facts && <Evidence items={facts} />}
        {note && <p className="xo-omx-finding-note">{note}</p>}
        {evidence && open && <Evidence items={evidence} />}
      </div>
      {evidence && (
        <button type="button" className="xo-omx-link xo-omx-finding-toggle" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          {tx(open ? 'ui.patient.omx_hide_evidence' : 'ui.patient.omx_show_evidence')}
          {open ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
        </button>
      )}
    </article>
  );
}

function FindingList({ items, empty, limit, onMore, moreLabel }) {
  if (items.length === 0) return <EmptyState>{empty}</EmptyState>;
  const shown = limit ? items.slice(0, limit) : items;
  return (
    <div className="xo-omx-findings">
      {shown}
      {limit && items.length > limit && onMore && <SeeAll label={moreLabel} onClick={onMore} />}
    </div>
  );
}

// ------------------------------------------------ shared words for codes

// The variant as a clinician reads it: the change, with its transcript apart.
function hgvsParts(value) {
  if (!hasValue(value)) return null;
  const text = String(value);
  const at = text.indexOf(':');
  return at > 0 ? { change: text.slice(at + 1), reference: text.slice(0, at) } : { change: text, reference: null };
}

function VariantCell({ row, cKey = 'hgvsc', pKey = 'hgvsp' }) {
  const c = hgvsParts(row[cKey]);
  const p = hgvsParts(row[pKey]);
  if (!c && !p) return <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span>;
  return (
    <span className="xo-omx-variant is-hgvs">
      {c && <strong title={c.reference || undefined}>{c.change}</strong>}
      {p && <small>{p.change}</small>}
    </span>
  );
}

function variantLine(row, cKey = 'hgvsc', pKey = 'hgvsp') {
  return [hgvsParts(row[cKey])?.change, hgvsParts(row[pKey])?.change].filter(Boolean).join(' ');
}

function genomicPosition(row, chromKey = 'chrom') {
  if (!hasValue(row[chromKey]) || !hasValue(row.pos)) return null;
  const change = hasValue(row.ref) && hasValue(row.alt) ? ` ${row.ref}>${row.alt}` : '';
  return `chr${String(row[chromKey]).replace(/^chr/i, '')}:${NUMBER.format(Number(row.pos))}${change}`;
}

// Classification codes read with spaces; ClinVar joins several with & and /.
function classWords(value) {
  if (!hasValue(value)) return null;
  return String(value).split('&').map((part) => part.split('/').map((word) => human(word.replace(/^_+/, ''))).join(' / ')).join(', ');
}

const CLASS_TONE = Object.freeze({
  pathogenic: 'bad', likely_pathogenic: 'bad', uncertain_significance: 'warn', likely_benign: 'muted', benign: 'muted',
});

function ClassChip({ value, prefix }) {
  if (!hasValue(value)) return null;
  const tone = CLASS_TONE[String(value).toLowerCase()] || 'muted';
  return <Chip tone={tone}>{prefix ? `${prefix} ${classWords(value)}` : classWords(value)}</Chip>;
}

const INHERITANCE_KEYS = Object.freeze({
  AR: 'ui.patient.omx_inheritance_ar', AD: 'ui.patient.omx_inheritance_ad', XLR: 'ui.patient.omx_inheritance_xlr',
  XLD: 'ui.patient.omx_inheritance_xld', XL: 'ui.patient.omx_inheritance_xl', MT: 'ui.patient.omx_inheritance_mt',
});

function inheritanceWords(row) {
  if (hasValue(row.mode_of_inheritance)) return String(row.mode_of_inheritance);
  const code = String(row.inheritances || row.inheritance || '').trim();
  if (!code) return null;
  return code.split(/[,;/]\s*/).map((part) => (INHERITANCE_KEYS[part.toUpperCase()] ? tx(INHERITANCE_KEYS[part.toUpperCase()]) : part)).join(', ');
}

// An allele frequency (0 to 1) as a short percentage: 29.8%, 0.81%, 0.0004%.
function frequencyPercent(value) {
  const parsed = numeric(value);
  if (parsed === null) return null;
  const pct = parsed * 100;
  if (pct === 0) return '0%';
  return `${pct >= 1 ? formatNumber(pct, 1) : pct >= 0.01 ? formatNumber(pct, 2) : pct.toPrecision(1)}%`;
}

// A z-score with its sign and fixed decimals, so a column of them lines up.
function zText(value, digits = 2) {
  const z = numeric(value);
  if (z === null) return null;
  const text = Math.abs(z).toFixed(digits);
  return Number(text) === 0 ? text : `${z > 0 ? '+' : '−'}${text}`;
}

function frequencyWords(value) {
  const parsed = numeric(value);
  if (parsed === null) return null;
  if (parsed === 0) return tx('ui.patient.omx_not_seen_in_population');
  return tx('ui.patient.omx_population_frequency', { value: frequencyPercent(parsed) });
}

function reviewWords(row) {
  const stars = numeric(row.reviewstar);
  const status = hasValue(row.clinvar_clnrevstat) ? human(String(row.clinvar_clnrevstat).replace(/,_?/g, ', ')) : null;
  if (stars === null) return status;
  return [counted('ui.patient.omx_review_stars', stars), status].filter(Boolean).join(' · ');
}

// ------------------------------------------------ genome

const IMPORTANCE = Object.freeze({
  very_important: { key: 'ui.patient.omx_importance_very', tone: 'bad', rank: 0 },
  important: { key: 'ui.patient.omx_importance_important', tone: 'warn', rank: 1 },
  noteworthy: { key: 'ui.patient.omx_importance_noteworthy', tone: 'info', rank: 2 },
});

function importanceOf(row) {
  return IMPORTANCE[String(row.importance_code || '').toLowerCase()] || null;
}

const TARGET_GROUPS = Object.freeze({
  'Carrier-screening': 'ui.patient.omx_group_carrier',
  'Newborn-screening': 'ui.patient.omx_group_newborn',
  'Health predipositions/Disease risk': 'ui.patient.omx_group_risk',
  'Heriditary-cancer risk syndrome': 'ui.patient.omx_group_cancer',
  Expanded_clinvar_mono_diseases: 'ui.patient.omx_group_clinvar',
  Expanded_mono_rare_diseases: 'ui.patient.omx_group_rare',
});

function groupWords(value) {
  if (!hasValue(value)) return null;
  return TARGET_GROUPS[value] ? tx(TARGET_GROUPS[value]) : human(value);
}

function acmgRank(value) {
  return ['pathogenic', 'likely_pathogenic', 'uncertain_significance', 'likely_benign', 'benign'].indexOf(String(value || '').toLowerCase());
}

function genomeFindingOrder(left, right) {
  return (importanceOf(left)?.rank ?? 9) - (importanceOf(right)?.rank ?? 9)
    || (acmgRank(left.acmg_classification) < 0 ? 9 : acmgRank(left.acmg_classification)) - (acmgRank(right.acmg_classification) < 0 ? 9 : acmgRank(right.acmg_classification))
    || compareValues(left.genes, right.genes);
}

function variantEvidence(row) {
  return [
    { label: tx('ui.patient.omx_acmg_criteria'), value: row.acmg_criteria },
    { label: tx('ui.patient.omx_clinvar'), value: classWords(row.clinvar_clnsig) },
    { label: tx('ui.patient.omx_review'), value: reviewWords(row) },
    { label: tx('ui.patient.omx_clingen'), value: classWords(row.clingen_classification) },
    { label: tx('ui.patient.omx_gene_disease_confidence'), value: row.gene_disease_confidence_level },
    { label: tx('ui.patient.omx_consequence'), value: [human(row.consequence), row.impact ? human(row.impact) : null].filter(Boolean).join(' · ') },
    { label: tx('ui.patient.omx_frequency'), value: frequencyWords(row.max_af) },
    { label: tx('ui.patient.omx_predictors'), value: [
      hasValue(row.cadd_phred) ? tx('ui.patient.omx_cadd', { value: row.cadd_phred }) : null,
      hasValue(row.revel_score) ? tx('ui.patient.omx_revel', { value: row.revel_score }) : null,
      hasValue(row.am_class) ? tx('ui.patient.omx_alphamissense', { value: human(row.am_class) }) : null,
    ].filter(Boolean).join(' · ') },
    { label: tx('ui.patient.omx_position'), value: [genomicPosition(row), row.id && String(row.id).startsWith('rs') ? row.id : null].filter(Boolean).join(' · ') },
    { label: tx('ui.patient.omx_transcript'), value: hgvsParts(row.hgvsc)?.reference },
    { label: tx('ui.patient.omx_panel_group'), value: groupWords(row.target_group) },
    { label: tx('ui.patient.omx_why_listed'), value: row.reason },
    { label: tx('ui.patient.omx_interpretation_summary'), value: row.summary_of_interpretation },
    { label: tx('ui.patient.omx_identifiers'), value: [row.mondo_id, row.omim_id ? `OMIM ${row.omim_id}` : null, row.clinvar_id ? `ClinVar ${row.clinvar_id}` : null].filter(Boolean).join(' · ') },
  ];
}

function GenomeFinding({ row }) {
  const importance = importanceOf(row);
  return (
    <Finding tone={importance?.tone || 'info'} title={row.disease || tx('ui.patient.omx_condition_not_named')}
      chips={<>{importance && <Chip tone={importance.tone}>{tx(importance.key)}</Chip>}<ClassChip value={row.acmg_classification} prefix="ACMG" /></>}
      lines={[row.genes, variantLine(row), human(row.zygosity)]}
      facts={[
        { label: tx('ui.patient.inheritance'), value: inheritanceWords(row) },
        { label: tx('ui.patient.omx_clinvar'), value: classWords(row.clinvar_clnsig) },
        { label: tx('ui.patient.omx_panel_group'), value: groupWords(row.target_group) },
      ]}
      evidence={variantEvidence(row)} />
  );
}

// The whole_genome report's drug colours: very important red, important
// amber, for-information light (pharmacogenetics.js drawDrugGrid).
function pharmacoTone(row) {
  const code = String(row.importance_code || '').toLowerCase();
  if (code === 'vip') return 'bad';
  if (code === 'high') return 'warn';
  return 'info';
}

function isGuidedDrug(row) {
  return ['vip', 'high'].includes(String(row.importance_code || '').toLowerCase()) && !String(row.rsid || '').includes('*');
}

const PHARMACO_IMPORTANCE = Object.freeze({ vip: 'ui.patient.omx_drug_vip', high: 'ui.patient.omx_drug_high', low: 'ui.patient.omx_drug_low' });

function PharmacoFinding({ row }) {
  const code = String(row.importance_code || '').toLowerCase();
  return (
    <Finding tone={pharmacoTone(row)} title={tx('ui.patient.omx_gene_drug', { gene: row.gene || tx('ui.patient.omx_gene_unknown'), drug: row.drug_s || tx('ui.patient.omx_drug_unknown') })}
      chips={<>{PHARMACO_IMPORTANCE[code] && <Chip tone={pharmacoTone(row)}>{tx(PHARMACO_IMPORTANCE[code])}</Chip>}{hasValue(row.level_of_evidence) && <Chip>{tx('ui.patient.omx_evidence_level', { level: row.level_of_evidence })}</Chip>}</>}
      lines={[row.rsid, hasValue(row.genotype) ? tx('ui.patient.omx_genotype_value', { genotype: row.genotype }) : null, row.phenotype_category, row.phenotype_s]}
      note={row.annotation_text}
      evidence={[
        { label: tx('ui.patient.zygosity'), value: human(row.zygosity) },
        { label: tx('ui.patient.omx_evidence_score'), value: hasValue(row.score) ? formatNumber(row.score, 2) : null },
        { label: tx('ui.patient.omx_source'), value: row.url ? <a href={row.url} target="_blank" rel="noreferrer">{tx('ui.patient.omx_pharmgkb')}<ExternalLink aria-hidden="true" /></a> : null },
      ]} />
  );
}

function GenomeView({ entry, query }) {
  // The tables list hidden rows too, for whoever may show them again; the summary
  // and its counts are what reports, the app and the digital twin show.
  const inheritanceRows = readRows(entry, 'genomeInheritance', ['importance_code', 'disease', 'genes', 'hgvsc', 'zygosity'], { withHidden: true });
  const pharmacoRows = readRows(entry, 'genomePharmaco', ['annotation_id', 'genotype', 'importance_code', 'gene', 'drug_s'], { withHidden: true });
  const traitRows = readRows(entry, 'genomeTraits', ['traits_name', 'category', 'patient_genotypes', 'genotype_description'], { withHidden: true });
  const shownRows = (rows) => rows.filter((row) => row.hidden !== true);
  const inheritance = shownRows(inheritanceRows);
  const pharmaco = shownRows(pharmacoRows);
  const traits = shownRows(traitRows);
  const prioritised = inheritance.filter(importanceOf).sort(genomeFindingOrder);
  const toDiscuss = prioritised.filter((row) => importanceOf(row).rank < 2);
  // The whole_genome report's drug groups: very important and important
  // (vip, high), leaving out star-allele rows (an rsID containing "*").
  const actionable = pharmaco.filter(isGuidedDrug)
    .sort((left, right) => pharmacoTone(left).localeCompare(pharmacoTone(right)) || compareValues(left.gene, right.gene));
  const differing = traits.filter((row) => String(row.zygosity || '').toLowerCase() !== 'homo_ref');
  const count = (code) => inheritance.filter((row) => String(row.importance_code || '').toLowerCase() === code).length;
  const groups = [...new Set(inheritance.map((row) => row.target_group).filter(hasValue))];
  const acmgClasses = [...new Set(inheritance.map((row) => row.acmg_classification).filter(hasValue))].sort((a, b) => acmgRank(a) - acmgRank(b));
  const categories = [...new Set(traits.map((row) => row.category).filter(hasValue))].sort(compareValues);
  const drugCategories = [...new Set(pharmaco.map((row) => row.phenotype_category).filter(hasValue))].sort(compareValues);
  const variantColumns = [
    { key: 'importance_code', label: tx('ui.patient.importance'), sort: (row) => importanceOf(row)?.rank ?? 9,
      render: (row) => { const importance = importanceOf(row); return importance ? <span className="xo-omx-cell-mark"><OmicsMark tone={importance.tone} />{tx(importance.key)}</span> : <span className="xo-omx-none">{tx('ui.patient.omx_not_prioritised')}</span>; } },
    { key: 'disease', label: tx('ui.patient.omx_condition') },
    { key: 'genes', label: tx('ui.patient.gene'), render: (row) => <strong>{row.genes}</strong> },
    { key: 'hgvsc', label: tx('ui.patient.variant'), text: (row) => `${row.hgvsc || ''} ${row.hgvsp || ''}`, render: (row) => <VariantCell row={row} /> },
    { key: 'zygosity', label: tx('ui.patient.zygosity'), render: (row) => human(row.zygosity) },
    { key: 'acmg_classification', label: tx('ui.patient.omx_classification'), sort: (row) => acmgRank(row.acmg_classification),
      text: (row) => `${row.acmg_classification || ''} ${row.clinvar_clnsig || ''}`,
      render: (row) => <span className="xo-omx-class"><ClassChip value={row.acmg_classification} prefix="ACMG" />{hasValue(row.clinvar_clnsig) && <small>{tx('ui.patient.omx_clinvar_value', { value: classWords(row.clinvar_clnsig) })}</small>}</span> },
    { key: 'mode_of_inheritance', label: tx('ui.patient.inheritance'), text: inheritanceWords, render: (row) => inheritanceWords(row) || <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span> },
  ];
  return (
    <OmicsView record={entry}
      about={tx('ui.patient.omx_genome_about')}
      facts={[
        { label: tx('ui.patient.omx_importance_very'), value: count('very_important'), tone: count('very_important') ? 'bad' : 'good' },
        { label: tx('ui.patient.omx_importance_important'), value: count('important'), tone: count('important') ? 'warn' : 'good' },
        { label: tx('ui.patient.omx_importance_noteworthy'), value: count('noteworthy') },
        { label: tx('ui.patient.omx_drug_guidance'), value: actionable.length, tone: actionable.length ? 'warn' : 'good' },
        { label: tx('ui.patient.omx_traits_differing'), value: tx('ui.patient.omx_of_total', { count: NUMBER.format(differing.length), total: NUMBER.format(traits.length) }) },
      ]}
      tabs={[
        {
          value: 'summary', label: tx('ui.patient.omx_tab_summary'),
          render: (go) => (
            <div className="xo-omx-summary">
              <OmicsBlock title={tx('ui.patient.omx_genome_discuss')} detail={tx('ui.patient.omx_genome_discuss_detail')}
                action={prioritised.length > 0 && <SeeAll label={tx('ui.patient.omx_all_prioritised', { count: NUMBER.format(prioritised.length) })} onClick={() => go('variants')} />}>
                <FindingList items={toDiscuss.map((row) => <GenomeFinding key={row.sourceRow} row={row} />)} limit={6}
                  empty={tx('ui.patient.omx_genome_none')} onMore={() => go('variants')} moreLabel={tx('ui.patient.omx_see_all_findings', { count: NUMBER.format(toDiscuss.length) })} />
              </OmicsBlock>
              <OmicsBlock title={tx('ui.patient.omx_drug_title')} detail={tx('ui.patient.omx_drug_detail')}
                action={<SeeAll label={tx('ui.patient.omx_all_drug', { count: NUMBER.format(pharmaco.length) })} onClick={() => go('drugs')} />}>
                <FindingList items={actionable.map((row) => <PharmacoFinding key={row.sourceRow} row={row} />)} limit={4}
                  empty={tx('ui.patient.omx_drug_none')} onMore={() => go('drugs')} moreLabel={tx('ui.patient.omx_see_all_findings', { count: NUMBER.format(actionable.length) })} />
              </OmicsBlock>
              <OmicsBlock title={tx('ui.patient.omx_traits_title')} detail={tx('ui.patient.omx_traits_detail')}
                action={<SeeAll label={tx('ui.patient.omx_all_traits', { count: NUMBER.format(traits.length) })} onClick={() => go('traits')} />}>
                <ul className="xo-omx-traits">
                  {differing.slice(0, 6).map((row) => (
                    <li key={row.sourceRow}><strong>{row.traits_name}</strong><span>{row.genotype_description || tx('ui.patient.omx_not_given')}</span><small>{[row.genes, row.patient_genotypes].filter(Boolean).join(' · ')}</small></li>
                  ))}
                </ul>
                {differing.length === 0 && <EmptyState>{tx('ui.patient.omx_traits_none')}</EmptyState>}
              </OmicsBlock>
            </div>
          ),
        },
        {
          value: 'variants', label: tx('ui.patient.omx_tab_disease_variants'), count: prioritised.length,
          render: () => (
            <OmicsTable rows={inheritanceRows} query={query} columns={variantColumns} label={tx('ui.patient.omx_tab_disease_variants')} hideable
              rowKey={(row) => row.sourceRow} initialSort={{ key: 'importance_code', direction: 'asc' }} detail={(row) => <Evidence items={variantEvidence(row)} />}
              empty={tx('ui.patient.omx_genome_none')} searchLabel={tx('ui.patient.omx_search_variants')}
              filters={[
                { key: 'importance', label: tx('ui.patient.importance'), initial: 'prioritised', options: [
                  { value: 'prioritised', label: tx('ui.patient.omx_prioritised') }, { value: 'very_important', label: tx('ui.patient.omx_importance_very') },
                  { value: 'important', label: tx('ui.patient.omx_importance_important') }, { value: 'noteworthy', label: tx('ui.patient.omx_importance_noteworthy') },
                  { value: 'none', label: tx('ui.patient.omx_not_prioritised') }, { value: 'all', label: tx('ui.patient.omx_all') }],
                  test: (row, value) => (value === 'prioritised' ? Boolean(importanceOf(row)) : value === 'none' ? !importanceOf(row) : String(row.importance_code || '').toLowerCase() === value) },
                { key: 'group', label: tx('ui.patient.omx_panel_group'), options: [{ value: 'all', label: tx('ui.patient.omx_all') }, ...groups.map((group) => ({ value: group, label: groupWords(group) }))],
                  test: (row, value) => row.target_group === value },
                { key: 'acmg', label: tx('ui.patient.omx_classification'), options: [{ value: 'all', label: tx('ui.patient.omx_all') }, ...acmgClasses.map((value) => ({ value, label: classWords(value) }))],
                  test: (row, value) => row.acmg_classification === value },
              ]} />
          ),
        },
        {
          value: 'drugs', label: tx('ui.patient.omx_tab_drug_response'), count: actionable.length,
          render: () => (
            <OmicsTable rows={pharmacoRows} query={query} label={tx('ui.patient.omx_tab_drug_response')} rowKey={(row) => row.sourceRow} hideable
              initialSort={{ key: 'importance_code', direction: 'asc' }} searchLabel={tx('ui.patient.omx_search_drugs')} empty={tx('ui.patient.omx_drug_none')}
              detail={(row) => <Evidence items={[
                { label: tx('ui.patient.omx_guidance'), value: row.annotation_text },
                { label: tx('ui.patient.zygosity'), value: human(row.zygosity) },
                { label: tx('ui.patient.omx_evidence_score'), value: hasValue(row.score) ? formatNumber(row.score, 2) : null },
                { label: tx('ui.patient.omx_source'), value: row.url ? <a href={row.url} target="_blank" rel="noreferrer">{tx('ui.patient.omx_pharmgkb')}<ExternalLink aria-hidden="true" /></a> : null },
              ]} />}
              columns={[
                { key: 'importance_code', label: tx('ui.patient.importance'), sort: (row) => ({ vip: 0, high: 1, low: 2 }[String(row.importance_code || '').toLowerCase()] ?? 3),
                  render: (row) => { const code = String(row.importance_code || '').toLowerCase(); return PHARMACO_IMPORTANCE[code] ? <span className="xo-omx-cell-mark"><OmicsMark tone={pharmacoTone(row)} />{tx(PHARMACO_IMPORTANCE[code])}</span> : <span className="xo-omx-none">{tx('ui.patient.omx_not_prioritised')}</span>; } },
                { key: 'gene', label: tx('ui.patient.gene'), render: (row) => <strong>{row.gene}</strong> },
                { key: 'drug_s', label: tx('ui.patient.drug') },
                { key: 'phenotype_category', label: tx('ui.patient.omx_effect'), text: (row) => `${row.phenotype_category || ''} ${row.phenotype_s || ''}`,
                  render: (row) => <span className="xo-omx-variant"><strong>{row.phenotype_category || tx('ui.patient.omx_not_given')}</strong>{row.phenotype_s && <small>{row.phenotype_s}</small>}</span> },
                { key: 'rsid', label: tx('ui.patient.variant') },
                { key: 'genotype', label: tx('ui.patient.genotype') },
                { key: 'level_of_evidence', label: tx('ui.patient.omx_evidence') },
              ]}
              filters={[
                { key: 'importance', label: tx('ui.patient.importance'), initial: 'actionable', options: [
                  { value: 'actionable', label: tx('ui.patient.omx_drug_actionable') }, { value: 'vip', label: tx('ui.patient.omx_drug_vip') },
                  { value: 'high', label: tx('ui.patient.omx_drug_high') }, { value: 'low', label: tx('ui.patient.omx_drug_low') },
                  { value: 'none', label: tx('ui.patient.omx_not_prioritised') }, { value: 'all', label: tx('ui.patient.omx_all') }],
                  test: (row, value) => { const code = String(row.importance_code || '').toLowerCase(); return value === 'actionable' ? isGuidedDrug(row) : value === 'none' ? !code : code === value; } },
                { key: 'category', label: tx('ui.patient.omx_effect'), options: [{ value: 'all', label: tx('ui.patient.omx_all') }, ...drugCategories.map((value) => ({ value, label: value }))],
                  test: (row, value) => row.phenotype_category === value },
              ]} />
          ),
        },
        {
          value: 'traits', label: tx('ui.patient.traits'), count: traits.length,
          render: () => (
            <OmicsTable rows={traitRows} query={query} label={tx('ui.patient.traits')} rowKey={(row) => row.sourceRow} hideable searchLabel={tx('ui.patient.omx_search_traits')}
              initialSort={{ key: 'category', direction: 'asc' }} empty={tx('ui.patient.omx_traits_none')}
              detail={(row) => <Evidence items={[{ label: tx('ui.patient.description'), value: row.description }, { label: tx('ui.patient.variants'), value: row.variants }, { label: tx('ui.patient.zygosity'), value: String(row.zygosity).toLowerCase() === 'homo_ref' ? tx('ui.patient.omx_reference_genotype') : human(row.zygosity) }]} />}
              columns={[
                { key: 'traits_name', label: tx('ui.patient.trait'), render: (row) => <strong>{row.traits_name}</strong> },
                { key: 'category', label: tx('ui.patient.category') },
                { key: 'genotype_description', label: tx('ui.patient.omx_result') },
                { key: 'patient_genotypes', label: tx('ui.patient.genotype') },
                { key: 'genes', label: tx('ui.patient.genes') },
              ]}
              filters={[
                { key: 'genotype', label: tx('ui.patient.genotype'), initial: 'differs', options: [
                  { value: 'all', label: tx('ui.patient.omx_all') }, { value: 'differs', label: tx('ui.patient.omx_differs_from_reference') },
                  { value: 'reference', label: tx('ui.patient.omx_reference_genotype') }],
                  test: (row, value) => (String(row.zygosity || '').toLowerCase() === 'homo_ref') === (value === 'reference') },
                { key: 'category', label: tx('ui.patient.category'), options: [{ value: 'all', label: tx('ui.patient.omx_all') }, ...categories.map((value) => ({ value, label: value }))],
                  test: (row, value) => row.category === value },
              ]} />
          ),
        },
      ]} />
  );
}

// ------------------------------------------------ carrier and newborn screening

// The screening tables give either finding rows or a single message row
// saying nothing reportable was found.
// The FamilyRisk individual reports' rows (familyrisk-individual data.js): a
// row is a finding when it names a disease, a c. change, a gene or a
// position, once per disease and variant; the rest are the pipeline's messages.
function isScreeningFinding(row) {
  return [row.disease, row.hgvsc, row.genes, row.pos].some(hasValue);
}

function screeningFindings(rows) {
  const seen = new Set();
  return rows.filter((row) => {
    if (!isScreeningFinding(row)) return false;
    const key = [row.disease, row.genes, row.x_chrom, row.pos, row.ref, row.alt, row.hgvsc].map((part) => String(part ?? '')).join('\u0000');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function screeningMessages(rows) {
  return rows.filter((row) => !isScreeningFinding(row) && hasValue(row.message)).map((row) => row.message);
}

// A finding is the reports' red whether screening for carrier status or the
// newborn panel; zygosity is shown as the pipeline writes it.
function ScreeningFinding({ row, tone = 'bad' }) {
  return (
    <Finding tone={tone} title={row.disease || tx('ui.patient.omx_condition_not_named')}
      chips={<ClassChip value={row.acmg_classification} prefix="ACMG" />}
      lines={[row.genes, variantLine(row), human(row.zygosity)]}
      facts={[
        { label: tx('ui.patient.inheritance'), value: inheritanceWords(row) },
        { label: tx('ui.patient.omx_clinvar'), value: classWords(row.clinvar_clnsig) },
        { label: tx('ui.patient.omx_frequency'), value: frequencyWords(row.max_af) },
        { label: tx('ui.patient.omx_other_programmes'), value: [
          // The condition name other programmes report, left out when it only repeats this finding's condition.
          String(row.reported_disease_from_other_nbseq_projects || '').trim().toLowerCase() === String(row.disease || '').trim().toLowerCase() ? null : row.reported_disease_from_other_nbseq_projects,
          row.results_from_other_nbseq_projects,
        ].filter(hasValue).join(' · ') },
      ]}
      evidence={[
        { label: tx('ui.patient.omx_position'), value: genomicPosition(row, 'x_chrom') },
        { label: tx('ui.patient.omx_transcript'), value: hgvsParts(row.hgvsc)?.reference },
        { label: tx('ui.patient.impact'), value: hasValue(row.impact) ? human(row.impact) : null },
      ]} />
  );
}

function candidateVariantTable(rows, query, label) {
  const classes = [...new Set(rows.map((row) => row.acmg_classification).filter(hasValue))].sort((a, b) => acmgRank(a) - acmgRank(b));
  return (
    <OmicsTable rows={rows} query={query} label={label} rowKey={(row) => row.sourceRow} searchLabel={tx('ui.patient.omx_search_variants')}
      initialSort={{ key: 'acmg_classification', direction: 'asc' }} empty={tx('ui.patient.omx_no_variants')}
      detail={(row) => <Evidence items={[
        { label: tx('ui.patient.omx_acmg_criteria'), value: row.acmg_criteria },
        { label: tx('ui.patient.omx_clinvar_condition'), value: hasValue(row.clinvar_clndn) ? human(String(row.clinvar_clndn).replace(/\|/g, ', ')) : null },
        { label: tx('ui.patient.omx_review'), value: hasValue(row.clinvar_clnrevstat) ? human(String(row.clinvar_clnrevstat).replace(/,_?/g, ', ')) : null },
        { label: tx('ui.patient.omx_consequence'), value: [human(row.consequence), row.impact ? human(row.impact) : null].filter(Boolean).join(' · ') },
        { label: tx('ui.patient.omx_frequency'), value: frequencyWords(row.max_af) },
        { label: tx('ui.patient.omx_predictors'), value: [hasValue(row.revel_score) ? tx('ui.patient.omx_revel', { value: row.revel_score }) : null,
          hasValue(row.alphamissense_class) ? tx('ui.patient.omx_alphamissense', { value: human(row.alphamissense_class) }) : null].filter(Boolean).join(' · ') },
        { label: tx('ui.patient.omx_position'), value: [genomicPosition(row, 'x_chrom'), row.rsid].filter(Boolean).join(' · ') },
        { label: tx('ui.patient.omx_disorder_group'), value: row.disorder_group },
      ]} />}
      columns={[
        { key: 'genes', label: tx('ui.patient.gene'), text: (row) => row.genes || row.symbol, render: (row) => <strong>{row.genes || row.symbol}</strong> },
        { key: 'hgvsc', label: tx('ui.patient.variant'), text: (row) => `${row.hgvsc || ''} ${row.hgvsp || ''}`, render: (row) => <VariantCell row={row} /> },
        { key: 'zygosity', label: tx('ui.patient.zygosity'), render: (row) => human(row.zygosity) },
        { key: 'acmg_classification', label: tx('ui.patient.omx_classification'), sort: (row) => acmgRank(row.acmg_classification),
          render: (row) => <span className="xo-omx-class"><ClassChip value={row.acmg_classification} prefix="ACMG" />{hasValue(row.clinvar_clnsig) && <small>{tx('ui.patient.omx_clinvar_value', { value: classWords(row.clinvar_clnsig) })}</small>}</span> },
        { key: 'disease', label: tx('ui.patient.omx_condition') },
        { key: 'inheritance', label: tx('ui.patient.inheritance'), text: inheritanceWords, render: (row) => inheritanceWords(row) || <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span> },
        { key: 'max_af', label: tx('ui.patient.omx_frequency_short'), align: 'end', sort: (row) => numeric(row.max_af), render: (row) => (numeric(row.max_af) === null ? <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span> : frequencyPercent(row.max_af)) },
      ]}
      filters={[
        { key: 'acmg', label: tx('ui.patient.omx_classification'), options: [{ value: 'all', label: tx('ui.patient.omx_all') }, { value: 'plp', label: tx('ui.patient.omx_pathogenic_or_likely') }, ...classes.map((value) => ({ value, label: classWords(value) }))],
          test: (row, value) => (value === 'plp' ? acmgRank(row.acmg_classification) >= 0 && acmgRank(row.acmg_classification) < 2 : row.acmg_classification === value) },
        { key: 'impact', label: tx('ui.patient.impact'), options: [{ value: 'all', label: tx('ui.patient.omx_all') }, ...['HIGH', 'MODERATE', 'LOW', 'MODIFIER'].map((value) => ({ value, label: human(value) }))],
          test: (row, value) => String(row.impact || '').toUpperCase() === value },
      ]} />
  );
}

// With no finding, a report-covered result shows the report's clear result
// (its title and line); a table no report covers shows the pipeline's message.
function ScreeningResult({ findings, messages, tone, empty, emptyDetail = null }) {
  if (findings.length === 0) {
    return (
      <div className="xo-omx-verdict is-good">
        <OmicsMark tone="good" />
        {emptyDetail ? <div><strong>{empty}</strong><p>{emptyDetail}</p></div>
          : <div><strong>{messages[0] || empty}</strong>{messages.slice(1).map((message) => <p key={message}>{message}</p>)}</div>}
      </div>
    );
  }
  return <div className="xo-omx-findings">{findings.map((row) => <ScreeningFinding key={row.sourceRow} row={row} tone={tone} />)}</div>;
}

function CarrierView({ entry, query }) {
  const screening = readRows(entry, 'carrierScreening', ['disease', 'genes', 'hgvsc', 'zygosity']);
  const variants = readRows(entry, 'carrierVariants', ['genes', 'hgvsc', 'zygosity', 'acmg_classification']);
  const findings = screeningFindings(screening);
  const genes = [...new Set(findings.map((row) => row.genes).filter(hasValue))];
  return (
    <OmicsView record={entry} about={tx('ui.patient.omx_carrier_about')}
      facts={[
        { label: tx('ui.patient.carrier_findings'), value: findings.length, tone: findings.length ? 'bad' : 'good' },
        { label: tx('ui.patient.genes_with_findings'), value: genes.length ? genes.join(', ') : tx('ui.patient.omx_none') },
        { label: tx('ui.patient.omx_candidate_variants'), value: variants.length },
      ]}
      tabs={[
        { value: 'summary', label: tx('ui.patient.omx_tab_summary'), render: (go) => (
          <div className="xo-omx-summary">
            <OmicsBlock title={tx('ui.patient.omx_carrier_result')} detail={tx('ui.patient.omx_carrier_result_detail')}
              action={<SeeAll label={tx('ui.patient.omx_all_candidates', { count: NUMBER.format(variants.length) })} onClick={() => go('variants')} />}>
              <ScreeningResult findings={findings} messages={screeningMessages(screening)} tone="bad" empty={tx('ui.patient.omx_carrier_none')} emptyDetail={tx('ui.patient.omx_carrier_none_detail')} />
            </OmicsBlock>
          </div>
        ) },
        { value: 'variants', label: tx('ui.patient.omx_candidate_variants'), count: variants.length, render: () => candidateVariantTable(variants, query, tx('ui.patient.omx_candidate_variants')) },
      ]} />
  );
}

function NewbornView({ entry, query }) {
  const monogenic = readRows(entry, 'newbornScreening', ['disease', 'genes', 'hgvsc', 'zygosity']);
  const carrier = readRows(entry, 'newbornCarrier', ['disease', 'genes', 'hgvsc', 'zygosity']);
  const summary = readRows(entry, 'newbornSummary', ['content', 'count']);
  const variants = readRows(entry, 'newbornVariants', ['genes', 'hgvsc', 'zygosity', 'acmg_classification']);
  const monogenicFindings = screeningFindings(monogenic);
  const carrierFindings = screeningFindings(carrier);
  const summaryValue = (pattern) => summary.find((row) => pattern.test(String(row.content || '')))?.count;
  const runFacts = summary.filter((row) => !isSampleIdRow(row));
  return (
    <OmicsView record={entry} about={tx('ui.patient.omx_newborn_about')}
      facts={[
        { label: tx('ui.patient.omx_newborn_monogenic'), value: monogenicFindings.length, tone: monogenicFindings.length ? 'bad' : 'good' },
        { label: tx('ui.patient.omx_newborn_carrier'), value: carrierFindings.length, tone: carrierFindings.length ? 'warn' : 'good' },
        { label: tx('ui.patient.omx_newborn_plp'), value: summaryValue(/P\/LP/i) },
        { label: tx('ui.patient.omx_newborn_filtered'), value: summaryValue(/filtered/i) },
      ]}
      tabs={[
        { value: 'summary', label: tx('ui.patient.omx_tab_summary'), render: (go) => (
          <div className="xo-omx-summary">
            <OmicsBlock title={tx('ui.patient.omx_newborn_monogenic')} detail={tx('ui.patient.omx_newborn_monogenic_detail')}>
              <ScreeningResult findings={monogenicFindings} messages={screeningMessages(monogenic)} tone="bad" empty={tx('ui.patient.omx_newborn_monogenic_none')} emptyDetail={tx('ui.patient.omx_newborn_monogenic_none_detail')} />
            </OmicsBlock>
            <OmicsBlock title={tx('ui.patient.omx_newborn_carrier')} detail={tx('ui.patient.omx_newborn_carrier_detail')}
              action={<SeeAll label={tx('ui.patient.omx_all_candidates', { count: NUMBER.format(variants.length) })} onClick={() => go('variants')} />}>
              <ScreeningResult findings={carrierFindings} messages={screeningMessages(carrier)} tone="warn" empty={tx('ui.patient.omx_newborn_carrier_none')} />
            </OmicsBlock>
            {runFacts.length > 0 && (
              <OmicsBlock title={tx('ui.patient.omx_newborn_run')}>
                <FactGrid items={runFacts.map((row) => ({ label: row.content, value: row.count }))} />
              </OmicsBlock>
            )}
          </div>
        ) },
        { value: 'variants', label: tx('ui.patient.omx_candidate_variants'), count: variants.length, render: () => candidateVariantTable(variants, query, tx('ui.patient.omx_candidate_variants')) },
      ]} />
  );
}

// ------------------------------------------------ polygenic scores

// The polygenic_risk_scores report's colours (shared/layouts/polygenic-risk-scores
// ui.js and summary_page.js): elevated red with "!", mildly elevated orange
// with "!", typical a check, insufficient data grey; tier 1 ("critical") red,
// tier 2 ("notable") navy, whichever way the score points; disease similarity
// strongest or strong red, moderate orange, weak navy.
const RISK_LEVEL = Object.freeze({
  ELEVATED: { key: 'ui.patient.omx_level_elevated', tone: 'bad' },
  MILDLY_ELEVATED: { key: 'ui.patient.omx_level_mildly_elevated', tone: 'warn' },
  TYPICAL: { key: 'ui.patient.omx_level_typical', tone: 'good' },
  INSUFFICIENT_DATA: { key: 'ui.patient.omx_level_insufficient', tone: 'dot' },
});

const TIER_WORDS = Object.freeze({ T1: 'ui.patient.omx_tier_1', T2: 'ui.patient.omx_tier_2', T3: 'ui.patient.omx_tier_3' });

function tierTone(row) {
  if (row.tier === 'T1') return 'bad';
  if (row.tier === 'T2') return 'info';
  return 'dot';
}

// A z-score on a fixed -3..3 axis with the shaded band inside ±1.5.
function ZBar({ value, band = 1.5 }) {
  const z = numeric(value);
  if (z === null) return <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span>;
  const position = (at) => ((Math.max(-3, Math.min(3, at)) + 3) / 6) * 100;
  return (
    <span className={`xo-omx-zbar${Math.abs(z) > band ? ' is-out' : ''}`} title={zText(z)}>
      <i style={{ left: `${position(-band)}%`, width: `${position(band) - position(-band)}%` }} />
      <b style={{ left: `${position(z)}%` }} />
    </span>
  );
}

// A clinical domain code in words: Gastro_Hepatology reads Gastro / Hepatology.
function domainWords(value) {
  if (!hasValue(value)) return tx('ui.patient.omx_unclassified');
  return String(value).split('_').filter(Boolean).map((part) => human(part)).join(' / ');
}

// How strongly the genetic profile resembles a condition's: strong needs a
// look first, moderate a look, weak none.
function similarityTone(row) {
  return { STRONGEST: 'bad', STRONG: 'bad', MODERATE: 'warn' }[String(row.strength || '').toUpperCase()] || 'info';
}

// The score groups behind one condition in the similarity ranking, each with
// its domain, lead trait, this person's z-score and the traits it combines.
function SimilarityDrivers({ rank, modules, members }) {
  const drivers = modules.filter((module) => Number(module.disease_rank) === Number(rank));
  if (drivers.length === 0) return <p className="xo-omx-none">{tx('ui.patient.omx_no_more_detail')}</p>;
  return (
    <ul className="xo-omx-drivers">
      {drivers.map((module) => {
        const traits = [...new Set(members
          .filter((member) => Number(member.disease_rank) === Number(rank) && Number(member.module_id) === Number(module.module_id))
          .map((member) => member.trait).filter(hasValue))];
        return (
          <li key={module.module_id}>
            <strong>{tx('ui.patient.omx_module_n', { id: module.module_id })}</strong>
            <span>{[hasValue(module.primary_domain) ? domainWords(module.primary_domain) : null, module.primary_trait].filter(hasValue).join(' · ')}</span>
            <span className="xo-omx-driver-z">{tx('ui.patient.omx_z_value', { value: zText(module.patient_z) || tx('ui.patient.omx_not_given') })}</span>
            <small>{traits.slice(0, 8).join(', ')}{traits.length > 8 ? ', …' : ''}</small>
          </li>
        );
      })}
    </ul>
  );
}

function GpgsView({ entry, query }) {
  const patient = oneRow(entry, 'gpgsPatient', ['pgs_scored', 'predicted_population', 'tier1_count']);
  const domains = readRows(entry, 'gpgsDomains', ['domain', 'n_pgs', 'mean_z', 'risk_level']);
  const findings = readRows(entry, 'gpgsFindings', ['pgs_id', 'trait', 'z_score', 'tier', 'direction', 'domain']);
  const rankings = readRows(entry, 'gpgsDiseaseRankings', ['rank', 'disease', 'probability', 'strength']);
  const modules = readRows(entry, 'gpgsDrivingModules', ['disease_rank', 'module_id', 'patient_z']);
  const modulePgs = readRows(entry, 'gpgsDrivingModulePgs', ['disease_rank', 'module_id', 'pgs_id', 'trait']);
  const monitoring = readRows(entry, 'gpgsMonitoring', ['domain', 'recommendation']);
  const landscapes = readRows(entry, 'gpgsLandscapes', ['landscape', 'n_pgs']);
  const subsections = readRows(entry, 'gpgsSubsections', ['landscape', 'subsection', 'n_pgs', 'mean_z']);
  const elevated = domains.filter((row) => RISK_LEVEL[row.risk_level]?.tone === 'bad' || RISK_LEVEL[row.risk_level]?.tone === 'warn');
  const tier1 = findings.filter((row) => row.tier === 'T1').sort((left, right) => Math.abs(Number(right.z_score)) - Math.abs(Number(left.z_score)));
  const ancestry = [
    ['EUR', patient.ancestry_rf_p_eur], ['AFR', patient.ancestry_rf_p_afr], ['AMR', patient.ancestry_rf_p_amr],
    ['CSA', patient.ancestry_rf_p_csa], ['EAS', patient.ancestry_rf_p_eas], ['MID', patient.ancestry_rf_p_mid],
  ].filter(([, value]) => numeric(value) !== null).map(([label, value]) => ({ label, value: Number(value) * 100 }));
  const domainNames = [...new Set(findings.map((row) => row.domain).filter(hasValue))].sort(compareValues);
  const thresholds = { t1: formatNumber(patient.tier1_threshold, 1), t2: formatNumber(patient.tier2_threshold, 1) };
  return (
    <OmicsView record={entry} about={tx('ui.patient.omx_gpgs_about', { count: NUMBER.format(numeric(patient.pgs_scored) || findings.length) })}
      facts={[
        { label: tx('ui.patient.elevated_domains'), value: elevated.length, tone: elevated.length ? 'warn' : 'good', detail: elevated.map((row) => domainWords(row.domain)).join(', ') || null },
        { label: tx('ui.patient.omx_tier_1_findings'), value: tier1.length, tone: tier1.length ? 'warn' : 'good' },
        { label: tx('ui.patient.pgs_scored'), value: numeric(patient.pgs_scored) },
        { label: tx('ui.patient.reference_population'), value: patient.predicted_population },
      ]}
      tabs={[
        { value: 'summary', label: tx('ui.patient.omx_tab_summary'), render: (go) => (
          <div className="xo-omx-summary">
            <OmicsBlock title={tx('ui.patient.clinical_domains')} detail={tx('ui.patient.omx_gpgs_domains_detail')}>
              <ul className="xo-omx-domains">
                {/* The report's order: most tier 1 scores first, then most tier 2. */}
                {[...filterRows(domains, query)].sort((left, right) => (numeric(right.tier1_count) || 0) - (numeric(left.tier1_count) || 0)
                  || (numeric(right.tier2_count) || 0) - (numeric(left.tier2_count) || 0)).map((row) => {
                  const level = RISK_LEVEL[row.risk_level];
                  return (
                    <li key={row.domain}>
                      <span className="xo-omx-cell-mark"><OmicsMark tone={level ? level.tone : 'info'} /><strong>{domainWords(row.domain)}</strong></span>
                      <ZBar value={row.mean_z} />
                      <span className="xo-omx-domain-z">{zText(row.mean_z) || tx('ui.patient.omx_not_given')}</span>
                      <span className={level ? `is-${level.tone}` : 'xo-omx-none'}>{level ? tx(level.key) : tx('ui.patient.omx_unclassified')}</span>
                      <small>{tx('ui.patient.omx_domain_scores', { count: NUMBER.format(numeric(row.n_pgs) || 0), tier1: NUMBER.format(numeric(row.tier1_count) || 0) })}</small>
                    </li>
                  );
                })}
              </ul>
            </OmicsBlock>
            <OmicsBlock title={tx('ui.patient.omx_gpgs_top')} detail={tx('ui.patient.omx_gpgs_top_detail', thresholds)}
              action={<SeeAll label={tx('ui.patient.omx_all_scores', { count: NUMBER.format(findings.length) })} onClick={() => go('scores')} />}>
              <FindingList limit={6} onMore={() => go('scores')} moreLabel={tx('ui.patient.omx_see_all_findings', { count: NUMBER.format(tier1.length) })}
                empty={tx('ui.patient.omx_gpgs_top_none')}
                items={tier1.map((row) => (
                  <Finding key={row.sourceRow} tone={tierTone(row)} title={row.trait}
                    chips={<><Chip tone="bad">{tx('ui.patient.omx_tier_1')}</Chip><Chip>{tx(row.direction === 'elevated' ? 'ui.patient.omx_higher' : 'ui.patient.omx_lower')}</Chip>{hasValue(row.quality_label) && <Chip>{human(row.quality_label)}</Chip>}</>}
                    lines={[domainWords(row.domain), tx('ui.patient.omx_z_value', { value: zText(row.z_score) }), row.pgs_id]}
                    note={row.plain_language}
                    evidence={[
                      { label: tx('ui.patient.method'), value: row.method },
                      { label: tx('ui.patient.variants'), value: numeric(row.variants) === null ? null : NUMBER.format(Number(row.variants)) },
                      { label: tx('ui.patient.convergence'), value: row.convergence },
                      { label: tx('ui.patient.omx_source'), value: row.catalog_url ? <a href={row.catalog_url} target="_blank" rel="noreferrer">{tx('ui.patient.omx_pgs_catalog')}<ExternalLink aria-hidden="true" /></a> : null },
                    ]} />
                ))} />
            </OmicsBlock>
            <div className="xo-omx-split">
              <OmicsBlock title={tx('ui.patient.disease_similarity')} detail={patient.disease_similarity_framing || tx('ui.patient.omx_similarity_detail')}
                action={<SeeAll label={tx('ui.patient.omx_see_ranking')} onClick={() => go('similarity')} />}>
                <ol className="xo-omx-ranking">
                  {rankings.slice(0, 3).map((row) => (
                    <li key={row.rank}><b>{row.rank}</b><strong>{row.disease_name || row.disease}</strong><span>{percent(Number(row.probability), 1)}</span><Status value={row.strength} tone={similarityTone(row)} /></li>
                  ))}
                </ol>
              </OmicsBlock>
              <OmicsBlock title={tx('ui.patient.monitoring')} detail={tx('ui.patient.omx_monitoring_detail')}>
                {monitoring.length === 0 ? <EmptyState>{tx('ui.patient.omx_monitoring_none')}</EmptyState> : (
                  <ul className="xo-omx-list">{monitoring.map((row, index) => (
                    // The pipeline writes each check as display text (LDL cholesterol, eGFR); it is shown as written.
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={index}><strong>{row.recommendation}</strong><small>{domainWords(row.domain)}</small></li>
                  ))}</ul>
                )}
              </OmicsBlock>
            </div>
          </div>
        ) },
        { value: 'scores', label: tx('ui.patient.omx_tab_scores'), count: findings.length, render: () => (
          <OmicsTable rows={findings} query={query} label={tx('ui.patient.omx_tab_scores')} rowKey={(row) => row.sourceRow}
            searchLabel={tx('ui.patient.omx_search_scores')} initialSort={{ key: 'z_score', direction: 'desc' }} empty={tx('ui.patient.omx_gpgs_top_none')}
            detail={(row) => <Evidence items={[
              { label: tx('ui.patient.meaning'), value: row.plain_language },
              { label: tx('ui.patient.method'), value: row.method },
              { label: tx('ui.patient.variants'), value: numeric(row.variants) === null ? null : NUMBER.format(Number(row.variants)) },
              { label: tx('ui.patient.convergence'), value: row.convergence },
              { label: tx('ui.patient.importance'), value: numeric(row.importance) === null ? null : formatNumber(row.importance, 2) },
              { label: tx('ui.patient.omx_source'), value: row.catalog_url ? <a href={row.catalog_url} target="_blank" rel="noreferrer">{tx('ui.patient.omx_pgs_catalog')}<ExternalLink aria-hidden="true" /></a> : null },
            ]} />}
            columns={[
              { key: 'tier', label: tx('ui.patient.tier'), render: (row) => <span className="xo-omx-cell-mark"><OmicsMark tone={tierTone(row)} />{TIER_WORDS[row.tier] ? tx(TIER_WORDS[row.tier]) : row.tier}</span> },
              { key: 'trait', label: tx('ui.patient.trait'), render: (row) => <strong>{row.trait}</strong> },
              { key: 'domain', label: tx('ui.patient.domain'), text: (row) => domainWords(row.domain), render: (row) => domainWords(row.domain) },
              { key: 'z_score', label: tx('ui.patient.z_score'), align: 'end', firstDirection: 'desc', sort: (row) => (numeric(row.z_score) === null ? null : Math.abs(Number(row.z_score))),
                render: (row) => <span className="xo-omx-zcell"><ZBar value={row.z_score} band={1.3} /><span>{zText(row.z_score)}</span></span> },
              { key: 'direction', label: tx('ui.patient.direction'), render: (row) => tx(row.direction === 'elevated' ? 'ui.patient.omx_higher' : 'ui.patient.omx_lower') },
              { key: 'quality_label', label: tx('ui.patient.quality'), render: (row) => (hasValue(row.quality_label) ? human(row.quality_label) : <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span>) },
              { key: 'pgs_id', label: 'PGS' },
            ]}
            filters={[
              { key: 'tier', label: tx('ui.patient.tier'), initial: 'T1', options: [
                { value: 'T1', label: tx('ui.patient.omx_tier_1') }, { value: 'T12', label: tx('ui.patient.omx_tier_1_2') },
                { value: 'T2', label: tx('ui.patient.omx_tier_2') }, { value: 'T3', label: tx('ui.patient.omx_tier_3') }, { value: 'all', label: tx('ui.patient.omx_all') }],
                test: (row, value) => (value === 'T12' ? row.tier === 'T1' || row.tier === 'T2' : row.tier === value) },
              { key: 'direction', label: tx('ui.patient.direction'), options: [{ value: 'all', label: tx('ui.patient.omx_all') }, { value: 'elevated', label: tx('ui.patient.omx_higher') }, { value: 'reduced', label: tx('ui.patient.omx_lower') }],
                test: (row, value) => row.direction === value },
              { key: 'domain', label: tx('ui.patient.domain'), options: [{ value: 'all', label: tx('ui.patient.omx_all') }, ...domainNames.map((value) => ({ value, label: domainWords(value) }))],
                test: (row, value) => row.domain === value },
            ]} />
        ) },
        { value: 'similarity', label: tx('ui.patient.disease_similarity'), count: rankings.length, render: () => (
          <div className="xo-omx-summary">
            <p className="xo-omx-lead">{patient.disease_similarity_framing || tx('ui.patient.omx_similarity_detail')}</p>
            <OmicsTable rows={rankings} query={query} label={tx('ui.patient.disease_similarity')} rowKey={(row) => row.rank}
              initialSort={{ key: 'rank', direction: 'asc' }} searchLabel={tx('ui.patient.omx_search_conditions')} empty={tx('ui.patient.none_recorded')}
              detail={(row) => <SimilarityDrivers rank={row.rank} modules={modules} members={modulePgs} />}
              columns={[
                { key: 'rank', label: tx('ui.patient.rank'), sort: (row) => numeric(row.rank), render: (row) => <b className="xo-omx-rank-no">{row.rank}</b> },
                { key: 'disease', label: tx('ui.patient.omx_condition'), text: (row) => row.disease_name || row.disease, render: (row) => <strong>{row.disease_name || row.disease}</strong> },
                { key: 'probability', label: tx('ui.patient.probability'), firstDirection: 'desc', sort: (row) => numeric(row.probability),
                  render: (row) => <span className="xo-omx-meter"><i><b style={{ width: `${Math.max(2, Math.min(100, (numeric(row.probability) || 0) * 100))}%` }} /></i>{percent(Number(row.probability), 1)}</span> },
                { key: 'strength', label: tx('ui.patient.strength'), sort: (row) => ({ STRONG: 0, MODERATE: 1, WEAK: 2 }[String(row.strength).toUpperCase()] ?? 3),
                  render: (row) => <span className="xo-omx-cell-mark"><OmicsMark tone={similarityTone(row)} />{human(row.strength)}</span> },
                { key: 'groups', label: tx('ui.patient.omx_score_groups'), align: 'end', text: () => '',
                  sort: (row) => modules.filter((module) => Number(module.disease_rank) === Number(row.rank)).length,
                  render: (row) => NUMBER.format(modules.filter((module) => Number(module.disease_rank) === Number(row.rank)).length) },
              ]} />
          </div>
        ) },
        { value: 'landscapes', label: tx('ui.patient.phenotype_landscape'), count: landscapes.length, render: () => (
          <div className="xo-omx-summary">
            {landscapes.map((landscape) => (
              <OmicsBlock key={landscape.landscape} title={landscape.landscape}
                detail={tx('ui.patient.omx_landscape_detail', { count: NUMBER.format(numeric(landscape.n_pgs) || 0), tier1: NUMBER.format(numeric(landscape.tier1_count) || 0), tier2: NUMBER.format(numeric(landscape.tier2_count) || 0) })}>
                <ul className="xo-omx-domains is-compact">
                  {subsections.filter((row) => row.landscape === landscape.landscape).map((row) => (
                    <li key={row.subsection}>
                      <strong>{row.subsection}</strong>
                      <ZBar value={row.mean_z} />
                      <span className="xo-omx-domain-z">{zText(row.mean_z) || tx('ui.patient.omx_not_given')}</span>
                      <small>{tx('ui.patient.omx_subsection_scores', { count: NUMBER.format(numeric(row.n_pgs) || 0), elevated: NUMBER.format(numeric(row.n_elevated) || 0) })}</small>
                    </li>
                  ))}
                </ul>
              </OmicsBlock>
            ))}
          </div>
        ) },
        { value: 'ancestry', label: tx('ui.patient.ancestry'), render: () => (
          <div className="xo-omx-summary">
            {ancestry.length >= 2 ? <Composition title={tx('ui.patient.ancestry')} detail={tx('ui.patient.ancestry_detail')} items={ancestry} unit="%" /> : <EmptyState>{tx('ui.patient.omx_ancestry_none')}</EmptyState>}
            <OmicsBlock title={tx('ui.patient.omx_score_basis')}>
              <FactGrid items={[
                { label: tx('ui.patient.reference_population'), value: patient.predicted_population },
                { label: tx('ui.patient.omx_scores_after_filter'), value: numeric(patient.pgs_after_filter) === null ? null : NUMBER.format(Number(patient.pgs_after_filter)) },
                { label: tx('ui.patient.omx_modules_available'), value: numeric(patient.modules_available) === null ? null : NUMBER.format(Number(patient.modules_available)) },
                { label: tx('ui.patient.reliability'), value: numeric(patient.mean_reliability) === null ? null : formatNumber(patient.mean_reliability, 2) },
                { label: tx('ui.patient.omx_tier_thresholds'), value: tx('ui.patient.omx_tier_threshold_values', thresholds) },
              ]} />
            </OmicsBlock>
          </div>
        ) },
      ]} />
  );
}

// ------------------------------------------------ gut and oral microbiome
//
// Read the way the gut_health and oral_health PDF reports read them
// (PLReportGeneration shared/layouts/metagenomics: DiseaseAnalysis.js,
// MicrobiomeAnalysis.js, summary.js, data.js):
// - a condition score runs 0 to 1 and a LOW score is the disease-associated
//   end. Gut bands: above 0.8 high, above 0.55 normal-high, above 0.3
//   normal-low, else low. Oral: from 0.8 high, from 0.4 normal, else low.
//   Only the low band is flagged, and conditions are listed lowest first.
// - a diversity index inside its reference range is good, outside it on
//   either side improvable. F/B ratio is stored multiplied by 100.
// - a taxon is increased above the population's Q3, decreased below its Q1
//   and normal between them, which is the pipeline's own flag.

function taxonWords(reference) {
  if (!reference) return null;
  if (hasValue(reference.species)) return reference.species;
  return String(reference.taxonName || '').replace(/^[a-z]__/, '').replace(/_/g, ' ');
}

// A taxon's level against the population's Q1 to Q3.
function populationPlace(value, reference) {
  const at = numeric(value);
  const low = numeric(reference?.lowerAbundance);
  const high = numeric(reference?.upperAbundance);
  if (at === null || low === null || high === null || high < low) return null;
  if (at < low) return 'decreased';
  if (at > high) return 'increased';
  return 'normal';
}

// The report's level words and colours: normal in green, increased and decreased in orange.
const LEVEL_WORDS = Object.freeze({
  decreased: 'ui.patient.omx_below_population', increased: 'ui.patient.omx_above_population', normal: 'ui.patient.omx_within_population',
});

function levelOf(flag) {
  const value = String(flag || '').toLowerCase();
  return value === 'increased' || value === 'decreased' ? value : 'normal';
}

function Level({ level }) {
  if (!level) return <span className="xo-omx-none">{tx('ui.patient.omx_no_population_range')}</span>;
  return <span className="xo-omx-cell-mark"><OmicsMark tone={level === 'normal' ? 'good' : 'warn'} />{tx(LEVEL_WORDS[level])}</span>;
}

// The population's Q1 to Q3 written as the report writes it.
function populationRange(reference) {
  const low = numeric(reference?.lowerAbundance);
  const high = numeric(reference?.upperAbundance);
  if (low === null || high === null) return null;
  if (low === 0 && high === 0) return '<0.0001%';
  return tx('ui.patient.range_span', { low: `${low.toFixed(3)}%`, high: `${high.toFixed(3)}%` });
}

function bandTone(band) {
  return (BAND_WORDS[band] || [null, 'info'])[1];
}

// Where the report's band edges fall on the 0 to 100 score scale.
const MB_BAND_EDGES = Object.freeze({ gut: [0.3, 0.55, 0.8], oral: [0.4, 0.8] });

// The report's scales: the reference band with room either side, widened
// when the value itself falls outside that window (summary.js scalePlacement).
function scalePlacement(value, min, max) {
  const span = max - min || 1;
  let low = Math.max(0, min - span * 0.55);
  let high = max + span * 0.55;
  if (value < low) low = Math.max(0, value - span * 0.15);
  if (value > high) high = value + span * 0.15;
  const range = high - low || 1;
  const at = (number) => Math.max(0, Math.min(1, (number - low) / range)) * 100;
  return { from: at(min), to: at(max), position: at(value) };
}

function diversityIndices(diversity) {
  return MICROBIOME_RANGES.map((rule) => {
    const raw = numeric(diversity[rule.field]);
    if (raw === null) return { ...rule, value: null, state: null };
    const value = raw / rule.divisor;
    return { ...rule, value, state: value < rule.low ? 'decreased' : value > rule.high ? 'increased' : 'normal' };
  });
}

// One value on the report's scale: the shaded band is the reference range, the dot this sample.
function ScaleRow({ label, value, valueText, low, high, state, note, italic = false }) {
  const placement = numeric(value) === null ? null : scalePlacement(Number(value), low, high);
  return (
    <li className="xo-omx-scale-row">
      <span className="xo-omx-cell-mark"><OmicsMark tone={state === null ? 'dot' : state === 'normal' ? 'good' : 'warn'} />
        <strong className={italic ? 'is-italic' : undefined}>{label}</strong></span>
      <b>{valueText}</b>
      {placement ? (
        <span className="xo-omx-scale" aria-hidden="true">
          <i style={{ left: `${placement.from}%`, width: `${Math.max(1, placement.to - placement.from)}%` }} />
          <b className={state === 'normal' ? '' : 'is-out'} style={{ left: `${placement.position}%` }} />
        </span>
      ) : <span />}
      <small>{note}</small>
    </li>
  );
}

// A condition's score as the report draws it: 0 to 100, with its band edges marked.
function ScoreBar({ score, mode }) {
  const at = Math.max(0, Math.min(1, Number(score))) * 100;
  return (
    <span className="xo-omx-score" aria-hidden="true">
      <i style={{ width: `${Math.max(2, at)}%` }} />
      {MB_BAND_EDGES[mode].map((edge) => <em key={edge} style={{ left: `${edge * 100}%` }} />)}
      <b style={{ left: `${at}%` }} />
    </span>
  );
}

function conditionScore(row) {
  return Math.round(Number(row.your_score) * 100);
}

// A condition in full, with the report's sections: its analysis and
// suggestion for the band the score falls in, its definition, and the
// associated and protective taxa with their levels.
function MicrobiomeCondition({ row }) {
  const result = row.reference.results?.[row.band];
  const recommendation = row.reference.recommendations?.[row.band];
  const tone = bandTone(row.band);
  const taxa = (roles) => row.drivers.filter((driver) => roles.test(String(driver.role || '')))
    .map((driver) => `${driver.name} (${tx(LEVEL_WORDS[levelOf(driver.flag)])})`).join(', ');
  return (
    <Finding tone={tone} title={row.reference.title || row.reference.name}
      chips={<><Chip tone={tone === 'warn' ? 'warn' : 'muted'}>{tx('ui.patient.omx_prk_score_value', { score: conditionScore(row) })}</Chip><BandStatus band={row.band} /></>}
      lines={[row.reference.category]}
      facts={[
        { label: tx('ui.patient.omx_mb_analysis'), value: result },
        { label: tx('ui.patient.omx_mb_suggestion'), value: recommendation },
      ]}
      evidence={[
        { label: tx('ui.patient.omx_mb_definition'), value: row.reference.definition },
        { label: tx('ui.patient.omx_mb_associated'), value: taxa(/^associated/i) || null },
        { label: tx('ui.patient.omx_mb_protective'), value: taxa(/^protective/i) || null },
      ]} />
  );
}

// The report's summary list of condition scores: the lowest first, the low band flagged.
function ConditionScores({ rows, mode }) {
  if (rows.length === 0) return <EmptyState>{tx('ui.patient.omx_microbiome_conditions_none')}</EmptyState>;
  return (
    <ul className="xo-omx-mbscores">
      {rows.map((row) => (
        <li key={row.disease_id}>
          <span className="xo-omx-cell-mark"><OmicsMark tone={bandTone(row.band)} />
            <span><strong>{row.reference.title || row.reference.name}</strong>{hasValue(row.reference.category) && <small>{row.reference.category}</small>}</span></span>
          <ScoreBar score={row.your_score} mode={mode} />
          <b>{conditionScore(row)}</b>
          <BandStatus band={row.band} />
        </li>
      ))}
    </ul>
  );
}

// The report's groups: for gut its functional groups from the bacteria
// details, for oral its community signals from the condition taxa and the
// dominant phyla. A group counts the taxa outside the population range.
function microbiomeGroups(entry, gut, details, driverRows, topRows) {
  const named = (row) => ({ ...row, name: resolvedTaxon(entry, row.bacteria_id), level: levelOf(row.flag) });
  if (gut) {
    return [['butyrate', 'ui.patient.omx_mb_group_butyrate'], ['probiotic', 'ui.patient.omx_mb_group_probiotic'],
      ['pathogen', 'ui.patient.omx_mb_group_pathogen'], ['candida', 'ui.patient.omx_mb_group_candida']]
      .map(([type, key]) => ({ key: type, title: tx(key), rows: details.filter((row) => String(row.type).toLowerCase() === type).map(named) }))
      .filter((group) => group.rows.length > 0);
  }
  const byTaxon = (roles) => {
    const map = new Map();
    driverRows.filter((row) => roles.test(String(row.role || ''))).forEach((row) => map.set(Number(row.bacteria_id), named(row)));
    return [...map.values()];
  };
  return [
    { key: 'protective', title: tx('ui.patient.omx_mb_group_protective'), rows: byTaxon(/^protective/i) },
    { key: 'associated', title: tx('ui.patient.omx_mb_group_associated'), rows: byTaxon(/^associated/i) },
    { key: 'phyla', title: tx('ui.patient.omx_mb_group_phyla'), rows: topRows.map(named) },
  ].filter((group) => group.rows.length > 0);
}

function groupCounts(rows) {
  const counts = { normal: 0, increased: 0, decreased: 0 };
  rows.forEach((row) => { counts[row.level] += 1; });
  return counts;
}

function groupLine(rows) {
  const counts = groupCounts(rows);
  return [
    counted('ui.patient.omx_group_flagged', counts.increased + counts.decreased, { total: NUMBER.format(rows.length) }),
    [tx('ui.patient.omx_mb_within', { count: NUMBER.format(counts.normal) }),
      counts.increased ? tx('ui.patient.omx_mb_increased', { count: NUMBER.format(counts.increased) }) : null,
      counts.decreased ? tx('ui.patient.omx_mb_decreased', { count: NUMBER.format(counts.decreased) }) : null].filter(Boolean).join(', '),
  ].join(' · ');
}

function MicrobiomeView({ entry, query, mode }) {
  const gut = mode === 'gut';
  const diversity = oneRow(entry, gut ? 'gutDiversity' : 'oralDiversity', ['richness', 'shannon_diversity', 'pielou_evenness', 'fb_ratio']);
  const topRows = readRows(entry, gut ? 'gutTopPhylum' : 'oralTopPhylum', ['bacteria_id', 'patient_value', 'flag']);
  const profiled = readRows(entry, gut ? 'gutProfiled' : 'oralProfiled', ['bacteria_id', 'relative_abundance']);
  const driverRows = readRows(entry, gut ? 'gutDiseaseRows' : 'oralDiseaseRows', ['disease_id', 'bacteria_id', 'role', 'flag']);
  const summaries = readRows(entry, gut ? 'gutDiseaseSummary' : 'oralDiseaseSummary', ['disease_id', 'your_score']);
  const details = gut ? readRows(entry, 'gutDetails', ['bacteria_id', 'patient_value', 'flag', 'type']) : [];
  // Lowest score first: the report's order, the disease-associated end at the top.
  const diseases = summaries.map((row) => {
    const reference = resolvedDisease(entry, row.disease_id);
    return {
      ...row, reference, band: microbiomeBand(mode, row.your_score),
      drivers: driverRows.filter((driver) => Number(driver.disease_id) === Number(row.disease_id))
        .map((driver) => ({ ...driver, name: resolvedTaxon(entry, driver.bacteria_id) })),
    };
  }).sort((left, right) => Number(left.your_score) - Number(right.your_score));
  const low = diseases.filter((row) => row.band === 'low');
  const taxa = profiled.map((row) => {
    const reference = entry.references?.bacteria?.[String(row.bacteria_id)];
    if (!reference) throw new Error(`Taxon ${row.bacteria_id} has no resolved bacteria reference`);
    return { ...row, name: taxonWords(reference), phylum: reference.phylum, genus: reference.genus, place: populationPlace(row.relative_abundance, reference), reference };
  });
  const outside = taxa.filter((row) => row.place === 'increased' || row.place === 'decreased');
  const phyla = topRows.slice(0, 5).map((row) => {
    const reference = entry.references?.bacteria?.[String(row.bacteria_id)];
    return { ...row, name: resolvedTaxon(entry, row.bacteria_id), level: levelOf(row.flag), reference };
  }).sort((left, right) => Number(right.patient_value) - Number(left.patient_value));
  const coverage = phyla.reduce((sum, row) => sum + (numeric(row.patient_value) || 0), 0);
  const indices = diversityIndices(diversity);
  const index = (field) => indices.find((item) => item.field === field);
  const indexWord = (item) => (item.state === null ? tx('ui.patient.omx_not_given')
    : item.state === 'normal' ? tx(gut ? 'ui.patient.omx_mb_good_gut' : 'ui.patient.omx_mb_good_oral') : tx('ui.patient.omx_mb_improvable'));
  const indexFact = (item) => ({
    label: tx(item.labelKey), value: item.value === null ? null : item.value.toFixed(item.digits),
    tone: item.state === null ? 'info' : item.state === 'normal' ? 'good' : 'warn',
    detail: `${indexWord(item)} · ${tx('ui.patient.omx_typical_range', { low: item.low, high: item.high })}`,
  });
  const groups = microbiomeGroups(entry, gut, details, driverRows, topRows);
  const bandOptions = gut ? ['low', 'normallow', 'normalhigh', 'high'] : ['low', 'normal', 'high'];
  return (
    <OmicsView record={entry} about={tx(gut ? 'ui.patient.omx_gut_about' : 'ui.patient.omx_oral_about')}
      facts={[
        indexFact(index('shannon_diversity')),
        indexFact(index('richness')),
        { label: tx('ui.patient.omx_conditions_attention'), value: low.length, tone: low.length ? 'warn' : 'good',
          detail: low.length ? low.slice(0, 3).map((row) => row.reference.title || row.reference.name).join(', ') : null },
        { label: tx('ui.patient.omx_taxa_outside'), value: tx('ui.patient.omx_of_total', { count: NUMBER.format(outside.length), total: NUMBER.format(taxa.length) }) },
      ]}
      tabs={[
        { value: 'summary', label: tx('ui.patient.omx_tab_summary'), render: (go) => (
          <div className="xo-omx-summary">
            <OmicsBlock title={tx('ui.patient.omx_mb_conditions_title')} detail={tx('ui.patient.omx_mb_conditions_caption')}
              action={<SeeAll label={tx('ui.patient.omx_all_conditions', { count: NUMBER.format(diseases.length) })} onClick={() => go('conditions')} />}>
              <ConditionScores rows={diseases.slice(0, 6)} mode={mode} />
            </OmicsBlock>
            <div className="xo-omx-split">
              <OmicsBlock title={tx('ui.patient.omx_mb_diversity_title')} detail={tx('ui.patient.omx_mb_scale_note')}>
                <ul className="xo-omx-scales">
                  {indices.map((item) => (
                    <ScaleRow key={item.field} label={tx(item.labelKey)} value={item.value} state={item.state} low={item.low} high={item.high}
                      valueText={item.value === null ? tx('ui.patient.omx_not_given') : item.value.toFixed(item.digits)}
                      note={`${indexWord(item)} · ${tx('ui.patient.omx_typical_range', { low: item.low, high: item.high })}`} />
                  ))}
                </ul>
              </OmicsBlock>
              <OmicsBlock title={tx('ui.patient.omx_mb_composition_title')}
                detail={tx('ui.patient.omx_mb_composition_coverage', { count: NUMBER.format(phyla.length), percent: coverage.toFixed(1) })}>
                <ul className="xo-omx-scales">
                  {phyla.map((row) => {
                    const lowEdge = numeric(row.reference?.lowerAbundance);
                    const highEdge = numeric(row.reference?.upperAbundance);
                    return (
                      <ScaleRow key={row.bacteria_id} label={row.name} value={lowEdge === null || highEdge === null || highEdge <= lowEdge ? null : row.patient_value}
                        state={row.level} low={lowEdge ?? 0} high={highEdge ?? 0}
                        valueText={`${formatNumber(row.patient_value, 1)}%`}
                        note={[tx(LEVEL_WORDS[row.level]), populationRange(row.reference)].filter(Boolean).join(' · ')} />
                    );
                  })}
                </ul>
              </OmicsBlock>
            </div>
            {groups.length > 0 && (
              <OmicsBlock title={tx(gut ? 'ui.patient.omx_mb_groups_gut' : 'ui.patient.omx_mb_groups_oral')}
                detail={tx('ui.patient.omx_mb_groups_caption', { count: NUMBER.format(groups.reduce((sum, group) => sum + group.rows.length, 0)) })}
                action={<SeeAll label={tx('ui.patient.omx_see_groups')} onClick={() => go('groups')} />}>
                <ul className="xo-omx-groups">
                  {groups.map((group) => {
                    const counts = groupCounts(group.rows);
                    const flagged = counts.increased + counts.decreased;
                    return (
                      <li key={group.key}>
                        <span className="xo-omx-cell-mark"><OmicsMark tone={flagged ? 'warn' : 'good'} /><strong>{group.title}</strong></span>
                        <b>{NUMBER.format(flagged)}</b>
                        <small>{groupLine(group.rows)}</small>
                      </li>
                    );
                  })}
                </ul>
              </OmicsBlock>
            )}
          </div>
        ) },
        { value: 'conditions', label: tx('ui.patient.omx_tab_conditions'), count: diseases.length, render: () => (
          <OmicsTable rows={diseases} query={query} label={tx('ui.patient.omx_tab_conditions')} rowKey={(row) => row.disease_id}
            initialSort={{ key: 'your_score', direction: 'asc' }} searchLabel={tx('ui.patient.omx_search_conditions')} empty={tx('ui.patient.omx_microbiome_conditions_none')}
            detail={(row) => <MicrobiomeCondition row={row} />}
            columns={[
              { key: 'band', label: tx('ui.patient.band'), sort: (row) => numeric(row.your_score), text: (row) => tx(BAND_WORDS[row.band][0]),
                render: (row) => <span className="xo-omx-cell-mark"><OmicsMark tone={bandTone(row.band)} /><BandStatus band={row.band} /></span> },
              { key: 'name', label: tx('ui.patient.omx_condition'), text: (row) => row.reference.title || row.reference.name, render: (row) => <strong>{row.reference.title || row.reference.name}</strong> },
              { key: 'category', label: tx('ui.patient.category'), text: (row) => row.reference.category, render: (row) => row.reference.category || <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span> },
              { key: 'your_score', label: tx('ui.patient.score'), sort: (row) => numeric(row.your_score),
                render: (row) => <span className="xo-omx-zcell"><ScoreBar score={row.your_score} mode={mode} /><span>{conditionScore(row)}</span></span> },
            ]}
            filters={[{ key: 'band', label: tx('ui.patient.band'), initial: 'low', options: [{ value: 'all', label: tx('ui.patient.omx_all') },
              ...bandOptions.map((band) => ({ value: band, label: tx(BAND_WORDS[band][0]) }))],
              test: (row, value) => row.band === value }]} />
        ) },
        { value: 'taxa', label: tx('ui.patient.omx_tab_taxa'), count: taxa.length, render: () => (
          <OmicsTable rows={taxa} query={query} label={tx('ui.patient.omx_tab_taxa')} rowKey={(row) => row.bacteria_id}
            initialSort={{ key: 'relative_abundance', direction: 'desc' }} searchLabel={tx('ui.patient.omx_search_taxa')} empty={tx('ui.patient.omx_no_taxa')}
            detail={(row) => <Evidence items={[
              { label: tx('ui.patient.omx_taxonomy'), value: hasValue(row.reference.fullTaxonomy) ? String(row.reference.fullTaxonomy).split('|').map((part) => part.replace(/^[a-z]__/, '').replace(/_/g, ' ')).join(' › ') : null },
              { label: tx('ui.patient.omx_population_middle'), value: populationRange(row.reference) },
              { label: tx('ui.patient.omx_population_mean'), value: numeric(row.reference.meanAbundance) === null ? null : `${formatNumber(row.reference.meanAbundance, 3)}%` },
            ]} />}
            columns={[
              { key: 'name', label: tx('ui.patient.taxon'), render: (row) => <strong className="is-italic">{row.name}</strong> },
              { key: 'phylum', label: tx('ui.patient.omx_phylum') },
              { key: 'relative_abundance', label: tx('ui.patient.relative_abundance'), align: 'end', firstDirection: 'desc', sort: (row) => numeric(row.relative_abundance), render: (row) => `${formatNumber(row.relative_abundance, 3)}%` },
              { key: 'range', label: tx('ui.patient.omx_population_middle'), align: 'end', sortable: false, text: (row) => populationRange(row.reference), render: (row) => populationRange(row.reference) || <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span> },
              { key: 'place', label: tx('ui.patient.omx_against_population'), text: (row) => (row.place ? tx(LEVEL_WORDS[row.place]) : ''),
                sort: (row) => ({ increased: 0, decreased: 1, normal: 2 }[row.place] ?? 3), render: (row) => <Level level={row.place} /> },
            ]}
            filters={[{ key: 'place', label: tx('ui.patient.omx_against_population'), initial: 'outside', options: [
              { value: 'all', label: tx('ui.patient.omx_all') }, { value: 'outside', label: tx('ui.patient.omx_outside_population') },
              { value: 'increased', label: tx('ui.patient.omx_above_population') }, { value: 'decreased', label: tx('ui.patient.omx_below_population') }],
              test: (row, value) => (value === 'outside' ? row.place === 'increased' || row.place === 'decreased' : row.place === value) }]} />
        ) },
        ...(groups.length ? [{ value: 'groups', label: tx(gut ? 'ui.patient.omx_mb_groups_gut' : 'ui.patient.omx_mb_groups_oral'), count: groups.reduce((sum, group) => sum + group.rows.length, 0), render: () => (
          <div className="xo-omx-summary">
            {groups.map((group) => (
              <OmicsBlock key={group.key} title={group.title} detail={groupLine(group.rows)}>
                <ul className="xo-omx-taxa">
                  {[...group.rows].sort((left, right) => ({ increased: 0, decreased: 1, normal: 2 }[left.level] - { increased: 0, decreased: 1, normal: 2 }[right.level])).map((row, rowIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={rowIndex} className={`is-${row.level === 'normal' ? 'good' : 'warn'}`}>
                      <em>{row.name}</em>
                      <span>{numeric(row.patient_value) === null ? '' : `${formatNumber(row.patient_value, 3)}%`}</span>
                      <Level level={row.level} />
                    </li>
                  ))}
                </ul>
              </OmicsBlock>
            ))}
          </div>
        ) }] : []),
      ]} />
  );
}

// ------------------------------------------------ vaginal microbiome
//
// Read the way the vaginal_health PDF report reads it (PLReportGeneration
// shared/layouts/vaginal-health: reportDesign.js classificationStyle,
// reportResultsOverview.js, data.js, evidence.js). A clinical section means
// the classification of its primary clinician flag: favourable or not
// observed is a check, a concern-associated pattern or a reported signal is
// danger, context dependent is a warning, anything else neutral. Sections are
// reviewed concern first, then signal, then context, then in report order. The
// balance score takes the colour of the vaginal_health_score product's
// primary flag; its band is only words.

// A band or status code that is a plain word or phrase in sentence case;
// human() keeps short capitals such as LOW as an acronym.
function codeText(value) {
  return hasValue(value) ? human(String(value).toLowerCase()) : null;
}

const VG_CLASS_TONE = Object.freeze({
  favorable_association: 'good', signal_not_observed_nonexclusion: 'good',
  concern_association: 'bad', signal_reported: 'bad', context_dependent: 'warn',
});
const VG_CLASS_RANK = Object.freeze({ concern_association: 0, signal_reported: 1, context_dependent: 2 });
// The report's vocabularies, worded by its own translations (VG.classification.*,
// VG.review.category.title.*, VG.value.*).
const VG_CLASSES = Object.freeze(['concern_association', 'context_dependent', 'descriptive', 'favorable_association', 'not_assessable',
  'quality_limitation', 'signal_not_observed_nonexclusion', 'signal_reported']);
const VG_SECTIONS = Object.freeze(['vaginal_balance', 'community_state_type', 'bacterial_vaginosis', 'aerobic_vaginitis', 'bacterial_sti',
  'viral_sti', 'pid_associated', 'cytolytic_vaginosis', 'yeast', 'uti_associated']);
const VG_BANDS = Object.freeze(['lactobacillus_predominant', 'moderate_weighted_lactobacillus', 'adverse_taxa_predominant', 'bv_targets_predominant']);

function vgCode(value) {
  return String(value ?? '').trim().toLowerCase();
}

function vaginalTone(classification) {
  return VG_CLASS_TONE[vgCode(classification)] || 'info';
}

function classificationWords(classification) {
  const code = vgCode(classification);
  if (!code) return null;
  return VG_CLASSES.includes(code) ? tx(`ui.patient.omx_vg_class_${code}`) : codeText(code);
}

function sectionWords(code) {
  const value = vgCode(code);
  return VG_SECTIONS.includes(value) ? tx(`ui.patient.omx_vg_section_${value}`) : human(code).replace(/\bsti\b/gi, 'STI');
}

function bandWords(code) {
  const value = vgCode(code);
  if (!value) return null;
  return VG_BANDS.includes(value) ? tx(`ui.patient.omx_vg_band_${value}`) : codeText(value);
}

// A section's classification: its primary clinician flag's, else the section row's own.
function sectionClassification(section, flags) {
  const primary = flags.find((flag) => String(flag.source_code || '').toUpperCase() === 'CLINICIAN_CONTRACT'
    && vgCode(flag.section_code) === vgCode(section.section_code) && vgCode(flag.role_code) === 'primary');
  return vgCode(primary?.classification_code || section.classification_code);
}

// A report product's primary flag classification, as the balance score is coloured.
function productClassification(product, flags) {
  const primary = flags.find((flag) => vgCode(flag.source_product_code) === product && vgCode(flag.role_code) === 'primary');
  return primary ? vgCode(primary.classification_code) : null;
}

function reviewOrder(left, right) {
  return (VG_CLASS_RANK[left.classification] ?? 3) - (VG_CLASS_RANK[right.classification] ?? 3)
    || Number(left.section_order || 0) - Number(right.section_order || 0);
}

// An organism counts as reported as the report counts it: in the profile, or past the sequence threshold.
function organismReported(row) {
  return String(row.profile_observation_code || '') === 'REPORTED_PRIMARY_CLADE' || Number(row.sequence_threshold_passed || 0) === 1;
}

function VaginalSection({ row, organisms }) {
  const tone = vaginalTone(row.classification);
  const listed = organisms.filter((organism) => vgCode(organism.section_code) === vgCode(row.section_code) && organismReported(organism));
  return (
    <Finding tone={tone} title={sectionWords(row.section_code)}
      chips={<>{hasValue(row.classification) && <Chip tone={tone === 'good' ? 'muted' : tone === 'info' ? 'info' : tone}>{classificationWords(row.classification)}</Chip>}
        {hasValue(row.priority_code) && <Chip>{codeText(row.priority_code)}</Chip>}</>}
      lines={[hasValue(row.score_value) ? tx('ui.patient.omx_score_band', { score: formatNumber(row.score_value, 1), band: bandWords(row.score_band_code) || tx('ui.patient.omx_not_given') }) : null,
        hasValue(row.reported_target_count) ? tx('ui.patient.omx_targets_reported', { reported: row.reported_target_count, configured: row.configured_target_count }) : null]}
      evidence={[
        { label: tx('ui.patient.omx_vg_primary_flag'), value: codeText(row.primary_flag_code) },
        { label: tx('ui.patient.confidence'), value: codeText(row.score_confidence_code) },
        { label: tx('ui.patient.coverage'), value: numeric(row.modeled_coverage_fraction) === null ? null : percent(Number(row.modeled_coverage_fraction), 1) },
        { label: tx('ui.patient.omx_organisms'), value: listed.map((organism) => organism.canonical_taxon || organism.requested_label).join(', ') || null },
      ]} />
  );
}

function VaginalView({ entry, query }) {
  const summary = oneRow(entry, 'vaginalSummary', ['health_score_value', 'cst_code', 'analysis_version']);
  const flags = readRows(entry, 'vaginalFlags', ['flag_code', 'section_code', 'role_code', 'classification_code']);
  const sections = readRows(entry, 'vaginalSections', ['section_code', 'primary_flag_code'])
    .map((row) => ({ ...row, classification: sectionClassification(row, flags) }));
  const organisms = readRows(entry, 'vaginalOrganisms', ['organism_code', 'section_code']);
  const species = readRows(entry, 'vaginalSpecies', ['abundance_rank', 'taxon', 'relative_abundance_pct']);
  // The report's "Results to review": the four most pressing sections, balance and CST left out.
  const review = sections.filter((row) => !['vaginal_balance', 'community_state_type'].includes(vgCode(row.section_code))).sort(reviewOrder).slice(0, 4);
  const ordered = [...sections].sort(reviewOrder);
  const scoreClass = productClassification('vaginal_health_score', flags);
  const reported = organisms.filter(organismReported);
  return (
    <OmicsView record={entry} about={tx('ui.patient.omx_vaginal_about')}
      facts={[
        { label: tx('ui.patient.omx_vg_score'), value: summary.health_score_value, unit: tx('ui.patient.omx_vg_score_scale'),
          detail: [bandWords(summary.health_score_band_code), classificationWords(scoreClass)].filter(Boolean).join(' · ') || null, tone: vaginalTone(scoreClass) },
        { label: tx('ui.patient.protective_score'), value: summary.protective_score_value, detail: bandWords(summary.protective_score_band_code) },
        { label: tx('ui.patient.omx_cst'), value: [summary.cst_code, summary.cst_subtype_code].filter(hasValue).join(' · ') || null },
        { label: tx('ui.patient.dominant_species'), value: summary.dominant_species, detail: numeric(summary.dominant_species_abundance_pct) === null ? null : `${formatNumber(summary.dominant_species_abundance_pct, 1)}%` },
        { label: tx('ui.patient.lactobacillus'), value: numeric(summary.lactobacillus_abundance_pct) === null ? null : formatNumber(summary.lactobacillus_abundance_pct, 1), unit: '%' },
        { label: tx('ui.patient.clinical_status'), value: codeText(summary.clinical_status_code) },
      ]}
      tabs={[
        { value: 'summary', label: tx('ui.patient.omx_tab_summary'), render: (go) => (
          <div className="xo-omx-summary">
            <OmicsBlock title={tx('ui.patient.omx_vg_review_title')} detail={tx('ui.patient.omx_vaginal_sections_detail')}
              action={<SeeAll label={tx('ui.patient.omx_see_sections')} onClick={() => go('sections')} />}>
              <FindingList items={review.map((row) => <VaginalSection key={row.section_code} row={row} organisms={organisms} />)} empty={tx('ui.patient.omx_no_rows_match')} />
            </OmicsBlock>
            <Composition title={tx('ui.patient.observed_species')} detail={tx('ui.patient.observed_species_detail')} unit="%"
              items={species.filter((row) => Number(row.is_displayed_top_species) === 1 || Number(row.abundance_rank) <= 8).map((row) => ({ label: row.taxon, value: row.relative_abundance_pct }))} />
          </div>
        ) },
        { value: 'sections', label: tx('ui.patient.clinical_guide'), count: sections.length, render: () => (
          <OmicsTable rows={ordered} query={query} label={tx('ui.patient.clinical_guide')} rowKey={(row) => row.section_code} searchLabel={tx('ui.patient.omx_search_table')}
            empty={tx('ui.patient.omx_no_rows_match')} detail={(row) => <VaginalSection row={row} organisms={organisms} />}
            columns={[
              { key: 'section_code', label: tx('ui.patient.section'), sort: (row) => (VG_CLASS_RANK[row.classification] ?? 3) * 100 + Number(row.section_order || 0),
                text: (row) => sectionWords(row.section_code), render: (row) => <span className="xo-omx-cell-mark"><OmicsMark tone={vaginalTone(row.classification)} /><strong>{sectionWords(row.section_code)}</strong></span> },
              { key: 'classification', label: tx('ui.patient.classification'), text: (row) => classificationWords(row.classification),
                render: (row) => classificationWords(row.classification) || <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span> },
              { key: 'priority_code', label: tx('ui.patient.omx_vg_priority'), text: (row) => codeText(row.priority_code), render: (row) => codeText(row.priority_code) || <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span> },
              { key: 'score_value', label: tx('ui.patient.score'), align: 'end', sort: (row) => numeric(row.score_value),
                render: (row) => (numeric(row.score_value) === null ? <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span> : formatNumber(row.score_value, 1)) },
              { key: 'score_band_code', label: tx('ui.patient.band'), text: (row) => bandWords(row.score_band_code), render: (row) => bandWords(row.score_band_code) || <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span> },
            ]} />
        ) },
        { value: 'organisms', label: tx('ui.patient.organism_evidence'), count: organisms.length, render: () => (
          <OmicsTable rows={organisms} query={query} label={tx('ui.patient.organism_evidence')} rowKey={(row) => row.sourceRow} searchLabel={tx('ui.patient.omx_search_taxa')}
            empty={tx('ui.patient.omx_no_taxa')} initialSort={{ key: 'relative_abundance_pct', direction: 'desc' }}
            columns={[
              { key: 'canonical_taxon', label: tx('ui.patient.organism'), text: (row) => row.canonical_taxon || row.requested_label, render: (row) => <strong className="is-italic">{row.canonical_taxon || row.requested_label}</strong> },
              { key: 'section_code', label: tx('ui.patient.section'), text: (row) => sectionWords(row.section_code), render: (row) => sectionWords(row.section_code) },
              { key: 'relative_abundance_pct', label: tx('ui.patient.relative_abundance'), align: 'end', firstDirection: 'desc', sort: (row) => numeric(row.relative_abundance_pct),
                render: (row) => (numeric(row.relative_abundance_pct) === null ? <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span>
                  : Number(row.relative_abundance_pct) > 0 && Number(row.relative_abundance_pct) < 0.001 ? '<0.001%' : `${Number(row.relative_abundance_pct).toFixed(3)}%`) },
              { key: 'reported', label: tx('ui.patient.status'), text: (row) => tx(organismReported(row) ? 'ui.patient.omx_vg_reported' : 'ui.patient.omx_vg_not_reported'),
                sort: (row) => (organismReported(row) ? 0 : 1), render: (row) => tx(organismReported(row) ? 'ui.patient.omx_vg_reported' : 'ui.patient.omx_vg_not_reported') },
              { key: 'sequence_observation_code', label: tx('ui.patient.sequence_observation'), text: (row) => codeText(row.sequence_observation_code),
                render: (row) => codeText(row.sequence_observation_code) || <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span> },
            ]}
            filters={[{ key: 'seen', label: tx('ui.patient.status'), initial: 'reported', options: [{ value: 'all', label: tx('ui.patient.omx_all') }, { value: 'reported', label: tx('ui.patient.omx_vg_reported') }],
              test: (row) => reported.includes(row) }]} />
        ) },
        { value: 'species', label: tx('ui.patient.observed_species'), count: species.length, render: () => (
          <OmicsTable rows={species} query={query} label={tx('ui.patient.observed_species')} rowKey={(row) => row.sourceRow} searchLabel={tx('ui.patient.omx_search_taxa')}
            empty={tx('ui.patient.omx_no_taxa')} initialSort={{ key: 'abundance_rank', direction: 'asc' }}
            columns={[
              { key: 'abundance_rank', label: tx('ui.patient.rank'), align: 'end', sort: (row) => numeric(row.abundance_rank) },
              { key: 'taxon', label: tx('ui.patient.taxon'), render: (row) => <strong className="is-italic">{row.taxon}</strong> },
              { key: 'genus', label: tx('ui.patient.omx_genus') },
              { key: 'relative_abundance_pct', label: tx('ui.patient.relative_abundance'), align: 'end', firstDirection: 'desc', sort: (row) => numeric(row.relative_abundance_pct), render: (row) => `${formatNumber(row.relative_abundance_pct, 2)}%` },
              { key: 'cst_mapping_status_code', label: tx('ui.patient.omx_cst_mapping'), text: (row) => codeText(row.cst_mapping_status_code), render: (row) => codeText(row.cst_mapping_status_code) || <span className="xo-omx-none">{tx('ui.patient.omx_not_given')}</span> },
            ]} />
        ) },
        { value: 'quality', label: tx('ui.patient.omx_tab_quality'), render: () => (
          <div className="xo-omx-summary">
            <OmicsBlock title={tx('ui.patient.omx_tab_quality')} detail={tx('ui.patient.omx_quality_detail')}>
              <FactGrid items={[
                { label: tx('ui.patient.technical_status'), value: codeText(summary.technical_status_code) },
                { label: tx('ui.patient.controls'), value: codeText(summary.controls_status_code) },
                { label: tx('ui.patient.omx_raw_reads'), value: numeric(summary.raw_total_reads) === null ? null : NUMBER.format(Number(summary.raw_total_reads)) },
                { label: tx('ui.patient.omx_filtered_reads'), value: numeric(summary.filtered_total_reads) === null ? null : NUMBER.format(Number(summary.filtered_total_reads)) },
                { label: tx('ui.patient.omx_host_reads'), value: numeric(summary.host_alignment_pct) === null ? null : `${formatNumber(summary.host_alignment_pct, 1)}%` },
                { label: tx('ui.patient.omx_bacterial_share'), value: numeric(summary.bacterial_abundance_pct) === null ? null : `${formatNumber(summary.bacterial_abundance_pct, 1)}%` },
                { label: tx('ui.patient.shannon_diversity'), value: summary.diversity_shannon },
                { label: tx('ui.patient.gini_simpson'), value: summary.diversity_gini_simpson },
                { label: tx('ui.patient.analysis_version'), value: summary.analysis_version },
              ]} />
            </OmicsBlock>
          </div>
        ) },
      ]} />
  );
}

// ------------------------------------------------ transcriptome

// The transcriptome report's reading (PLReportGeneration shared/layouts/wbtrisk
// reportDiseaseRisk.js, reportOverview.js): a disease axis needs attention
// when its confidence is High or Moderate, whatever its z-score; the
// report's colours for confidence are shades of its brand, not alarms. The
// age difference is only words: within 2 years aligned, else older or younger.
function transcriptTone(row) {
  const confidence = String(row.confidence || '').toLowerCase();
  return confidence === 'high' || confidence === 'moderate' ? 'warn' : 'info';
}

function transcriptInterpretation(row) {
  const confidence = String(row.confidence || '').toLowerCase();
  return ['high', 'moderate', 'low'].includes(confidence) ? tx(`ui.patient.omx_tx_interp_${confidence}`, { disease: row.disease }) : row.interpretation;
}

function ageSentence(gap) {
  const value = numeric(gap);
  if (value === null) return tx('ui.patient.omx_tx_age_unavailable');
  const difference = tx('ui.patient.omx_tx_years', { value: formatNumber(Math.abs(value), 1) });
  if (value <= -2) return tx('ui.patient.omx_tx_age_younger', { difference });
  if (value >= 2) return tx('ui.patient.omx_tx_age_older', { difference });
  return tx('ui.patient.omx_tx_age_aligned', { difference });
}

function TranscriptomeView({ entry, query }) {
  const cells = oneRow(entry, 'transcriptCells', ['t_cells', 'b_cells', 'monocytes']);
  const diseases = readRows(entry, 'transcriptDiseases', ['rank_order', 'disease', 'zscore', 'confidence']);
  const report = oneRow(entry, 'transcriptReport', ['biological_age_predicted', 'biological_age_gap']);
  // High confidence first, then moderate, each in the pipeline's rank order.
  const signals = diseases.filter((row) => transcriptTone(row) !== 'info')
    .sort((left, right) => (String(left.confidence).toLowerCase() === 'high' ? 0 : 1) - (String(right.confidence).toLowerCase() === 'high' ? 0 : 1)
      || Number(left.rank_order) - Number(right.rank_order));
  const gap = numeric(report.biological_age_gap);
  const cellItems = transcriptCellItems(cells);
  return (
    <OmicsView record={entry} about={tx('ui.patient.omx_transcriptome_about')}
      facts={[
        { label: tx('ui.patient.predicted_age'), value: numeric(report.biological_age_predicted) === null ? null : formatNumber(report.biological_age_predicted, 1), unit: tx('ui.patient.years_unit') },
        { label: tx('ui.patient.age_gap'), value: gap === null ? null : signed(gap, 1), unit: tx('ui.patient.years_unit'),
          detail: numeric(report.biological_age_chronological) === null ? null : tx('ui.patient.omx_chronological_age', { age: formatNumber(report.biological_age_chronological, 0) }) },
        { label: tx('ui.patient.omx_disease_signals'), value: signals.length, tone: signals.length ? 'warn' : 'good' },
        { label: tx('ui.patient.disease_healthy_score'), value: numeric(report.disease_healthy_score) === null ? null : formatNumber(report.disease_healthy_score, 2) },
        { label: tx('ui.patient.smoking_call'), value: report.smoking_call },
      ]}
      tabs={[
        { value: 'summary', label: tx('ui.patient.omx_tab_summary'), render: (go) => (
          <div className="xo-omx-summary">
            <div className="xo-omx-verdict is-info"><OmicsMark tone="dot" /><div><strong>{tx('ui.patient.omx_tx_age_title')}</strong><p>{ageSentence(report.biological_age_gap)}</p></div></div>
            <OmicsBlock title={tx('ui.patient.omx_disease_signals')} detail={tx('ui.patient.omx_transcript_signals_detail')}
              action={<SeeAll label={tx('ui.patient.omx_all_conditions', { count: NUMBER.format(diseases.length) })} onClick={() => go('diseases')} />}>
              <FindingList limit={5} onMore={() => go('diseases')} moreLabel={tx('ui.patient.omx_see_all_findings', { count: NUMBER.format(signals.length) })}
                empty={tx('ui.patient.omx_transcript_signals_none')}
                items={signals.map((row) => (
                  <Finding key={row.sourceRow} tone={transcriptTone(row)} title={row.disease}
                    chips={<><Chip tone="warn">{tx('ui.patient.omx_confidence_value', { value: human(row.confidence) })}</Chip><Chip>{tx('ui.patient.omx_z_value', { value: zText(row.zscore) })}</Chip></>}
                    lines={[]} note={transcriptInterpretation(row)}
                    evidence={[{ label: tx('ui.patient.score'), value: formatNumber(row.score, 2) }, { label: tx('ui.patient.margin'), value: formatNumber(row.margin, 2) }, { label: tx('ui.patient.rank'), value: row.rank_order }]} />
                ))} />
            </OmicsBlock>
            <div className="xo-omx-split">
              <Composition title={tx('ui.patient.immune_cells')} detail={tx('ui.patient.immune_cells_detail')} items={cellItems} unit="%" />
              {/* The report places each percentile within its reference cohort and draws no typical band. */}
              <OmicsBlock title={tx('ui.patient.percentiles')} detail={tx('ui.patient.omx_percentiles_detail')}>
                <FactGrid items={[['ui.patient.biological_age', report.biological_age_percentile], ['ui.patient.bmi', report.bmi_percentile], ['ui.patient.smoking', report.smoking_percentile]]
                  .filter(([, value]) => numeric(value) !== null)
                  .map(([key, value]) => ({ label: tx(key), value: tx('ui.patient.percentile_value', { value: formatNumber(value, 0) }) }))} />
              </OmicsBlock>
            </div>
          </div>
        ) },
        { value: 'diseases', label: tx('ui.patient.omx_tab_conditions'), count: diseases.length, render: () => (
          <OmicsTable rows={diseases} query={query} label={tx('ui.patient.omx_tab_conditions')} rowKey={(row) => row.sourceRow}
            initialSort={{ key: 'rank_order', direction: 'asc' }} searchLabel={tx('ui.patient.omx_search_conditions')} empty={tx('ui.patient.omx_transcript_signals_none')}
            detail={(row) => <Evidence items={[{ label: tx('ui.patient.interpretation'), value: transcriptInterpretation(row) }, { label: tx('ui.patient.margin'), value: formatNumber(row.margin, 2) }]} />}
            columns={[
              { key: 'confidence', label: tx('ui.patient.confidence'), sort: (row) => ({ high: 0, moderate: 1, low: 2 }[String(row.confidence).toLowerCase()] ?? 3),
                render: (row) => <span className="xo-omx-cell-mark"><OmicsMark tone={transcriptTone(row)} />{human(row.confidence)}</span> },
              { key: 'disease', label: tx('ui.patient.omx_condition'), render: (row) => <strong>{row.disease}</strong> },
              { key: 'zscore', label: tx('ui.patient.z_score'), align: 'end', firstDirection: 'desc', sort: (row) => numeric(row.zscore), render: (row) => <span className="xo-omx-zcell"><ZBar value={row.zscore} band={1} /><span>{zText(row.zscore)}</span></span> },
              { key: 'score', label: tx('ui.patient.score'), align: 'end', sort: (row) => numeric(row.score), render: (row) => formatNumber(row.score, 2) },
              { key: 'rank_order', label: tx('ui.patient.rank'), align: 'end', sort: (row) => numeric(row.rank_order) },
            ]}
            filters={[{ key: 'confidence', label: tx('ui.patient.confidence'), initial: 'signals', options: [
              { value: 'all', label: tx('ui.patient.omx_all') }, { value: 'signals', label: tx('ui.patient.omx_needs_a_look') },
              ...['High', 'Moderate', 'Low'].map((value) => ({ value, label: human(value) }))],
              test: (row, value) => (value === 'signals' ? transcriptTone(row) !== 'info' : String(row.confidence).toLowerCase() === value.toLowerCase()) }]} />
        ) },
        { value: 'cells', label: tx('ui.patient.immune_cells'), count: cellItems.length, render: () => (
          <div className="xo-omx-summary">
            <Composition title={tx('ui.patient.immune_cells')} detail={tx('ui.patient.immune_cells_detail')} items={cellItems} unit="%" />
            <OmicsBlock title={tx('ui.patient.model_coverage')} detail={tx('ui.patient.omx_model_coverage_detail')}>
              <FactGrid items={[
                { label: tx('ui.patient.disease_genes_used'), value: report.disease_genes_used },
                { label: tx('ui.patient.disease_genes_missing'), value: report.disease_missing_genes },
                { label: tx('ui.patient.age_genes_missing'), value: report.biological_age_missing_genes },
                { label: tx('ui.patient.bmi_genes_missing'), value: report.bmi_missing_genes },
                { label: tx('ui.patient.smoking_genes_missing'), value: report.smoking_missing_genes },
              ]} />
            </OmicsBlock>
          </div>
        ) },
      ]} />
  );
}

// ------------------------------------------------ metabolomics and lipidomics

// The pipelines rank each feature against the other features of the same
// sample (a within-sample percentile, bands by thirds). There is no
// population reference, so nothing here is called typical or outside a range.
const RANK_BANDS = Object.freeze({ high: 'ui.patient.omx_rank_top', typical: 'ui.patient.omx_rank_middle', low: 'ui.patient.omx_rank_bottom' });
const FEATURE_ABOUT = Object.freeze({
  lipids: 'ui.patient.omx_lipids_about', plasma: 'ui.patient.omx_plasma_metabolites_about', urine: 'ui.patient.omx_urine_metabolites_about',
});

function FeatureView({ entry, query, aliasKey, kind }) {
  const features = readRows(entry, aliasKey, ['feature_name', 'abundance', 'abundance_percentile', 'signal_band', 'qc_status']);
  const measured = features.filter((row) => numeric(row.abundance) !== null);
  const missing = features.length - measured.length;
  const top = [...measured].sort((left, right) => Number(right.abundance) - Number(left.abundance)).slice(0, 8);
  return (
    <OmicsView record={entry} about={tx(FEATURE_ABOUT[kind])}
      facts={[
        { label: tx('ui.patient.features_measured'), value: measured.length },
        { label: tx('ui.patient.omx_not_measured'), value: missing },
        { label: tx('ui.patient.omx_features_total'), value: features.length },
      ]}
      tabs={[
        { value: 'summary', label: tx('ui.patient.omx_tab_summary'), render: (go) => (
          <div className="xo-omx-summary">
            <div className="xo-omx-verdict is-info"><OmicsMark tone="info" /><div><strong>{tx('ui.patient.omx_features_no_reference')}</strong><p>{tx('ui.patient.omx_features_no_reference_detail')}</p></div></div>
            <OmicsBlock title={tx('ui.patient.omx_most_abundant')} detail={tx('ui.patient.omx_most_abundant_detail')}
              action={<SeeAll label={tx('ui.patient.omx_all_features', { count: NUMBER.format(features.length) })} onClick={() => go('features')} />}>
              <RankBars rows={top.map((row) => ({ label: row.feature_name, value: Number(row.abundance), detail: row.formula }))} />
            </OmicsBlock>
          </div>
        ) },
        { value: 'features', label: tx(kind === 'lipids' ? 'ui.patient.omx_tab_lipids' : 'ui.patient.omx_tab_metabolites'), count: features.length, render: () => (
          <OmicsTable rows={features} query={query} label={tx('ui.patient.feature_detail')} rowKey={(row) => row.sourceRow}
            initialSort={{ key: 'abundance', direction: 'desc' }} searchLabel={tx('ui.patient.omx_search_features')} empty={tx('ui.patient.omx_no_features')}
            detail={(row) => <Evidence items={[
              { label: tx('ui.patient.calculated_mw'), value: row.calculated_mw },
              { label: tx('ui.patient.delta_mass'), value: row.delta_mass_ppm },
              { label: tx('ui.patient.area_max'), value: numeric(row.area_max) === null ? null : NUMBER.format(Number(row.area_max)) },
              { label: tx('ui.patient.reference_ion'), value: row.reference_ion },
              { label: tx('ui.patient.omx_qc'), value: hasValue(row.qc_status) ? human(row.qc_status) : null },
            ]} />}
            columns={[
              { key: 'feature_name', label: tx('ui.patient.feature'), render: (row) => <strong>{row.feature_name}</strong> },
              { key: 'formula', label: tx('ui.patient.formula') },
              { key: 'mz', label: 'm/z', align: 'end', sort: (row) => numeric(row.mz), render: (row) => formatNumber(row.mz, 4) },
              { key: 'retention_time_min', label: tx('ui.patient.retention_time'), align: 'end', sort: (row) => numeric(row.retention_time_min), render: (row) => formatNumber(row.retention_time_min, 2) },
              { key: 'abundance', label: tx('ui.patient.abundance'), align: 'end', firstDirection: 'desc', sort: (row) => numeric(row.abundance),
                render: (row) => (numeric(row.abundance) === null ? <span className="xo-omx-none">{tx('ui.patient.omx_not_measured')}</span>
                  : NUMBER.format(Math.abs(Number(row.abundance)) >= 100 ? Math.round(Number(row.abundance)) : Number(row.abundance))) },
              { key: 'abundance_percentile', label: tx('ui.patient.omx_rank_in_sample'), align: 'end', sort: (row) => numeric(row.abundance_percentile),
                render: (row) => (numeric(row.abundance_percentile) === null ? <span className="xo-omx-none">{tx('ui.patient.omx_not_measured')}</span> : tx('ui.patient.percentile_value', { value: formatNumber(row.abundance_percentile, 0) })) },
            ]}
            filters={[
              { key: 'band', label: tx('ui.patient.omx_rank_in_sample'), options: [{ value: 'all', label: tx('ui.patient.omx_all') }, ...Object.entries(RANK_BANDS).map(([value, key]) => ({ value, label: tx(key) })),
                { value: 'unavailable', label: tx('ui.patient.omx_not_measured') }],
                test: (row, value) => String(row.signal_band).toLowerCase() === value },
            ]} />
        ) },
      ]} />
  );
}

// Plain horizontal bars for a short ranked list (the section's own values, not a comparison).
function RankBars({ rows }) {
  const most = Math.max(1, ...rows.map((row) => row.value));
  return (
    <ol className="xo-omx-bars">
      {rows.map((row) => (
        <li key={row.label}>
          <span title={row.label}><strong>{row.label}</strong>{row.detail && <small>{row.detail}</small>}</span>
          <i><b style={{ width: `${Math.max(2, (row.value / most) * 100)}%` }} /></i>
          <em>{NUMBER.format(Math.abs(row.value) >= 100 ? Math.round(row.value) : row.value)}</em>
        </li>
      ))}
    </ol>
  );
}

// ------------------------------------------------ ProteoRisk (Olink proteomics)

// The olink-proteomics 2.0.0 report tables, read with the ProteoRisk PDF's
// rules (PLReportGeneration shared/layouts/proteomics/data.js). Condition
// groups, names and interpretations are the model's English, which is also
// the English the PDF prints. A result without valid report tables says so,
// and its measured proteins stay available.
const PRK_FLAG_Z = 1.5;
const PRK_TIERS = ['high', 'moderate', 'low'];
const PRK_SECTIONS = ['current', 'ten_year'];
const PRK_SYSTEMS = [
  ['circulatory', 'Circulatory system', ['Heart and', 'blood vessels']],
  ['respiratory', 'Respiratory system', ['Lungs and', 'breathing']],
  ['metabolic', 'Endocrine, nutritional & metabolic', ['Metabolism', 'and hormones']],
  ['digestive', 'Digestive system', ['Digestion']],
  ['genitourinary', 'Genitourinary system', ['Urinary and', 'reproductive system']],
  ['musculoskeletal', 'Musculoskeletal system', ['Bones, joints', 'and muscles']],
  ['blood', 'Blood & immune mechanism', ['Blood and', 'immunity']],
  ['infections', 'Infectious & parasitic diseases', ['Infections']],
  ['neoplasms', 'Neoplasms', ['Tumours and', 'growths']],
  ['eye', 'Eye & adnexa', ['Eyes']],
  ['skin', 'Skin & subcutaneous tissue', ['Skin']],
  ['multisystem', 'Multisystem / composite', ['Several body', 'systems']],
].map(([code, category, lines], order) => Object.freeze({ code, category, lines, order, nameKey: `ui.patient.omx_prk_system_${code}` }));
const PRK_SYSTEM_BY_CATEGORY = new Map(PRK_SYSTEMS.map((system) => [system.category, system]));
const PRK_STATUS_TONE = Object.freeze({ high: 'bad', moderate: 'warn', typical: 'good' });
const PRK_TIER_TONE = Object.freeze({ high: 'bad', moderate: 'warn', low: 'info' });
const PRK_STATUS_KEY = Object.freeze({ high: 'ui.patient.omx_prk_strong', moderate: 'ui.patient.omx_prk_moderate', typical: 'ui.patient.omx_prk_typical' });
const PRK_TIER_KEY = Object.freeze({ high: 'ui.patient.omx_prk_tier_high', moderate: 'ui.patient.omx_prk_tier_moderate', low: 'ui.patient.omx_prk_tier_low' });

function prkFail(message) {
  const error = new Error(message);
  error.proteoRiskUnavailable = true;
  throw error;
}

function prkInvalid(label) {
  prkFail(tx('ui.patient.omx_prk_invalid', { label }));
}

function prkRows(entry, table) {
  if (!Array.isArray(entry?.data)) prkFail(tx('ui.patient.omx_prk_no_outputs'));
  const matches = entry.data.filter((output) => output?.targetTable === table);
  if (matches.length === 0) return null;
  if (matches.length > 1) prkFail(tx('ui.patient.omx_prk_duplicate_table', { table, count: matches.length }));
  if (!Array.isArray(matches[0].rows)) prkFail(tx('ui.patient.omx_prk_rows_missing', { table }));
  return matches[0].rows;
}

function prkNumber(value, label) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') prkInvalid(label);
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) prkInvalid(label);
  return parsed;
}

function prkWhole(value, label) {
  const parsed = prkNumber(value, label);
  if (!Number.isSafeInteger(parsed) || parsed < 0) prkInvalid(label);
  return parsed;
}

function prkText(value, label) {
  const parsed = typeof value === 'string' ? value.trim() : '';
  if (!parsed) prkInvalid(label);
  return parsed;
}

function prkFlag(value, label) {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return prkInvalid(label);
}

// The model's fixed tier boundaries: below 0.35 is low, below 0.65 moderate.
function prkTierFor(score) {
  if (score >= 0.65) return 'high';
  return score >= 0.35 ? 'moderate' : 'low';
}

// 0 to 100, rounded down as the PDF does, so a number stays on the same side of a tier boundary.
function prkScore(score) {
  return Math.floor(score * 100 + 1e-9);
}

function prkByScore(left, right) {
  return (right.score - left.score) || (left.order - right.order);
}

function prkStatus(rows) {
  if (rows.some((row) => row.tier === 'high')) return 'high';
  return rows.some((row) => row.tier === 'moderate') ? 'moderate' : 'typical';
}

function readProteoRisk(entry) {
  const summaryRows = prkRows(entry, 'olink_report_summary');
  const findingRows = prkRows(entry, 'olink_report_findings');
  const panelRows = prkRows(entry, 'olink_report_panel_proteins');
  const missing = [['olink_report_summary', summaryRows], ['olink_report_findings', findingRows], ['olink_report_panel_proteins', panelRows]]
    .filter(([, rows]) => rows === null).map(([table]) => table);
  if (missing.length) prkFail(tx('ui.patient.omx_prk_tables_missing', { tables: missing.join(', ') }));
  if (summaryRows.length !== 1) prkFail(tx('ui.patient.omx_prk_summary_rows', { count: summaryRows.length }));
  const row = summaryRows[0];
  const summary = {
    platform: prkText(row.panel_platform, 'panel_platform'),
    panelCount: prkWhole(row.panel_protein_count, 'panel_protein_count'),
    observed: prkWhole(row.observed_protein_count, 'observed_protein_count'),
    coverage: prkNumber(row.coverage_fraction, 'coverage_fraction'),
    age: prkNumber(row.age_years, 'age_years'),
    sex: ['Male', 'Female'].includes(row.sex) ? row.sex : prkInvalid('sex'),
    bmi: row.bmi === null || row.bmi === undefined ? null : prkNumber(row.bmi, 'bmi'),
  };
  if (findingRows.length === 0) prkFail(tx('ui.patient.omx_prk_findings_empty'));
  const seen = new Set();
  const findings = findingRows.map((finding) => {
    const taskIndex = prkWhole(finding.task_index, 'task_index');
    if (seen.has(taskIndex)) prkInvalid(`task_index ${taskIndex}`);
    seen.add(taskIndex);
    const category = prkText(finding.category, 'category');
    const system = PRK_SYSTEM_BY_CATEGORY.get(category);
    if (!system) prkFail(tx('ui.patient.omx_prk_unknown_group', { group: category }));
    const score = prkNumber(finding.score, 'score');
    if (score < 0 || score > 1) prkInvalid(`score ${taskIndex}`);
    if (!PRK_TIERS.includes(finding.tier) || finding.tier !== prkTierFor(score)) prkInvalid(`tier ${taskIndex}`);
    if (!PRK_SECTIONS.includes(finding.section)) prkInvalid('section');
    return {
      id: `${finding.section}-${taskIndex}`, taskIndex, order: prkWhole(finding.sourceRow, 'sourceRow'), section: finding.section, system,
      disease: prkText(finding.disease, 'disease'), score, score100: prkScore(score), tier: finding.tier,
      interpretation: prkText(finding.interpretation, 'interpretation'), hasPanel: prkFlag(finding.has_protein_panel, 'has_protein_panel'), proteins: [],
    };
  });
  const byTask = new Map(findings.map((finding) => [finding.taskIndex, finding]));
  panelRows.forEach((panel) => {
    const finding = byTask.get(prkWhole(panel.task_index, 'task_index'));
    if (!finding || !finding.hasPanel) prkInvalid(`panel ${panel.task_index}`);
    finding.proteins.push({
      order: prkWhole(panel.sourceRow, 'sourceRow'), proteinId: prkText(panel.protein_id, 'protein_id'), geneName: prkText(panel.gene_name, 'gene_name'),
      zScore: prkNumber(panel.z_score, 'z_score'), flagged: prkFlag(panel.flagged, 'flagged'),
    });
  });
  findings.forEach((finding) => {
    finding.proteins.sort((left, right) => left.order - right.order);
    if (finding.hasPanel && finding.proteins.length === 0) prkInvalid(`panel ${finding.taskIndex}`);
  });
  const systems = PRK_SYSTEMS.map((system) => {
    const rows = findings.filter((finding) => finding.system === system);
    if (rows.length === 0) return null;
    const notable = rows.filter((finding) => finding.tier !== 'low').sort(prkByScore);
    const others = new Set(notable.map((finding) => finding.disease));
    if (notable[0]) others.delete(notable[0].disease);
    return { system, status: prkStatus(rows), top: notable[0] || null, others: others.size, notable: notable.length, total: rows.length,
      counts: { high: rows.filter((f) => f.tier === 'high').length, moderate: rows.filter((f) => f.tier === 'moderate').length, low: rows.filter((f) => f.tier === 'low').length } };
  }).filter(Boolean);
  const highlights = [];
  const names = new Set();
  findings.filter((finding) => finding.tier !== 'low').sort(prkByScore).forEach((finding) => {
    if (highlights.length >= 4 || names.has(finding.disease)) return;
    names.add(finding.disease);
    highlights.push(finding);
  });
  const outside = new Map();
  [...findings].sort(prkByScore).forEach((finding) => finding.proteins.filter((protein) => protein.flagged).forEach((protein) => {
    if (!outside.has(protein.proteinId)) outside.set(protein.proteinId, { ...protein, findings: [] });
    const item = outside.get(protein.proteinId);
    if (Math.abs(protein.zScore) > Math.abs(item.zScore)) item.zScore = protein.zScore;
    item.findings.push(finding);
  }));
  return { summary, findings, systems, highlights, outside: [...outside.values()] };
}

function prkHeadline(systems) {
  const total = systems.length;
  const flagged = systems.filter((system) => system.status !== 'typical').length;
  const values = { total: NUMBER.format(total), typical: NUMBER.format(total - flagged), count: NUMBER.format(flagged) };
  if (flagged === 0) return tx('ui.patient.omx_prk_headline_none', values);
  if (flagged === total) return tx('ui.patient.omx_prk_headline_all', values);
  return tx(flagged === 1 ? 'ui.patient.omx_prk_headline_one' : 'ui.patient.omx_prk_headline_many', values);
}

function prkSentence(finding) {
  return tx(finding.section === 'current' ? 'ui.patient.omx_prk_like_current' : 'ui.patient.omx_prk_like_ten_year', { condition: finding.disease });
}

function prkSystemSentence(system) {
  if (!system.top) return tx('ui.patient.omx_prk_system_typical');
  const first = tx(system.top.section === 'current' ? 'ui.patient.omx_prk_system_current' : 'ui.patient.omx_prk_system_ten_year', { condition: system.top.disease });
  if (system.others === 0) return first;
  return `${first} ${counted('ui.patient.omx_prk_system_more', system.others)}`;
}

function prkPolar(cx, cy, radius, degrees) {
  const radians = (degrees * Math.PI) / 180;
  return [cx + radius * Math.sin(radians), cy - radius * Math.cos(radians)];
}

function prkArc(cx, cy, radius, from, to) {
  const [x0, y0] = prkPolar(cx, cy, radius, from);
  const [x1, y1] = prkPolar(cx, cy, radius, to);
  return `M ${x0} ${y0} A ${radius} ${radius} 0 ${to - from > 180 ? 1 : 0} 1 ${x1} ${y1}`;
}

function prkSector(cx, cy, inner, outer, from, to) {
  const [ox0, oy0] = prkPolar(cx, cy, outer, from);
  const [ox1, oy1] = prkPolar(cx, cy, outer, to);
  const [ix1, iy1] = prkPolar(cx, cy, inner, to);
  const [ix0, iy0] = prkPolar(cx, cy, inner, from);
  const large = to - from > 180 ? 1 : 0;
  return `M ${ox0} ${oy0} A ${outer} ${outer} 0 ${large} 1 ${ox1} ${oy1} L ${ix1} ${iy1} A ${inner} ${inner} 0 ${large} 0 ${ix0} ${iy0} Z`;
}

// The ProteoRisk body system drawings: 24 x 24 line icons with round caps.
const PRK_BONE = 'M7.75 10.9H16.25A2.2 2.2 0 1 1 19.41 12A2.2 2.2 0 1 1 16.25 13.1H7.75A2.2 2.2 0 1 1 4.59 12A2.2 2.2 0 1 1 7.75 10.9Z';
const PRK_ICONS = Object.freeze({
  circulatory: [{ d: 'M12 19.6 5.3 12.9a4.4 4.4 0 0 1 0-6.2 4.4 4.4 0 0 1 6.2 0l.5.5.5-.5a4.4 4.4 0 0 1 6.2 0 4.4 4.4 0 0 1 0 6.2z' }, { d: 'M6.2 12.2h2.6l1.4-2.6 2.2 4.4 1.5-2.9h3.9' }],
  respiratory: [{ d: 'M12 4v7.4' }, { d: 'M12 10.2c-.7 1.3-1.6 2-2.6 2.3M12 10.2c.7 1.3 1.6 2 2.6 2.3' }, { d: 'M8.9 7.1C6.4 7.8 4.5 11.8 4.5 16c0 2.1 1 3.4 2.7 3.4 1.8 0 2.8-1.5 2.8-3.5V8.8c0-1.1-.4-1.8-1.1-1.7z' }, { d: 'M15.1 7.1c2.5.7 4.4 4.7 4.4 8.9 0 2.1-1 3.4-2.7 3.4-1.8 0-2.8-1.5-2.8-3.5V8.8c0-1.1.4-1.8 1.1-1.7z' }],
  metabolic: [{ cx: 8.6, cy: 14.6, r: 4.6 }, { cx: 16.4, cy: 7.8, r: 3.1 }, { cx: 17.4, cy: 16.8, r: 2.1 }],
  digestive: [{ d: 'M9 2.8v3.4c0 2.2-4 3.2-4 7.4s3.2 7 7.4 7c2.8 0 5-1.2 6.2-3.2l2.2.8' }, { d: 'M12 2.8v4c0 3.8 1.4 6.4 4.6 7 1.4.3 2.4 1.5 2 3.6' }],
  genitourinary: [{ d: 'M12.6 3.8c-3.8 0-7.1 3.6-7.1 8.2s3.3 8.2 7.1 8.2c2.5 0 4.1-1.7 4.1-3.8 0-1.9-1.7-2.6-1.7-4.4s1.7-2.5 1.7-4.4c0-2.1-1.6-3.8-4.1-3.8z' }, { d: 'M15.2 12h2.2c1 0 1.8.8 1.8 1.8v6.7' }],
  musculoskeletal: [{ d: PRK_BONE, transform: 'rotate(-45 12 12)' }],
  blood: [{ d: 'M12 3.4c3.2 4.2 6 7.3 6 10.7a6 6 0 0 1-12 0c0-3.4 2.8-6.5 6-10.7z' }, { d: 'M9.2 14.4a2.8 2.8 0 0 0 2.8 2.8' }],
  infections: [{ d: 'M12 3.4l7 2.8v5.2c0 4.4-2.9 7.9-7 9.1-4.1-1.2-7-4.7-7-9.1V6.2z' }, { d: 'M12 9.2v4.8M9.6 11.6h4.8' }],
  neoplasms: [{ cx: 12, cy: 12, r: 8.2 }, { cx: 12, cy: 12, r: 4.6 }, { cx: 12, cy: 12, r: 1.4, fill: true }],
  eye: [{ d: 'M2.8 12s3.4-6.1 9.2-6.1 9.2 6.1 9.2 6.1-3.4 6.1-9.2 6.1S2.8 12 2.8 12z' }, { cx: 12, cy: 12, r: 2.8 }],
  skin: [{ d: 'M3 7.5c3-2.4 6 2.4 9 0s6-2.4 9 0' }, { d: 'M3 12.5c3-2.4 6 2.4 9 0s6-2.4 9 0' }, { d: 'M3 17.5c3-2.4 6 2.4 9 0s6-2.4 9 0' }],
  multisystem: [{ d: 'M12 7.5 6.8 16M12 7.5 17.2 16M8.8 17.6h6.4' }, { cx: 12, cy: 5.5, r: 2.6 }, { cx: 5.5, cy: 17.6, r: 2.6 }, { cx: 18.5, cy: 17.6, r: 2.6 }],
});

function PrkIcon({ code, className }) {
  return (
    <g className={className}>
      {PRK_ICONS[code].map((shape, index) => (shape.d
        // eslint-disable-next-line react/no-array-index-key
        ? <path key={index} d={shape.d} transform={shape.transform} />
        // eslint-disable-next-line react/no-array-index-key
        : <circle key={index} cx={shape.cx} cy={shape.cy} r={shape.r} className={shape.fill ? 'is-filled' : undefined} />))}
    </g>
  );
}

function PrkBadge({ system, status, size = 34 }) {
  return (
    <svg className={`xo-prk-badge is-${status}`} width={size} height={size} viewBox="0 0 36 36" aria-hidden="true">
      <circle cx="18" cy="18" r="18" />
      <g transform="translate(7 7) scale(0.9167)"><PrkIcon code={system.code} /></g>
    </svg>
  );
}

const PRK_WHEEL = Object.freeze({ width: 600, height: 440, cx: 300, cy: 220, outer: 118, inner: 70, ring: 125, label: 138, stretch: 1.1, gap: 1.2 });

function PrkWheel({ systems }) {
  const slice = 360 / systems.length;
  const typical = systems.filter((system) => system.status === 'typical').length;
  return (
    <div className="xo-prk-wheel" role="img" aria-label={tx('ui.patient.omx_prk_wheel_label', { count: NUMBER.format(typical), total: NUMBER.format(systems.length) })}>
      <svg viewBox={`0 0 ${PRK_WHEEL.width} ${PRK_WHEEL.height}`} aria-hidden="true">
        <circle className="xo-prk-ring" cx={PRK_WHEEL.cx} cy={PRK_WHEEL.cy} r={PRK_WHEEL.ring} />
        {systems.map((system, index) => {
          const from = index * slice - slice / 2 + PRK_WHEEL.gap;
          const to = (index + 1) * slice - slice / 2 - PRK_WHEEL.gap;
          const [ix, iy] = prkPolar(PRK_WHEEL.cx, PRK_WHEEL.cy, (PRK_WHEEL.inner + PRK_WHEEL.outer) / 2, index * slice);
          return (
            <g key={system.system.code} className={`xo-prk-sector is-${system.status}`}>
              <path d={prkSector(PRK_WHEEL.cx, PRK_WHEEL.cy, PRK_WHEEL.inner, PRK_WHEEL.outer, from, to)} />
              <g transform={`translate(${ix - 13} ${iy - 13}) scale(${26 / 24})`}><PrkIcon code={system.system.code} /></g>
            </g>
          );
        })}
      </svg>
      <span className="xo-prk-centre"><strong>{tx('ui.patient.omx_of_total', { count: NUMBER.format(typical), total: NUMBER.format(systems.length) })}</strong><small>{tx('ui.patient.omx_prk_centre')}</small></span>
      {systems.map((system, index) => {
        const angle = index * slice;
        const [x, y] = prkPolar(PRK_WHEEL.cx, PRK_WHEEL.cy, PRK_WHEEL.label, angle);
        const sin = Math.sin((angle * Math.PI) / 180);
        const cos = Math.cos((angle * Math.PI) / 180);
        const side = sin > 0.3 ? 'is-left' : sin < -0.3 ? 'is-right' : 'is-centre';
        const vertical = cos > 0.95 ? 'is-above' : cos < -0.95 ? 'is-below' : '';
        const offset = sin > 0.3 ? 4 : sin < -0.3 ? -4 : 0;
        return (
          <span key={system.system.code} className={`xo-prk-label ${side} ${vertical}`.trim()}
            style={{ left: `${((x + offset) / PRK_WHEEL.width) * 100}%`, top: `${((PRK_WHEEL.cy + (y - PRK_WHEEL.cy) * PRK_WHEEL.stretch) / PRK_WHEEL.height) * 100}%` }}>
            <b>{system.system.lines.map((line) => <span key={line}>{line}</span>)}</b>
            <span className={`xo-prk-status is-${system.status}`}><OmicsMark tone={PRK_STATUS_TONE[system.status]} />{tx(PRK_STATUS_KEY[system.status])}</span>
          </span>
        );
      })}
    </div>
  );
}

function PrkGauge({ finding }) {
  return (
    <svg className={`xo-prk-gauge is-${finding.tier}`} viewBox="0 0 64 42" aria-hidden="true">
      <path className="is-track" d={prkArc(32, 35, 26, -90, 90)} />
      <path className="is-value" d={prkArc(32, 35, 26, -90, -90 + Math.max(2, 180 * finding.score))} />
      <text x="32" y="37" textAnchor="middle">{finding.score100}</text>
    </svg>
  );
}

function PrkZScale({ zScore, flagged }) {
  const position = (value) => ((Math.max(-4, Math.min(4, value)) + 4) / 8) * 100;
  const beyond = Math.abs(zScore) > 4;
  return (
    <span className={`xo-prk-zscale${flagged ? ' is-flagged' : ''}`} aria-hidden="true">
      <i style={{ left: `${position(-PRK_FLAG_Z)}%`, width: `${position(PRK_FLAG_Z) - position(-PRK_FLAG_Z)}%` }} />
      <em />
      {beyond ? <b className={zScore > 0 ? 'is-beyond-high' : 'is-beyond-low'} /> : <b style={{ left: `${position(zScore)}%` }} />}
    </span>
  );
}

function prkProteinState(protein) {
  if (!protein.flagged) return tx('ui.patient.omx_prk_protein_typical');
  return tx(protein.zScore > 0 ? 'ui.patient.omx_prk_protein_above' : 'ui.patient.omx_prk_protein_below');
}

// A ProteoRisk finding inside its body system's group, so the system is not repeated.
function PrkFinding({ finding }) {
  return (
    <Finding tone={PRK_TIER_TONE[finding.tier]} title={finding.disease}
      chips={<><Chip tone={PRK_TIER_TONE[finding.tier] === 'info' ? 'muted' : PRK_TIER_TONE[finding.tier]}>{tx('ui.patient.omx_prk_score_value', { score: finding.score100 })}</Chip><Chip>{tx(PRK_TIER_KEY[finding.tier])}</Chip></>}
      lines={[]}
      note={finding.interpretation}
      evidence={finding.proteins.length === 0 ? [{ label: tx('ui.patient.omx_prk_panel'), value: tx('ui.patient.omx_prk_panel_none') }] : finding.proteins.map((protein) => ({
        label: protein.geneName,
        value: <span className="xo-prk-protein"><PrkZScale zScore={protein.zScore} flagged={protein.flagged} /><span>{tx('ui.patient.omx_z_value', { value: zText(protein.zScore, 1) })}</span><span className={protein.flagged ? 'is-flagged' : ''}>{prkProteinState(protein)}</span></span>,
      }))} />
  );
}

function ProteoRiskView({ entry, query }) {
  const read = React.useMemo(() => {
    try {
      return { report: readProteoRisk(entry), reason: null };
    } catch (error) {
      if (error?.proteoRiskUnavailable === true) return { report: null, reason: error.message };
      throw error;
    }
  }, [entry]);
  const features = readRows(entry, 'olinkProteinFeatures', ['protein_code', 'normalized_expression', 'expression_percentile', 'signal_band', 'qc_status']);
  const measuredTab = {
    value: 'measured', label: tx('ui.patient.omx_prk_measured'), count: features.length, render: () => (
      <OmicsTable rows={features} query={query} label={tx('ui.patient.omx_prk_measured')} rowKey={(row) => row.sourceRow}
        initialSort={{ key: 'protein_code', direction: 'asc' }} searchLabel={tx('ui.patient.omx_search_proteins')} empty={tx('ui.patient.omx_no_features')}
        columns={[
          { key: 'protein_code', label: tx('ui.patient.protein'), render: (row) => <strong>{row.protein_code}</strong> },
          { key: 'normalized_expression', label: tx('ui.patient.omx_level_npx'), align: 'end', sort: (row) => numeric(row.normalized_expression),
            render: (row) => (numeric(row.normalized_expression) === null ? <span className="xo-omx-none">{tx('ui.patient.omx_not_measured')}</span> : Number(row.normalized_expression).toFixed(2)) },
          { key: 'expression_percentile', label: tx('ui.patient.omx_rank_in_sample'), align: 'end', sort: (row) => numeric(row.expression_percentile),
            render: (row) => (numeric(row.expression_percentile) === null ? <span className="xo-omx-none">{tx('ui.patient.omx_not_measured')}</span> : tx('ui.patient.percentile_value', { value: formatNumber(row.expression_percentile, 0) })) },
          { key: 'qc_status', label: tx('ui.patient.omx_qc'), render: (row) => human(row.qc_status) },
        ]}
        filters={[{ key: 'measured', label: tx('ui.patient.omx_measurement'), options: [{ value: 'all', label: tx('ui.patient.omx_all') }, { value: 'missing', label: tx('ui.patient.omx_not_measured') }],
          test: (row) => numeric(row.normalized_expression) === null }]} />
    ),
  };
  if (!read.report) {
    return (
      <div className="xo-omx">
        <div className="xo-omx-verdict is-bad" role="alert"><OmicsMark tone="bad" /><div><strong>{tx('ui.patient.omx_prk_unavailable')}</strong><p>{read.reason}</p><small>{tx('ui.patient.omx_prk_unavailable_detail')}</small></div></div>
        {measuredTab.render()}
      </div>
    );
  }
  const { report } = read;
  const flaggedSystems = report.systems.filter((system) => system.status !== 'typical');
  const notable = report.findings.filter((finding) => finding.tier !== 'low');
  const bySection = (section) => report.findings.filter((finding) => finding.section === section);
  return (
    <OmicsView record={entry} about={tx('ui.patient.omx_prk_about')}
      facts={[
        { label: tx('ui.patient.omx_prk_systems_flagged'), value: tx('ui.patient.omx_of_total', { count: NUMBER.format(flaggedSystems.length), total: NUMBER.format(report.systems.length) }), tone: flaggedSystems.some((system) => system.status === 'high') ? 'bad' : flaggedSystems.length ? 'warn' : 'good' },
        { label: tx('ui.patient.omx_prk_notable'), value: tx('ui.patient.omx_of_total', { count: NUMBER.format(notable.length), total: NUMBER.format(report.findings.length) }) },
        { label: tx('ui.patient.omx_prk_outside'), value: report.outside.length },
        { label: tx('ui.patient.omx_prk_used'), value: `${NUMBER.format(report.summary.observed)} (${percent(report.summary.coverage, 1)})`, detail: report.summary.platform },
      ]}
      tabs={[
        { value: 'summary', label: tx('ui.patient.omx_tab_summary'), render: (go) => (
          <div className="xo-omx-summary">
            <p className="xo-prk-headline">{prkHeadline(report.systems)} {tx('ui.patient.omx_prk_subline', { count: NUMBER.format(report.summary.observed) })}</p>
            <div className="xo-omx-split is-wheel">
              <OmicsBlock title={tx('ui.patient.omx_prk_systems')}>
                <PrkWheel systems={report.systems} />
                <p className="xo-prk-legend">{['typical', 'moderate', 'high'].map((status) => <span key={status} className={`xo-prk-status is-${status}`}><OmicsMark tone={PRK_STATUS_TONE[status]} />{tx(PRK_STATUS_KEY[status])}</span>)}</p>
              </OmicsBlock>
              <OmicsBlock title={tx('ui.patient.omx_prk_highlights')} action={<SeeAll label={tx('ui.patient.omx_see_all_findings', { count: NUMBER.format(notable.length) })} onClick={() => go('findings')} />}>
                {report.highlights.length === 0 ? (
                  <div className="xo-omx-verdict is-good"><OmicsMark tone="good" /><div><strong>{tx('ui.patient.omx_prk_highlights_none')}</strong><p>{tx('ui.patient.omx_prk_highlights_none_detail')}</p></div></div>
                ) : (
                  <ol className="xo-prk-highlights">
                    {report.highlights.map((finding) => (
                      <li key={finding.id}>
                        <PrkGauge finding={finding} />
                        <span><strong>{prkSentence(finding)}</strong><span className={`xo-prk-status is-${finding.tier === 'high' ? 'high' : 'moderate'}`}><OmicsMark tone={PRK_TIER_TONE[finding.tier]} />{tx(PRK_STATUS_KEY[finding.tier === 'high' ? 'high' : 'moderate'])}</span></span>
                      </li>
                    ))}
                  </ol>
                )}
              </OmicsBlock>
            </div>
            <OmicsBlock title={tx('ui.patient.omx_prk_each_system')}>
              <ul className="xo-prk-systems">
                {[...report.systems].sort((left, right) => ({ high: 0, moderate: 1, typical: 2 }[left.status] - { high: 0, moderate: 1, typical: 2 }[right.status]) || ((right.top?.score || 0) - (left.top?.score || 0)) || (left.system.order - right.system.order)).map((system) => (
                  <li key={system.system.code} className={`is-${system.status}`}>
                    <PrkBadge system={system.system} status={system.status} />
                    <span>
                      <strong>{tx(system.system.nameKey)}</strong>
                      <span className={`xo-prk-status is-${system.status}`}><OmicsMark tone={PRK_STATUS_TONE[system.status]} />{tx(PRK_STATUS_KEY[system.status])}</span>
                      <p>{prkSystemSentence(system)}</p>
                      <small>{tx('ui.patient.omx_prk_notable_of', { count: NUMBER.format(system.notable), total: NUMBER.format(system.total) })}</small>
                    </span>
                  </li>
                ))}
              </ul>
            </OmicsBlock>
            <p className="xo-omx-footnote">{tx('ui.patient.omx_prk_footnote')}</p>
          </div>
        ) },
        { value: 'findings', label: tx('ui.patient.omx_prk_findings'), count: notable.length, render: () => <PrkFindings report={report} query={query} bySection={bySection} /> },
        { value: 'outside', label: tx('ui.patient.omx_prk_outside'), count: report.outside.length, render: () => (
          <OmicsTable rows={report.outside} query={query} label={tx('ui.patient.omx_prk_outside')} rowKey={(row) => row.proteinId}
            initialSort={{ key: 'zScore', direction: 'desc' }} searchLabel={tx('ui.patient.omx_search_proteins')} empty={tx('ui.patient.omx_prk_outside_none')}
            detail={(row) => <Evidence items={[{ label: tx('ui.patient.omx_prk_behind'), value: [...new Set(row.findings.map((finding) => finding.disease))].join(', ') }]} />}
            columns={[
              { key: 'geneName', label: tx('ui.patient.protein'), text: (row) => `${row.geneName} ${row.proteinId}`, render: (row) => <span className="xo-omx-variant"><strong>{row.geneName}</strong>{row.proteinId !== row.geneName && <small>{row.proteinId}</small>}</span> },
              { key: 'zScore', label: tx('ui.patient.z_score'), align: 'end', firstDirection: 'desc', sort: (row) => Math.abs(row.zScore),
                render: (row) => <span className="xo-omx-zcell"><PrkZScale zScore={row.zScore} flagged /><span>{zText(row.zScore, 1)}</span></span> },
              { key: 'state', label: tx('ui.patient.status'), text: prkProteinState, sort: (row) => (row.zScore > 0 ? 0 : 1), render: (row) => prkProteinState(row) },
              { key: 'findings', label: tx('ui.patient.omx_prk_behind'), sortable: false, text: (row) => row.findings.map((finding) => finding.disease).join(' '),
                // The first three conditions; the opened row lists them all.
                render: (row) => {
                  const diseases = [...new Set(row.findings.map((finding) => finding.disease))];
                  return diseases.length > 3 ? tx('ui.patient.omx_list_more', { items: diseases.slice(0, 3).join(', '), count: NUMBER.format(diseases.length - 3) }) : diseases.join(', ');
                } },
            ]} />
        ) },
        measuredTab,
      ]} />
  );
}

function PrkFindings({ report, query, bySection }) {
  const [section, setSection] = React.useState('current');
  const rows = bySection(section);
  const groups = PRK_SYSTEMS.map((system) => {
    const inSystem = rows.filter((finding) => finding.system === system).sort(prkByScore);
    return { system, notable: inSystem.filter((finding) => finding.tier !== 'low'), low: inSystem.filter((finding) => finding.tier === 'low'), total: inSystem.length };
  }).filter((group) => group.total > 0).sort((left, right) => {
    const a = left.notable[0]?.score ?? -1;
    const b = right.notable[0]?.score ?? -1;
    return (b - a) || (left.system.order - right.system.order);
  });
  const wanted = normalQuery(query);
  return (
    <div className="xo-omx-summary">
      <div className="xo-omx-segment">
        <Segmented value={section} onChange={setSection} options={PRK_SECTIONS.map((value) => ({
          value, label: `${tx(value === 'current' ? 'ui.patient.omx_prk_current' : 'ui.patient.omx_prk_ten_year')} (${NUMBER.format(bySection(value).filter((finding) => finding.tier !== 'low').length)})`,
        }))} />
        <p>{tx(section === 'current' ? 'ui.patient.omx_prk_current_detail' : 'ui.patient.omx_prk_ten_year_detail')}</p>
      </div>
      {groups.map((group) => {
        const notable = group.notable.filter((finding) => !wanted || matchesQuery({ disease: finding.disease, system: tx(group.system.nameKey) }, query));
        const low = group.low.filter((finding) => !wanted || matchesQuery(finding.disease, query));
        if (wanted && notable.length === 0 && low.length === 0) return null;
        return (
          <OmicsBlock key={group.system.code} title={tx(group.system.nameKey)} detail={tx('ui.patient.omx_prk_notable_of', { count: NUMBER.format(group.notable.length), total: NUMBER.format(group.total) })}>
            {notable.length > 0 && <div className="xo-omx-findings">{notable.map((finding) => <PrkFinding key={finding.id} finding={finding} />)}</div>}
            {low.length > 0 && (
              <details className="xo-omx-low">
                <summary>{tx('ui.patient.omx_prk_low_count', { count: NUMBER.format(low.length) })}</summary>
                <ul>{low.map((finding) => <li key={finding.id}><span>{finding.disease}</span><b>{finding.score100}</b></li>)}</ul>
              </details>
            )}
          </OmicsBlock>
        );
      })}
    </div>
  );
}

const RENDERERS = Object.freeze({
  'medical.patient_history': renderPatientHistory,
  'medical.anthropometrics': renderAnthropometrics,
  'medical.tonometry': renderTonometry,
  'medical.vital_signs': renderVitalSigns,
  'medical.electrocardiogram': renderElectrocardiogram,
  'medical.ankle_brachial_index': renderAbi,
  'medical.resting_metabolic_rate': renderMetabolicRate,
  'medical.bone_health_imaging': renderBone,
  'medical.diagnostic_ultrasound': renderUltrasound,
  'medical.gp_examination': renderGp,
  'medical.cognitive_assessment': renderCognition,
  'medical.total_body_skin_mapping': renderSkin,
  'medical.body_composition_3d': renderBodyComposition,
  'medical.laboratory_panel': renderLaboratory,
  'medical.nutrition_assessment': renderNutrition,
  'omics.genome': (entry, query) => <GenomeView entry={entry} query={query} />,
  'omics.carrier_screening': (entry, query) => <CarrierView entry={entry} query={query} />,
  'omics.newborn_screening': (entry, query) => <NewbornView entry={entry} query={query} />,
  'omics.gpgs': (entry, query) => <GpgsView entry={entry} query={query} />,
  'omics.gut_microbiome': (entry, query) => <MicrobiomeView entry={entry} query={query} mode="gut" />,
  'omics.oral_microbiome': (entry, query) => <MicrobiomeView entry={entry} query={query} mode="oral" />,
  'omics.vaginal_microbiome': (entry, query) => <VaginalView entry={entry} query={query} />,
  'omics.transcriptome': (entry, query) => <TranscriptomeView entry={entry} query={query} />,
  'omics.plasma_lipidomics': (entry, query) => <FeatureView entry={entry} query={query} aliasKey="plasmaLipidFeatures" kind="lipids" />,
  'omics.plasma_metabolomics': (entry, query) => <FeatureView entry={entry} query={query} aliasKey="plasmaMetaboliteFeatures" kind="plasma" />,
  'omics.urine_metabolomics': (entry, query) => <FeatureView entry={entry} query={query} aliasKey="urineMetaboliteFeatures" kind="urine" />,
  'omics.olink_proteomics': (entry, query) => <ProteoRiskView entry={entry} query={query} />,
});

function assertRegistry(registry, name) {
  const missing = DATA_TYPE_CODES.filter((code) => typeof registry[code] !== 'function');
  const extra = Object.keys(registry).filter((code) => !DATA_TYPE_CODES.includes(code));
  if (missing.length || extra.length) throw new Error(`${name} registry mismatch; missing [${missing.join(', ')}], extra [${extra.join(', ')}]`);
}
assertRegistry(RENDERERS, 'Patient renderer');

export class PatientVisualBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }

  static getDerivedStateFromError(error) { return { error }; }

  componentDidUpdate(previous) { if (previous.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null }); }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="xo-pv-failure" role="alert">
        <strong>{tx('ui.patient.cannot_display')}</strong>
        <p>{this.state.error.message}</p>
        <small>{tx('ui.patient.cannot_display_detail')}</small>
      </div>
    );
  }
}

// ---------------------------------------------------------------- one line per result

function summaryMedical(entry) {
  const code = entry.dataType.code;
  const data = entry.data || {};
  const summaries = {
    'medical.patient_history': () => ({
      headline: counted('ui.patient.sum_conditions', asList(data.chronicConditions).length),
      detail: asList(data.chiefConcerns).join(' · ') || tx('ui.patient.no_chief_concerns'),
      tone: asList(data.chronicConditions).length ? 'warn' : 'good',
    }),
    'medical.anthropometrics': () => ({
      headline: tx('ui.patient.sum_bmi', { value: formatNumber(data.bmiKgM2) }),
      detail: tx('ui.patient.sum_weight_waist', { weight: formatNumber(data.weightKg), waist: formatNumber(data.waistCircumferenceCm) }),
      tone: 'info',
    }),
    'medical.tonometry': () => ({
      headline: tx('ui.patient.sum_pressure_pair', { left: formatNumber(data.leftEyePressureMmhg), right: formatNumber(data.rightEyePressureMmhg) }),
      detail: tx('ui.patient.sum_left_right_eye'),
      tone: 'info',
    }),
    'medical.vital_signs': () => ({
      headline: tx('ui.patient.sum_pressure_pair', { left: formatNumber(data.systolicBloodPressureMmhg, 0), right: formatNumber(data.diastolicBloodPressureMmhg, 0) }),
      detail: tx('ui.patient.sum_spo2_pulse', { spo2: formatNumber(data.spo2Percent, 0), pulse: formatNumber(data.pulseBpm, 0) }),
      tone: 'info',
    }),
    'medical.electrocardiogram': () => ({
      headline: hasValue(data.rhythm) ? human(data.rhythm) : tx('ui.patient.rhythm_not_recorded'),
      detail: tx('ui.patient.sum_bpm_note', { bpm: formatNumber(data.heartRateBpm, 0), note: data.interpretation || tx('ui.patient.no_interpretation') }),
      tone: toneForStatus(data.interpretation),
    }),
    'medical.ankle_brachial_index': () => ({
      headline: `${formatNumber(data.leftAbi, 2)} / ${formatNumber(data.rightAbi, 2)}`,
      detail: data.interpretation || tx('ui.patient.sum_left_right_abi'),
      tone: toneForStatus(data.interpretation),
    }),
    'medical.resting_metabolic_rate': () => ({
      headline: tx('ui.patient.sum_kcal_day', { value: formatNumber(data.restingEnergyExpenditureKcalDay, 0) }),
      detail: tx('ui.patient.sum_rq', { value: formatNumber(data.respiratoryQuotient, 2) }),
      tone: 'info',
    }),
    'medical.bone_health_imaging': () => ({
      headline: tx('ui.patient.sum_fracture_risk', { value: formatNumber(data.fractureRisk5yPercent) }),
      detail: data.findings || counted('ui.patient.sum_sites', asList(data.sites).length),
      tone: 'info',
    }),
    'medical.diagnostic_ultrasound': () => ({
      headline: counted('ui.patient.sum_studies', asList(data.studies).length),
      detail: data.impression || data.findings || tx('ui.patient.no_impression'),
      tone: 'info',
    }),
    'medical.gp_examination': () => ({
      headline: tx('ui.patient.sum_clinical_examination'),
      detail: data.clinicalImpression || gpReportSections(data.report).map((section) => section.finding).join(' ') || tx('ui.patient.no_impression'),
      tone: toneForStatus(data.clinicalImpression),
    }),
    'medical.cognitive_assessment': () => ({
      headline: `${formatNumber(data.score)} / ${formatNumber(data.maximumScore)}`,
      detail: data.interpretation || data.instrument || tx('ui.patient.sum_cognitive'),
      tone: toneForStatus(data.interpretation),
    }),
    'medical.total_body_skin_mapping': () => ({
      headline: counted('ui.patient.sum_lesions', asList(data.lesions).length),
      detail: data.summary || tx('ui.patient.no_summary'),
      tone: asList(data.lesions).some((row) => /high/i.test(row.risk)) ? 'warn' : 'info',
    }),
    'medical.body_composition_3d': () => ({
      headline: tx('ui.patient.sum_body_fat', { value: formatNumber(data.bodyFatPercent) }),
      detail: tx('ui.patient.sum_muscle_visceral', { muscle: formatNumber(data.skeletalMuscleMassKg), visceral: formatNumber(data.visceralFatRating) }),
      tone: 'info',
    }),
    'medical.laboratory_panel': () => {
      const flagged = asList(data.analytes).filter((row) => hasValue(row.flag) && String(row.flag).toLowerCase() !== 'normal');
      return {
        headline: counted('ui.patient.sum_flagged', flagged.length),
        detail: data.panelName || data.panelCode || tx('ui.patient.sum_laboratory'),
        tone: flagged.length ? 'warn' : 'good',
      };
    },
    'medical.nutrition_assessment': () => ({
      headline: data.dietaryPattern || tx('ui.patient.sum_nutrition'),
      detail: tx('ui.patient.sum_goals_recommendations', { goals: asList(data.goals).length, recommendations: asList(data.recommendations).length }),
      tone: 'info',
    }),
  };
  return summaries[code]();
}

function summaryOmics(entry) {
  const code = entry.dataType.code;
  if (code === 'omics.genome') {
    const rows = readRows(entry, 'genomeInheritance', ['importance_code']);
    const urgent = rows.filter((row) => row.importance_code === 'very_important');
    const important = rows.filter((row) => row.importance_code === 'important').length;
    // Name the genes behind the very important findings, so the line says what they are.
    const genes = [...new Set(urgent.map((row) => row.genes).filter(hasValue))];
    return {
      // The whole_genome report's words: "Immediate attention needed", "Monitor closely".
      headline: counted('ui.patient.omx_sum_immediate', urgent.length),
      detail: [genes.slice(0, 4).join(', ') + (genes.length > 4 ? ', …' : ''), counted('ui.patient.omx_sum_important', important)].filter(Boolean).join(' · '),
      tone: urgent.length ? 'bad' : important ? 'warn' : 'good',
    };
  }
  if (code === 'omics.carrier_screening') {
    // A message row saying nothing was found is not a finding.
    const rows = screeningFindings(screeningRows(entry, 'carrier'));
    return {
      headline: rows.length ? counted('ui.patient.sum_reportable', rows.length) : tx('ui.patient.no_reportable'),
      detail: rows.length ? [...new Set(rows.map((row) => row.disease || row.genes))].join(' · ') : tx('ui.patient.carrier_screen'),
      // The carrier report's findings count is red whenever it finds any.
      tone: rows.length ? 'bad' : 'good',
    };
  }
  if (code === 'omics.newborn_screening') {
    const rows = screeningFindings(screeningRows(entry, 'newborn'));
    const carrier = screeningFindings(readRows(entry, 'newbornCarrier', ['disease', 'genes']));
    const carrierLine = carrier.length ? counted('ui.patient.omx_sum_carrier_status', carrier.length) : tx('ui.patient.omx_newborn_carrier_none');
    if (rows.length) {
      return { headline: counted('ui.patient.sum_reportable', rows.length), detail: [...new Set(rows.map((row) => row.disease || row.genes))].join(' · '), tone: 'bad' };
    }
    return carrier.length
      ? { headline: carrierLine, detail: [...new Set(carrier.map((row) => row.disease || row.genes))].join(' · '), tone: 'warn' }
      : { headline: tx('ui.patient.no_reportable'), detail: carrierLine, tone: 'good' };
  }
  if (code === 'omics.gpgs') {
    const domains = readRows(entry, 'gpgsDomains', ['domain', 'risk_level']);
    const elevated = domains.filter((row) => /elevated/i.test(row.risk_level));
    return {
      headline: counted('ui.patient.sum_elevated_domains', elevated.length),
      detail: elevated.length ? elevated.map((row) => domainWords(row.domain)).join(', ') : counted('ui.patient.sum_clinical_domains', domains.length),
      tone: elevated.length ? 'warn' : 'good',
    };
  }
  if (code === 'omics.gut_microbiome' || code === 'omics.oral_microbiome') {
    const mode = code === 'omics.gut_microbiome' ? 'gut' : 'oral';
    const diversity = oneRow(entry, mode === 'gut' ? 'gutDiversity' : 'oralDiversity', ['richness', 'shannon_diversity', 'pielou_evenness', 'fb_ratio']);
    const summaries = readRows(entry, mode === 'gut' ? 'gutDiseaseSummary' : 'oralDiseaseSummary', ['disease_id', 'your_score']);
    // The reports' reading: the low band is the flagged one, lowest first; a
    // diversity index outside its reference range is improvable.
    const low = summaries.filter((row) => microbiomeBand(mode, row.your_score) === 'low')
      .sort((left, right) => Number(left.your_score) - Number(right.your_score));
    const names = low.map((row) => {
      const reference = resolvedDisease(entry, row.disease_id);
      return reference.title || reference.name;
    });
    const improvable = diversityIndices(diversity).filter((item) => item.state !== null && item.state !== 'normal');
    const shannon = diversityIndices(diversity).find((item) => item.field === 'shannon_diversity');
    const diversityLine = tx('ui.patient.omx_sum_diversity', {
      shannon: shannon.value === null ? tx('ui.patient.omx_not_given') : shannon.value.toFixed(shannon.digits), richness: formatNumber(diversity.richness, 0),
    });
    return {
      headline: low.length ? counted('ui.patient.omx_sum_conditions_attention', low.length) : tx('ui.patient.omx_sum_no_conditions_attention'),
      detail: [names.slice(0, 3).join(', ') + (names.length > 3 ? ', …' : ''), diversityLine,
        improvable.length ? counted('ui.patient.omx_sum_improvable', improvable.length) : null].filter(Boolean).join(' · '),
      tone: low.length || improvable.length ? 'warn' : 'good',
    };
  }
  if (code === 'omics.vaginal_microbiome') {
    const row = oneRow(entry, 'vaginalSummary', ['health_score_value']);
    // The report's reading: each section's primary clinician flag classification sets its tone.
    const flags = readRows(entry, 'vaginalFlags', ['flag_code', 'section_code', 'role_code', 'classification_code']);
    const tones = readRows(entry, 'vaginalSections', ['section_code', 'primary_flag_code']).map((section) => vaginalTone(sectionClassification(section, flags)));
    const attention = tones.filter((tone) => tone === 'bad' || tone === 'warn').length;
    const cst = row.cst_subtype_code || row.cst_code;
    return {
      headline: tx('ui.patient.sum_health_score', { value: formatNumber(row.health_score_value, 0) }),
      detail: [hasValue(cst) ? tx('ui.patient.omx_cst_value', { code: cst }) : tx('ui.patient.cst_unassigned'), counted('ui.patient.omx_sum_areas_attention', attention)].join(' · '),
      tone: tones.includes('bad') ? 'bad' : attention ? 'warn' : tones.length ? 'good' : toneForStatus(row.clinical_status_code),
    };
  }
  if (code === 'omics.transcriptome') {
    const report = oneRow(entry, 'transcriptReport', ['biological_age_gap']);
    const disease = readRows(entry, 'transcriptDiseases', ['zscore', 'confidence']);
    // The report's attention rows: High or Moderate confidence, whatever the z-score.
    const attention = disease.filter((row) => transcriptTone(row) === 'warn').length;
    return {
      headline: tx('ui.patient.sum_age_gap', { value: signed(numeric(report.biological_age_gap), 1) || tx('ui.patient.omx_not_given') }),
      detail: counted('ui.patient.sum_attention_signals', attention),
      tone: attention ? 'warn' : 'good',
    };
  }
  if (code === 'omics.olink_proteomics') {
    // ProteoRisk: how many body systems show a pattern worth discussing.
    let report;
    try {
      report = readProteoRisk(entry);
    } catch (error) {
      if (error?.proteoRiskUnavailable !== true) throw error;
      return { headline: tx('ui.patient.omx_prk_unavailable'), detail: error.message, tone: 'bad' };
    }
    const flagged = report.systems.filter((system) => system.status !== 'typical');
    return {
      headline: counted('ui.patient.omx_sum_prk_systems', flagged.length, { total: NUMBER.format(report.systems.length) }),
      detail: counted('ui.patient.omx_sum_prk_strong', report.findings.filter((finding) => finding.tier === 'high').length),
      tone: flagged.some((system) => system.status === 'high') ? 'bad' : flagged.length ? 'warn' : 'good',
    };
  }
  // Metabolite and lipid features carry a rank within the sample, not a
  // population comparison, so the summary counts what was measured.
  const featureAlias = {
    'omics.plasma_lipidomics': 'plasmaLipidFeatures',
    'omics.plasma_metabolomics': 'plasmaMetaboliteFeatures',
    'omics.urine_metabolomics': 'urineMetaboliteFeatures',
  }[code];
  const features = readRows(entry, featureAlias, ['abundance', 'qc_status']);
  const missing = features.filter((row) => numeric(row.abundance) === null).length;
  return {
    headline: counted('ui.patient.omx_sum_features', features.length - missing),
    detail: counted('ui.patient.omx_sum_not_measured', missing),
    tone: 'info',
  };
}

function summaryFor(entry) {
  try {
    return entry.dataType.sourceType === 'medical_record' ? summaryMedical(entry) : summaryOmics(entry);
  } catch (error) {
    return { headline: tx('ui.patient.contract_mismatch'), detail: error.message, tone: 'bad', error: true };
  }
}

function availabilityList(availability) { if (Array.isArray(availability)) return availability; return asList(availability?.dataTypes); }
function codeSet(value) { if (value instanceof Set) return value; if (Array.isArray(value)) return new Set(value); return new Set(Object.keys(value || {}).filter((key) => value[key])); }
function entryMap(entries) {
  if (entries instanceof Map) return entries;
  if (Array.isArray(entries)) {
    const mapped = new Map();
    for (const entry of entries) {
      const code = entry?.dataType?.code;
      if (!code) continue;
      const previous = mapped.get(code);
      if (!previous || entryTimestamp(entry) >= entryTimestamp(previous)) mapped.set(code, entry);
    }
    return mapped;
  }
  return new Map(Object.entries(entries || {}));
}
function failedMessage(failedCodes, code) {
  if (failedCodes instanceof Map) return failedCodes.get(code);
  if (failedCodes && typeof failedCodes === 'object' && !Array.isArray(failedCodes) && !(failedCodes instanceof Set)) return failedCodes[code];
  return codeSet(failedCodes).has(code) ? tx('ui.patient.fetch_failed') : '';
}

// ---------------------------------------------------------------- the overview

// The record's four domains. The catalogue stays authoritative for which data
// types exist; this only groups its categories, and anything new lands in Omics.
export const DOMAINS = Object.freeze([
  { key: 'baseline', labelKey: 'ui.patient.domain_baseline', detailKey: 'ui.patient.domain_baseline_detail', categories: ['history'] },
  {
    key: 'vitals', labelKey: 'ui.patient.domain_vitals', detailKey: 'ui.patient.domain_vitals_detail',
    categories: ['vital_signs', 'cardiovascular', 'eye_health', 'metabolism', 'laboratory', 'clinical_examination', 'cognitive_health'],
  },
  {
    key: 'body', labelKey: 'ui.patient.domain_body', detailKey: 'ui.patient.domain_body_detail',
    categories: ['body_measurements', 'body_composition', 'radiology', 'dermatology', 'nutrition'],
  },
  { key: 'omics', labelKey: 'ui.patient.domain_omics', detailKey: 'ui.patient.domain_omics_detail', categories: null },
]);

export function domainOf(dataType) {
  const match = DOMAINS.find(({ categories }) => categories !== null && categories.includes(dataType?.category));
  return match ? match.key : 'omics';
}

function resultTime(result) {
  return Number(result.observedAt || result.recordedAt || result.uploadedAt || 0);
}

// The visits a record spans, as columns: results from the same booking share one,
// then results with the same visit number, then medical records from the same day.
function visitColumns(available) {
  const columns = new Map();
  available.forEach((item) => asList(item.results).forEach((result) => {
    const booking = Number(result.clinicBookingId);
    const visit = Number(result.visitNumber);
    const at = resultTime(result);
    const key = Number.isSafeInteger(booking) && booking > 0 ? `booking:${booking}`
      : Number.isSafeInteger(visit) && visit > 0 ? `visit:${visit}`
        : at > 0 ? `day:${new Date(at).toISOString().slice(0, 10)}` : 'undated';
    if (!columns.has(key)) columns.set(key, { key, visit: Number.isSafeInteger(visit) && visit > 0 ? visit : null, first: at || Infinity, domains: {} });
    const column = columns.get(key);
    if (at > 0 && at < column.first) column.first = at;
    const domain = domainOf(item.dataType);
    column.domains[domain] = (column.domains[domain] || 0) + 1;
  }));
  return [...columns.values()].sort((left, right) => left.first - right.first);
}

function Journey({ available }) {
  const columns = visitColumns(available);
  if (columns.length === 0) return <EmptyState>{tx('ui.patient.no_data')}</EmptyState>;
  const most = Math.max(1, ...columns.flatMap((column) => Object.values(column.domains)));
  return (
    <div className="xo-pv-journey" style={{ '--xo-pv-visits': columns.length }}>
      <div className="xo-pv-journey-row is-axis">
        <span />
        {columns.map((column, index) => (
          <span key={column.key} className="xo-pv-journey-visit">
            <b>{column.visit ? tx('ui.patient.visit_n', { n: column.visit }) : tx('ui.patient.visit_n', { n: index + 1 })}</b>
            <small>{Number.isFinite(column.first) ? dateText(column.first) : tx('ui.patient.not_recorded')}</small>
          </span>
        ))}
      </div>
      {DOMAINS.map((domain) => (
        <div className="xo-pv-journey-row" key={domain.key}>
          <span className="xo-pv-journey-domain">{tx(domain.labelKey)}</span>
          {columns.map((column) => {
            const count = column.domains[domain.key] || 0;
            const size = count === 0 ? 0 : 10 + Math.round((count / most) * 14);
            return (
              <span key={column.key} className="xo-pv-journey-cell" title={count ? counted('ui.patient.records_count', count) : ''}>
                <i className="xo-pv-journey-line" aria-hidden="true" />
                {count > 0 ? <b style={{ width: size, height: size }}>{count > 1 ? count : ''}</b> : <em aria-hidden="true" />}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// The patient's omics at a glance: one row per test, one column per result
// date. Each result opens that test at that date; the latest result's reading
// sits beside the test's name.
function OmicsMap({ available, cards, onSelect }) {
  const omics = available.filter((item) => domainOf(item.dataType) === 'omics' && asList(item.results).length > 0);
  if (omics.length === 0) return null;
  const columns = new Map();
  omics.forEach((item) => asList(item.results).forEach((result) => {
    const visit = Number(result.visitNumber);
    const at = resultTime(result);
    const key = Number.isSafeInteger(visit) && visit > 0 ? `visit:${visit}` : `day:${at > 0 ? new Date(at).toISOString().slice(0, 10) : 'undated'}`;
    if (!columns.has(key)) columns.set(key, { key, visit: Number.isSafeInteger(visit) && visit > 0 ? visit : null, first: at || Infinity });
    const column = columns.get(key);
    if (at > 0 && at < column.first) column.first = at;
  }));
  const ordered = [...columns.values()].sort((left, right) => left.first - right.first);
  const columnOf = (result) => {
    const visit = Number(result.visitNumber);
    const at = resultTime(result);
    return Number.isSafeInteger(visit) && visit > 0 ? `visit:${visit}` : `day:${at > 0 ? new Date(at).toISOString().slice(0, 10) : 'undated'}`;
  };
  return (
    <section className="xo-pv-panel xo-omx-map">
      <header className="xo-pv-panel-head">
        <div><span>{tx('ui.patient.domain_omics')}</span><h4>{tx('ui.patient.omx_map_title')}</h4><p>{tx('ui.patient.omx_map_detail')}</p></div>
        <b className="xo-pv-count">{counted('ui.patient.omx_map_tests', omics.length)}</b>
      </header>
      <div className="xo-omx-map-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">{tx('ui.patient.omx_map_test')}</th>
              {ordered.map((column, index) => (
                <th scope="col" key={column.key}>
                  <strong>{tx('ui.patient.visit_n', { n: column.visit || index + 1 })}</strong>
                  <small>{Number.isFinite(column.first) ? dateText(column.first) : tx('ui.patient.not_recorded')}</small>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {omics.map((item) => {
              const card = cards.find((candidate) => candidate.code === item.dataType.code);
              const Icon = dataTypeIcon(item.dataType);
              const results = asList(item.results);
              return (
                <tr key={item.dataType.code}>
                  <th scope="row">
                    <button type="button" onClick={() => onSelect?.(item.dataType.code, results.length - 1)}>
                      <span className="xo-pv-type-icon"><Icon aria-hidden="true" /></span>
                      <span>
                        <strong>{item.dataType.displayName}</strong>
                        {card?.summary && <small className={`is-${card.summary.tone}`}>{card.summary.headline}</small>}
                        {!card?.summary && card?.failed && <small className="is-bad">{tx('ui.patient.unavailable')}</small>}
                      </span>
                    </button>
                  </th>
                  {ordered.map((column) => {
                    const index = results.findIndex((result) => columnOf(result) === column.key);
                    if (index < 0) return <td key={column.key}><i className="xo-omx-map-none" aria-hidden="true" /></td>;
                    const latest = index === results.length - 1;
                    const tone = latest && card?.summary ? card.summary.tone : 'info';
                    return (
                      <td key={column.key}>
                        <button type="button" className={`xo-omx-map-dot is-${tone}`} onClick={() => onSelect?.(item.dataType.code, index)}
                          aria-label={tx('ui.patient.omx_map_open', { test: item.dataType.displayName, date: dateText(resultTime(results[index])) })}>
                          {latest && card?.summary && ['good', 'warn', 'bad'].includes(tone) ? <OmicsMark tone={tone} /> : <i />}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ul className="xo-omx-map-legend">
        <li><span className="xo-omx-map-dot is-info" aria-hidden="true"><i /></span>{tx('ui.patient.omx_map_legend_result')}</li>
        <li><OmicsMark tone="good" />{tx('ui.patient.omx_map_legend_good')}</li>
        <li><OmicsMark tone="warn" />{tx('ui.patient.omx_map_legend_warn')}</li>
        <li><OmicsMark tone="bad" />{tx('ui.patient.omx_map_legend_bad')}</li>
      </ul>
    </section>
  );
}

export function PatientOverview({ availability, entries, loadingCodes, failedCodes, onSelect, query }) {
  const available = availabilityList(availability);
  const byCode = entryMap(entries);
  const loading = codeSet(loadingCodes);
  const allCards = available.map((item) => {
    const code = item.dataType.code;
    const entry = byCode.get(code);
    const failed = failedMessage(failedCodes, code);
    return {
      item, code, entry, failed,
      summary: entry ? summaryFor(entry) : null,
      loading: loading.has(code),
    };
  });
  const cards = allCards.filter((card) => normalQuery(query) === '' || matchesQuery({
    code: card.code,
    name: card.item.dataType.displayName,
    category: card.item.dataType.category,
    summary: card.summary,
  }, query));
  const ready = allCards.filter((card) => card.entry || card.failed).length;
  const attention = allCards.filter((card) => ['bad', 'warn'].includes(card.summary?.tone))
    .sort((left, right) => (left.summary.tone === 'bad' ? -1 : 1) - (right.summary.tone === 'bad' ? -1 : 1));
  const steady = allCards.filter((card) => card.summary?.tone === 'good').length;
  const timestamps = available.flatMap((item) => asList(item.results)).map(resultTime).filter((timestamp) => timestamp > 0);
  const firstAt = timestamps.length ? Math.min(...timestamps) : 0;

  return (
    <div className="xo-pv xo-pv-overview">
      <div className="xo-pv-hero">
        <Section eyebrow={tx('ui.patient.overview')} title={tx('ui.patient.journey_title')}
          detail={firstAt ? tx('ui.patient.journey_detail', { date: dateText(firstAt) }) : null} className="xo-pv-hero-journey">
          <Journey available={available} />
        </Section>
        <aside className="xo-pv-attention">
          <header>
            <div>
              <span>{tx('ui.patient.needs_a_look')}</span>
              <strong>{NUMBER.format(attention.length)}</strong>
            </div>
            <small>{tx('ui.patient.ready_of', { ready: NUMBER.format(ready), total: NUMBER.format(available.length) })}<br />{counted('ui.patient.steady_count', steady)}</small>
          </header>
          {attention.length > 0 && (
            <ol>
              {attention.slice(0, 6).map((card) => {
                const Icon = dataTypeIcon(card.item.dataType);
                return (
                  <li key={card.code}>
                    <button type="button" onClick={() => onSelect?.(card.code)}>
                      <span className={`xo-pv-attention-icon is-${card.summary.tone}`}><Icon aria-hidden="true" /></span>
                      <span className="xo-pv-attention-main"><strong>{card.item.dataType.displayName}</strong><small>{card.summary.headline}</small></span>
                      <em className={`is-${card.summary.tone}`}>{tx(card.summary.tone === 'bad' ? 'ui.patient.priority' : 'ui.patient.review')}</em>
                      <ChevronRight aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
          {attention.length === 0 && loading.size === 0 && (
            <div className="xo-pv-attention-clear"><CheckCircle2 aria-hidden="true" /><span><strong>{tx('ui.patient.nothing_needs_a_look')}</strong><small>{tx('ui.patient.nothing_needs_a_look_detail')}</small></span></div>
          )}
          {attention.length === 0 && loading.size > 0 && [0, 1, 2].map((item) => <span className="xo-pv-shimmer is-dark" key={item} aria-hidden="true" />)}
        </aside>
      </div>

      <OmicsMap available={available} cards={allCards} onSelect={onSelect} />

      <div className="xo-pv-domains">
        {DOMAINS.map((domain, index) => {
          const domainCards = cards.filter((card) => domainOf(card.item.dataType) === domain.key);
          if (domainCards.length === 0) return null;
          const records = domainCards.reduce((sum, card) => sum + asList(card.item.results).length, 0);
          return (
            <section className="xo-pv-panel xo-pv-domain" key={domain.key}>
              <header className="xo-pv-panel-head">
                <div><span>{String(index + 1).padStart(2, '0')}</span><h4>{tx(domain.labelKey)}</h4><p>{tx(domain.detailKey)}</p></div>
                <b className="xo-pv-count">{counted('ui.patient.records_count', records)}</b>
              </header>
              <ol className="xo-pv-domain-rows">
                {domainCards.map((card) => {
                  const Icon = dataTypeIcon(card.item.dataType);
                  const results = asList(card.item.results);
                  const latest = Math.max(0, ...results.map(resultTime));
                  return (
                    <li key={card.code}>
                      <button type="button" className={card.failed ? 'is-failed' : ''} onClick={() => onSelect?.(card.code)}>
                        <span className="xo-pv-type-icon"><Icon aria-hidden="true" /></span>
                        <span className="xo-pv-domain-name">
                          <strong>{card.item.dataType.displayName}</strong>
                          <small>{[counted('ui.patient.records_count', results.length), latest > 0 ? dateText(latest) : null].filter(Boolean).join(' · ')}</small>
                        </span>
                        <span className="xo-pv-domain-read">
                          {card.loading && <span className="xo-pv-shimmer" aria-hidden="true" />}
                          {card.failed && <><b className="is-bad">{tx('ui.patient.unavailable')}</b><small>{formatValue(card.failed)}</small></>}
                          {!card.loading && !card.failed && card.summary && <><b className={`is-${card.summary.tone}`}>{card.summary.headline}</b><small title={card.summary.detail}>{card.summary.detail}</small></>}
                        </span>
                        <ChevronRight aria-hidden="true" />
                      </button>
                    </li>
                  );
                })}
              </ol>
            </section>
          );
        })}
      </div>
      {cards.length === 0 && <EmptyState>{tx('ui.patient.no_type_match')}</EmptyState>}
    </div>
  );
}

// ---------------------------------------------------------------- trends

function analysisVersion(entry) {
  if (Array.isArray(entry?.data)) {
    for (const output of entry.data) {
      const first = asList(output.rows)[0];
      if (hasValue(first?.analysis_version)) return String(first.analysis_version);
      if (hasValue(first?.pipeline_version)) return String(first.pipeline_version);
    }
  }
  return entry?.dataType?.pipeline?.codeVersion ? String(entry.dataType.pipeline.codeVersion) : null;
}

function point(entry, index, value) {
  return { label: entryVisitLabel(entry, index), value, version: analysisVersion(entry), at: entryTimestamp(entry) };
}

function scalarSeries(entries, specs, query) {
  return specs.filter((spec) => matchesQuery(spec.label, query)).map((spec) => ({
    label: spec.label,
    unit: spec.unit || '',
    points: entries.map((entry, index) => point(entry, index, spec.value(entry))),
  })).filter((series) => series.points.some((item) => numeric(item.value) !== null));
}

function MedicalScalarTrend({ entries, specs, query, title }) {
  return <SmallMultiples title={title || tx('ui.patient.measured_change')} series={scalarSeries(entries, specs, query)} />;
}

function staticTrend(message) {
  return <div className="xo-pv-static"><strong>{tx('ui.patient.trend_static_title')}</strong><p>{message}</p></div>;
}

const spec = (labelKey, value, unit = '') => ({ label: tx(labelKey), value, unit });

function trendAnthropometrics(entries, _dataType, query) {
  return <MedicalScalarTrend entries={entries} query={query} specs={[
    spec('ui.patient.height', (entry) => entry.data?.heightCm, ' cm'), spec('ui.patient.weight', (entry) => entry.data?.weightKg, ' kg'),
    spec('ui.patient.bmi', (entry) => entry.data?.bmiKgM2), spec('ui.patient.waist', (entry) => entry.data?.waistCircumferenceCm, ' cm'),
    spec('ui.patient.hip', (entry) => entry.data?.hipCircumferenceCm, ' cm'),
  ]} />;
}
function trendTonometry(entries, _dataType, query) {
  return <TrendLines title={tx('ui.patient.intraocular_pressure')} unit=" mmHg" series={scalarSeries(entries, [
    spec('ui.patient.left_eye', (entry) => entry.data?.leftEyePressureMmhg), spec('ui.patient.right_eye', (entry) => entry.data?.rightEyePressureMmhg),
  ], query)} />;
}
function trendVitals(entries, _dataType, query) {
  return <MedicalScalarTrend entries={entries} query={query} specs={[
    spec('ui.patient.systolic', (entry) => entry.data?.systolicBloodPressureMmhg, ' mmHg'), spec('ui.patient.diastolic', (entry) => entry.data?.diastolicBloodPressureMmhg, ' mmHg'),
    spec('ui.patient.oxygen_saturation', (entry) => entry.data?.spo2Percent, '%'), spec('ui.patient.pulse', (entry) => entry.data?.pulseBpm, ' bpm'),
    spec('ui.patient.respiration', (entry) => entry.data?.respiratoryRatePerMin, '/min'), spec('ui.patient.temperature', (entry) => entry.data?.temperatureC, ' °C'),
  ]} />;
}
function trendEcg(entries, _dataType, query) {
  return <MedicalScalarTrend entries={entries} query={query} specs={[
    spec('ui.patient.heart_rate', (entry) => entry.data?.heartRateBpm, ' bpm'), { label: 'PR', value: (entry) => entry.data?.prIntervalMs, unit: ' ms' },
    { label: 'QRS', value: (entry) => entry.data?.qrsDurationMs, unit: ' ms' }, { label: 'QT', value: (entry) => entry.data?.qtIntervalMs, unit: ' ms' },
    { label: 'QTc', value: (entry) => entry.data?.qtcIntervalMs, unit: ' ms' },
  ]} />;
}
function trendAbi(entries, _dataType, query) {
  return <TrendLines title={tx('ui.patient.ankle_brachial_index')} series={scalarSeries(entries, [
    spec('ui.patient.left_abi', (entry) => entry.data?.leftAbi), spec('ui.patient.right_abi', (entry) => entry.data?.rightAbi),
  ], query)} />;
}
function trendRmr(entries, _dataType, query) {
  return <MedicalScalarTrend entries={entries} query={query} specs={[
    spec('ui.patient.resting_energy', (entry) => entry.data?.restingEnergyExpenditureKcalDay, ' kcal'),
    { label: 'VO₂', value: (entry) => entry.data?.vo2MlMin, unit: ' mL/min' }, { label: 'VCO₂', value: (entry) => entry.data?.vco2MlMin, unit: ' mL/min' },
    spec('ui.patient.respiratory_quotient', (entry) => entry.data?.respiratoryQuotient),
  ]} />;
}

function stableRowsTrend(entries, rowsFor, keyFor, valueFor, query, title, note) {
  const keys = new Map();
  entries.forEach((entry) => rowsFor(entry).forEach((row) => {
    const key = keyFor(row, entry);
    if (hasValue(key) && matchesQuery({ key, row }, query) && !keys.has(key)) keys.set(key, row);
  }));
  const series = [...keys].slice(0, 24).map(([key, example]) => ({
    label: String(key),
    points: entries.map((entry, index) => {
      const row = rowsFor(entry).find((candidate) => keyFor(candidate, entry) === key);
      return point(entry, index, row ? valueFor(row, example) : null);
    }),
  }));
  return <SmallMultiples title={title} detail={note} series={series} />;
}

function trendBone(entries, _dataType, query) {
  return stableRowsTrend(entries, (entry) => asList(entry.data?.sites), (row) => `${human(row.anatomicalSite)}${row.hipSide ? ` · ${row.hipSide}` : ''}`,
    (row) => row.tScore, query, tx('ui.patient.t_score_by_site'));
}
function trendCognition(entries, _dataType, query) {
  return <MedicalScalarTrend entries={entries} query={query} specs={[
    spec('ui.patient.recorded_score', (entry) => entry.data?.score), spec('ui.patient.maximum_score', (entry) => entry.data?.maximumScore),
  ]} />;
}
function trendSkin(entries, _dataType, query) {
  return <MedicalScalarTrend entries={entries} query={query} specs={[
    spec('ui.patient.mapped_lesions', (entry) => asList(entry.data?.lesions).length),
    spec('ui.patient.high_risk_lesions', (entry) => asList(entry.data?.lesions).filter((row) => /high/i.test(row.risk)).length),
  ]} />;
}
function trendBody(entries, _dataType, query) {
  return <MedicalScalarTrend entries={entries} query={query} specs={[
    spec('ui.patient.body_fat', (entry) => entry.data?.bodyFatPercent, '%'), spec('ui.patient.fat_mass', (entry) => entry.data?.fatMassKg, ' kg'),
    spec('ui.patient.lean_mass', (entry) => entry.data?.leanMassKg, ' kg'), spec('ui.patient.skeletal_muscle', (entry) => entry.data?.skeletalMuscleMassKg, ' kg'),
    spec('ui.patient.visceral_fat', (entry) => entry.data?.visceralFatRating),
  ]} />;
}
function trendLaboratory(entries, _dataType, query) {
  return stableRowsTrend(entries, (entry) => asList(entry.data?.analytes).filter((row) => numeric(row.value) !== null),
    (row, entry) => `${analyteName(entry, row.analyteCode)}${row.unit ? ` (${row.unit})` : ''}`, (row) => row.value, query,
    tx('ui.patient.analytes_across_visits'), tx('ui.patient.analytes_across_visits_note'));
}

function presenceTrend(entries, rowsFor, stableKey, query, label) {
  const keys = new Map();
  entries.forEach((entry) => rowsFor(entry).forEach((row) => {
    const key = stableKey(row);
    if (hasValue(key) && matchesQuery({ key, row }, query)) keys.set(key, row);
  }));
  const present = tx('ui.patient.present');
  const rows = [...keys].slice(0, MAX_TABLE_ROWS).map(([key]) => {
    const result = { finding: key.split('|').filter(Boolean).join(' · ') };
    entries.forEach((entry, index) => {
      result[`v${index}`] = rowsFor(entry).some((row) => stableKey(row) === key);
    });
    return result;
  });
  const columns = [{ key: 'finding', label: tx('ui.patient.finding') }].concat(entries.map((entry, index) => ({
    key: `v${index}`,
    label: entryVisitLabel(entry, index),
    render: (value) => (value ? <span className="xo-pv-status is-info">{present}</span> : <span className="xo-pv-status is-muted">{tx('ui.patient.not_present')}</span>),
  })));
  return <>{staticTrend(label)}<Section title={tx('ui.patient.findings_by_visit')}><DataTable rows={rows} columns={columns} /></Section></>;
}

function trendGenome(entries, _dataType, query) {
  return presenceTrend(entries, (entry) => readRows(entry, 'genomeInheritance', ['disease', 'genes', 'hgvsc']),
    (row) => `${row.disease}|${row.genes}|${row.hgvsc}`, query, tx('ui.patient.trend_genome'));
}
function trendScreening(entries, _dataType, query, mode) {
  return presenceTrend(entries, (entry) => screeningRows(entry, mode), (row) => `${row.disease}|${row.genes}|${row.hgvsc}|${row.x_chrom}:${row.pos}`,
    query, tx(mode === 'carrier' ? 'ui.patient.trend_carrier' : 'ui.patient.trend_newborn'));
}
function trendGpgs(entries, _dataType, query) {
  return stableRowsTrend(entries, (entry) => readRows(entry, 'gpgsDomains', ['domain', 'mean_z']), (row) => domainWords(row.domain), (row) => row.mean_z,
    query, tx('ui.patient.domain_mean_z'), tx('ui.patient.domain_mean_z_note'));
}
function trendMicrobiome(entries, _dataType, query, mode) {
  const diversityKey = mode === 'gut' ? 'gutDiversity' : 'oralDiversity';
  const diseaseKey = mode === 'gut' ? 'gutDiseaseSummary' : 'oralDiseaseSummary';
  return (
    <>
      {/* The indices as the report shows them: F/B ratio divided by its stored factor of 100. */}
      <MedicalScalarTrend entries={entries} query={query} title={tx('ui.patient.diversity_across_visits')} specs={MICROBIOME_RANGES.map((rule) => spec(rule.labelKey, (entry) => {
        const raw = numeric(oneRow(entry, diversityKey)[rule.field]);
        return raw === null ? null : raw / rule.divisor;
      }))} />
      {/* Condition scores 0 to 100 as in the record, the lowest (disease-associated) at the latest visit first. */}
      {rankedRowsTrend(entries, {
        rowsFor: (entry) => readRows(entry, diseaseKey, ['disease_id', 'your_score']),
        keyFor: (row, entry) => resolvedDisease(entry, row.disease_id).title || resolvedDisease(entry, row.disease_id).name,
        valueFor: (row) => (numeric(row.your_score) === null ? null : Number(row.your_score) * 100),
        query, title: tx('ui.patient.disease_scores_across_visits'), order: lowestLatest, cappedKey: 'ui.patient.omx_trend_capped_lowest',
      })}
    </>
  );
}

function trendVaginal(entries, _dataType, query) {
  const series = scalarSeries(entries, [
    spec('ui.patient.health_score', (entry) => oneRow(entry, 'vaginalSummary').health_score_value),
    spec('ui.patient.protective_score', (entry) => oneRow(entry, 'vaginalSummary').protective_score_value),
    spec('ui.patient.shannon_diversity', (entry) => oneRow(entry, 'vaginalSummary').diversity_shannon),
    spec('ui.patient.lactobacillus', (entry) => oneRow(entry, 'vaginalSummary').lactobacillus_abundance_pct, '%'),
  ], query);
  const cstRows = entries.map((entry, index) => {
    const row = oneRow(entry, 'vaginalSummary');
    return {
      visit: entryVisitLabel(entry, index), analysis_version: row.analysis_version, cst: row.cst_code, subtype: row.cst_subtype_code,
      clinical_status: row.clinical_status_code, health_band: row.health_score_band_code,
    };
  });
  return (
    <>
      <SmallMultiples title={tx('ui.patient.vaginal_metrics')} series={series} />
      <Section title={tx('ui.patient.cst_transitions')}>
        <DataTable rows={cstRows} query={query} columns={[
          { key: 'visit', label: tx('ui.patient.visit') }, { key: 'analysis_version', label: tx('ui.patient.analysis_version') },
          { key: 'cst', label: 'CST' }, { key: 'subtype', label: tx('ui.patient.subtype') },
          statusColumn('clinical_status', tx('ui.patient.clinical_status')), statusColumn('health_band', tx('ui.patient.health_band')),
        ]} />
      </Section>
    </>
  );
}

function trendTranscriptome(entries, _dataType, query) {
  const cellSegments = [
    ['t_cells', 'ui.patient.t_cells'], ['b_cells', 'ui.patient.b_cells'], ['monocytes', 'ui.patient.monocytes'],
    ['dendritic_cells', 'ui.patient.dendritic_cells'], ['natural_killer_cells', 'ui.patient.nk_cells'],
    ['neutrophils', 'ui.patient.neutrophils'], ['basophils', 'ui.patient.basophils'], ['plasmablasts', 'ui.patient.plasmablasts'],
  ].map(([key, labelKey]) => ({ key, label: tx(labelKey) }));
  const cellGroups = entries.map((entry, index) => ({ label: entryVisitLabel(entry, index), values: oneRow(entry, 'transcriptCells') }));
  return (
    <>
      <MedicalScalarTrend entries={entries} query={query} title={tx('ui.patient.transcriptomic_estimates')} specs={[
        spec('ui.patient.age_gap', (entry) => oneRow(entry, 'transcriptReport').biological_age_gap),
        spec('ui.patient.age_percentile', (entry) => oneRow(entry, 'transcriptReport').biological_age_percentile),
        spec('ui.patient.bmi_percentile', (entry) => oneRow(entry, 'transcriptReport').bmi_percentile),
        spec('ui.patient.smoking_probability', (entry) => oneRow(entry, 'transcriptReport').smoking_probability),
        spec('ui.patient.disease_healthy_score', (entry) => oneRow(entry, 'transcriptReport').disease_healthy_score),
      ]} />
      <StackedBars title={tx('ui.patient.immune_cells_by_visit')} groups={cellGroups} segments={cellSegments} />
      {rankedRowsTrend(entries, {
        rowsFor: (entry) => readRows(entry, 'transcriptDiseases', ['disease', 'zscore']), keyFor: (row) => row.disease,
        valueFor: (row) => row.zscore, query, title: tx('ui.patient.disease_z_scores'), order: latestSize, cappedKey: 'ui.patient.omx_trend_capped_largest',
      })}
    </>
  );
}

// Omics rows across visits, one small chart per row, the most telling first
// (by `order`, given the row's values in visit order) and at most `limit`,
// saying so when some are left out. The record's tabs list them all.
function rankedRowsTrend(entries, { rowsFor, keyFor, valueFor, query, title, detail, unit = '', order, cappedKey, limit = 20 }) {
  const keys = new Set();
  entries.forEach((entry) => rowsFor(entry).forEach((row) => {
    const key = keyFor(row, entry);
    if (hasValue(key) && matchesQuery({ key, row }, query)) keys.add(key);
  }));
  const series = [...keys].map((key) => ({
    label: String(key),
    unit,
    points: entries.map((entry, index) => {
      const row = rowsFor(entry).find((candidate) => keyFor(candidate, entry) === key);
      return point(entry, index, row ? valueFor(row) : null);
    }),
  }));
  const values = (item) => item.points.map((entry) => numeric(entry.value)).filter((value) => value !== null);
  const ranked = [...series].sort((left, right) => (order(values(right)) ?? -Infinity) - (order(values(left)) ?? -Infinity));
  const note = ranked.length > limit
    ? [detail, tx(cappedKey, { shown: NUMBER.format(limit), total: NUMBER.format(ranked.length) })].filter(Boolean).join(' ')
    : detail;
  return <SmallMultiples title={title} detail={note} series={ranked.slice(0, limit)} />;
}

const lowestLatest = (values) => (values.length ? -values[values.length - 1] : null);
const latestSize = (values) => (values.length ? Math.abs(values[values.length - 1]) : null);
const largestMove = (values) => (values.length > 1 ? Math.abs(values[values.length - 1] - values[0]) : null);

function trendFeatures(entries, _dataType, query, aliasKey) {
  // The percentile is the feature's rank within its own sample; the features
  // whose rank moved most come first.
  return rankedRowsTrend(entries, {
    rowsFor: (entry) => readRows(entry, aliasKey, ['feature_name', 'abundance_percentile']), keyFor: (row) => row.feature_name,
    valueFor: (row) => row.abundance_percentile, query, title: tx('ui.patient.omx_rank_across_visits'),
    detail: tx('ui.patient.features_matched_by_name'), order: largestMove, cappedKey: 'ui.patient.omx_trend_capped_moved',
  });
}

// ProteoRisk scores (0 to 100) across visits, for every condition that was
// moderate or high at any visit. A visit whose report tables are missing
// fails the trend with its reason.
function trendProteoRisk(entries, _dataType, query) {
  const reports = entries.map((entry) => readProteoRisk(entry));
  const keyOf = (finding) => `${finding.section}|${finding.disease}`;
  const wanted = new Map();
  reports.forEach((report) => report.findings.filter((finding) => finding.tier !== 'low').forEach((finding) => {
    const label = tx(finding.section === 'current' ? 'ui.patient.omx_prk_trend_current' : 'ui.patient.omx_prk_trend_ten_year', { condition: finding.disease });
    if (matchesQuery(label, query) && !wanted.has(keyOf(finding))) wanted.set(keyOf(finding), label);
  }));
  // Highest score at the latest visit first; the Findings tab lists them all.
  const latest = reports[reports.length - 1];
  const latestScore = (key) => latest.findings.find((candidate) => keyOf(candidate) === key)?.score100 ?? -1;
  const ordered = [...wanted].sort(([left], [right]) => latestScore(right) - latestScore(left));
  const series = ordered.slice(0, PRK_TREND_LIMIT).map(([key, label]) => ({
    label,
    points: entries.map((entry, index) => {
      const finding = reports[index].findings.find((candidate) => keyOf(candidate) === key);
      return point(entry, index, finding ? finding.score100 : null);
    }),
  }));
  const detail = ordered.length > PRK_TREND_LIMIT
    ? tx('ui.patient.omx_prk_trend_detail_capped', { shown: NUMBER.format(PRK_TREND_LIMIT), total: NUMBER.format(ordered.length) })
    : tx('ui.patient.omx_prk_trend_detail');
  return <SmallMultiples title={tx('ui.patient.omx_prk_trend_title')} detail={detail} series={series} />;
}

const PRK_TREND_LIMIT = 20;

const TREND_RENDERERS = Object.freeze({
  'medical.patient_history': () => staticTrend(tx('ui.patient.trend_history')),
  'medical.anthropometrics': trendAnthropometrics,
  'medical.tonometry': trendTonometry,
  'medical.vital_signs': trendVitals,
  'medical.electrocardiogram': trendEcg,
  'medical.ankle_brachial_index': trendAbi,
  'medical.resting_metabolic_rate': trendRmr,
  'medical.bone_health_imaging': trendBone,
  'medical.diagnostic_ultrasound': () => staticTrend(tx('ui.patient.trend_ultrasound')),
  'medical.gp_examination': () => staticTrend(tx('ui.patient.trend_gp')),
  'medical.cognitive_assessment': trendCognition,
  'medical.total_body_skin_mapping': trendSkin,
  'medical.body_composition_3d': trendBody,
  'medical.laboratory_panel': trendLaboratory,
  'medical.nutrition_assessment': () => staticTrend(tx('ui.patient.trend_nutrition')),
  'omics.genome': trendGenome,
  'omics.carrier_screening': (entries, type, query) => trendScreening(entries, type, query, 'carrier'),
  'omics.newborn_screening': (entries, type, query) => trendScreening(entries, type, query, 'newborn'),
  'omics.gpgs': trendGpgs,
  'omics.gut_microbiome': (entries, type, query) => trendMicrobiome(entries, type, query, 'gut'),
  'omics.oral_microbiome': (entries, type, query) => trendMicrobiome(entries, type, query, 'oral'),
  'omics.vaginal_microbiome': trendVaginal,
  'omics.transcriptome': trendTranscriptome,
  'omics.plasma_lipidomics': (entries, type, query) => trendFeatures(entries, type, query, 'plasmaLipidFeatures'),
  'omics.plasma_metabolomics': (entries, type, query) => trendFeatures(entries, type, query, 'plasmaMetaboliteFeatures'),
  'omics.urine_metabolomics': (entries, type, query) => trendFeatures(entries, type, query, 'urineMetaboliteFeatures'),
  'omics.olink_proteomics': trendProteoRisk,
});

assertRegistry(TREND_RENDERERS, 'Patient trend');

export function PatientTrendVisual({ entries, dataType, query }) {
  const ordered = sortedEntries(entries);
  const code = dataType?.code || ordered[0]?.dataType?.code;
  if (!code) throw new Error('Trend view has no data type code');
  const renderer = TREND_RENDERERS[code];
  if (!renderer) throw new Error(`No trend renderer is registered for ${code}`);
  if (ordered.length < 2) return <div className="xo-pv xo-pv-trend">{staticTrend(tx('ui.patient.trend_needs_two'))}</div>;
  const versions = [...new Set(ordered.map(analysisVersion).filter(Boolean))];
  return (
    <div className="xo-pv xo-pv-trend">
      {versions.length > 0 && (
        <p className="xo-pv-trend-note">
          {versions.length > 1 ? tx('ui.patient.version_breaks', { versions: versions.join(', ') }) : tx('ui.patient.version_single', { version: versions[0] })}
        </p>
      )}
      {renderer(ordered, dataType, query)}
    </div>
  );
}

export function PatientDataVisual({ entry, query }) {
  const code = entry?.dataType?.code;
  if (!code) throw new Error('Patient result has no data type code');
  const renderer = RENDERERS[code];
  if (!renderer) throw new Error(`No patient renderer is registered for ${code}`);
  return <div className="xo-pv">{renderer(entry, query)}</div>;
}

export function dataRecordLabel(result) {
  if (Number.isSafeInteger(Number(result?.visitNumber))) return tx('ui.patient.visit_n', { n: Number(result.visitNumber) });
  const timestamp = Number(result?.observedAt || result?.recordedAt || result?.uploadedAt);
  return Number.isFinite(timestamp) && timestamp > 0 ? dateText(timestamp) : tx('ui.patient.clinical_record');
}
